import { UseCaseEntry, UseCaseLocalized, UseCaseLocalizedContent } from '../types/useCase'
import { Language } from '../i18n/translations'

export const useCases: UseCaseEntry[] = [
  {
    slug: 'fpna-scenario-planning',
    locales: {
      zh: {
        industry: '金融 / FP&A',
        title: '财务规划团队：用 SheetMate 审核多场景预算模型',
        subtitle: '把三表与运营指标拉进同一视图，缩短预测迭代周期',
        excerpt: '了解一家高速成长 SaaS 团队如何利用 SheetMate 在 Google Sheets 中以 JSON + Markdown 方式秒级审阅预算假设。',
        keywords: ['SheetMate', 'Google Sheets 扩展', '财务预算', 'JSON 预览', 'Markdown'],
        readingTime: '8 分钟',
        heroEyebrow: 'Finance Playbook',
        metaTitle: 'SheetMate 财务预算 Use Case｜实时审阅多场景模型',
        metaDescription: '财务规划团队借助 SheetMate 的 JSON 预览、双栏布局和 Markdown 模板，快速对比多个预算场景并将结果输出给管理层。',
        imagePlaceholder: {
          caption: '示意图：双栏对比不同预算场景的收入模型',
          alt: 'SheetMate 预算分屏视图占位图'
        },
        sections: [
          {
            title: '团队背景与挑战',
            paragraphs: [
              '这家 SaaS 企业在每月结账前，需要在 Google Sheets 中对比基线、拉伸与紧缩三套预算模型。每个模型都包含数百个假设输入、订阅留存分组以及跨部门的 headcount 计划。',
              '以往团队依赖多个 Apps Script 将 ERP、Stripe 与销售 CRM 的 JSON 数据导回表格，再把关键指标复制进文档说明。跨工具复制导致版本错乱，也很难追踪单元格位置。'
            ]
          },
          {
            title: 'SheetMate 如何融入工作流',
            paragraphs: [
              '现在财务分析师在 SheetMate 侧边栏实时查看当前单元格内容，并通过双栏布局同时打开两个场景。第一栏固定在 Markdown 提要，第二栏展示最新的 JSON 输出，所有内容随着焦点移动自动刷新。',
              '针对 API 拉取的 JSON 结果，SheetMate 的 ReactJson 视图会自动缩进、着色并允许编辑字段，分析师可以立刻验证折扣率或汇率是否被正确写回。',
              '当需要展示第三方 BI 图表时，只需在单元格中粘贴 Dashboard 链接，SheetMate 会在 iframe 中呈现预览，无需跳出 Google Sheets。'
            ]
          },
          {
            title: '落地成效与经验',
            paragraphs: [
              '采用 SheetMate 后，财务团队用字体大小控制快速切换演示模式，并把单元格定位标注在截图中，CFO 能清晰地追溯指标来源。',
              '团队将每个场景的讲稿保存在 Markdown 模板中，通过分屏对比不同假设，把待办事项直接记录在第二视图，让会议纪要与模型保持同步。'
            ]
          }
        ],
        workflow: [
          {
            title: '准备结构化源数据',
            description: '使用 Apps Script 将 ERP 与支付系统返回的数据转成 JSON 字符串写入隐藏列。'
          },
          {
            title: '并列比较关键指标',
            description: '在 SheetMate 中选择垂直分屏，左侧锁定 Markdown 讲稿，右侧切换到不同场景的 JSON 输出。'
          },
          {
            title: '记录假设与待办',
            description: '利用 Markdown 复选框记录风险与行动项，保持与当前单元格位置同步。'
          },
          {
            title: '导出会议纪要',
            description: '通过浏览器截图或屏幕录制保存 SheetMate 视图，随周度更新发送给 CFO 与业务负责人。'
          }
        ],
        highlights: [
          'JSON 自动缩进与语法着色，便于验证 ERP 回传字段。',
          '双视图模式下同时展示 Markdown 讲稿与核心指标。',
          '侧边栏显示实时单元格坐标，复盘时可快速回到数据源。'
        ],
        metrics: [
          {
            value: '4×',
            label: '模型审核速度',
            context: '滚动预测会议从 120 分钟缩短至 30 分钟。'
          },
          {
            value: '12',
            label: '标准化场景模板',
            context: '沉淀的 Markdown 模板覆盖全年核心假设。'
          },
          {
            value: '0',
            label: '跨工具切换次数',
            context: '分析师无需再复制内容到外部文档。'
          }
        ],
        quote: {
          text: '我们不再在 Slack 上贴长截图，只需共享 SheetMate 双栏视图就能让 CFO 迅速理解场景差异。',
          author: 'Lina Zhang',
          role: '财务规划总监'
        }
      },
      en: {
        industry: 'Finance / FP&A',
        title: 'FP&A Teams: Review Multi-Scenario Budgets in Minutes',
        subtitle: 'Keep models, assumptions, and dashboards in one workspace inside Google Sheets',
        excerpt: 'A high-growth SaaS finance team uses SheetMate to stack Markdown briefs, API outputs, and dashboards side by side so leadership reviews run faster.',
        keywords: ['SheetMate', 'Google Sheets extension', 'Financial planning', 'Budget review', 'Scenario analysis'],
        readingTime: '8 min read',
        heroEyebrow: 'Finance Playbook',
        metaTitle: 'SheetMate Finance Use Case｜Modern Scenario Planning in Google Sheets',
        metaDescription: 'Discover how FP&A teams compare scenarios, validate JSON feeds, and brief executives without leaving Google Sheets using SheetMate.',
        imagePlaceholder: {
          caption: 'Mockup: split view contrasting revenue assumptions across scenarios',
          alt: 'SheetMate split screen for budget review'
        },
        sections: [
          {
            title: 'Where the team started',
            paragraphs: [
              'Ahead of every close, the FP&A org needed to line up base, stretch, and downside models packed with retention cohorts and hiring plans.',
              'Analysts exported ERP, Stripe, and CRM JSON into separate decks to explain the story, which caused version drift and no clear trail back to the source cells.'
            ]
          },
          {
            title: 'How SheetMate keeps context together',
            paragraphs: [
              'With SheetMate docked in the side panel, analysts pin a Markdown brief on one side and live JSON on the other. Each view refreshes automatically as they move between cells.',
              'ReactJson formatting highlights structure, lets the team tweak fields inline, and confirms exchange rates, discounts, or pipeline inputs before readouts.',
              'When leadership needs to see a BI chart, a pasted dashboard link renders instantly in an iframe—no more tab toggling mid-meeting.'
            ]
          },
          {
            title: 'The impact',
            paragraphs: [
              'During reviews the team bumps up font sizes, captures screenshots with the cell reference baked in, and gives the CFO direct traceability.',
              'Scenario playbooks now live as Markdown templates. Teams compare assumptions side by side, log action items in the second pane, and ship meeting notes the same day.'
            ]
          }
        ],
        workflow: [
          {
            title: 'Stage structured inputs',
            description: 'Push ERP and payments data back into hidden columns as JSON strings via Apps Script.'
          },
          {
            title: 'Compare scenarios in split view',
            description: 'Lock the Markdown brief on the left and rotate JSON outputs for each scenario on the right.'
          },
          {
            title: 'Capture assumptions and follow-ups',
            description: 'Use Markdown checklists to track risks and handoffs so every note stays tied to the active cell.'
          },
          {
            title: 'Share the recap instantly',
            description: 'Grab a screenshot or quick recording of the SheetMate view and post it to execs after the meeting.'
          }
        ],
        highlights: [
          'JSON formatting makes ERP payloads easy to validate.',
          'Split view keeps narratives and metrics visible at the same time.',
          'Automatic cell coordinates help reviewers jump back to the source data.'
        ],
        metrics: [
          {
            value: '4×',
            label: 'Faster reviews',
            context: 'Budget readouts now wrap in 30 minutes instead of two hours.'
          },
          {
            value: '12',
            label: 'Reusable playbooks',
            context: 'Standard Markdown scenarios now cover the full fiscal year.'
          },
          {
            value: '0',
            label: 'Context switches',
            context: 'Analysts stay inside Google Sheets—no more copying data into docs.'
          }
        ],
        quote: {
          text: 'Sharing the SheetMate split view gives our CFO the full picture—no more screenshot threads or copy-paste decks.',
          author: 'Lina Zhang',
          role: 'Director of Financial Planning'
        }
      },
      ja: {
        "industry": "金融 / FP&A",
        "title": "FP&Aチーム：複数シナリオの予算レビューを数分で完了",
        "subtitle": "モデル、前提条件、ダッシュボードをGoogle Sheets内の一つのワークスペースにまとめる",
        "excerpt": "急成長中のSaaS企業の財務チームは、SheetMateを使ってMarkdownの要約、APIの出力、ダッシュボードを横並びに重ねることで、経営層のレビューをスピードアップさせています。",
        "keywords": [
          "SheetMate",
          "Google Sheets 拡張機能",
          "財務計画",
          "予算レビュー",
          "シナリオ分析"
        ],
        "readingTime": "8分で読了",
        "heroEyebrow": "ファイナンス活用術",
        "metaTitle": "SheetMate ファイナンス活用事例｜Google Sheetsで実現する最新のシナリオプランニング",
        "metaDescription": "FP&AチームがSheetMateを使い、Google Sheetsを離れることなくシナリオを比較し、JSONフィードを検証し、経営層に報告する方法をご紹介します。",
        "imagePlaceholder": {
          "caption": "モックアップ：複数シナリオ間の売上前提を対比する分割ビュー",
          "alt": "予算レビュー向けSheetMate分割画面"
        },
        "sections": [
          {
            "title": "チームの出発点",
            "paragraphs": [
              "決算を控えるたびに、FP&A部門はリテンションコホートや採用計画を詰め込んだベース、ストレッチ、ダウンサイドの各モデルを整理する必要がありました。",
              "アナリストはERP、Stripe、CRMのJSONを別々の資料に書き出してストーリーを説明していたため、バージョンのずれが生じ、元のセルまでたどれる明確な道筋がありませんでした。"
            ]
          },
          {
            "title": "SheetMateがコンテキストをまとめる仕組み",
            "paragraphs": [
              "サイドパネルにSheetMateをドッキングすると、アナリストは片側にMarkdownの要約を、もう片側にライブJSONを固定できます。セル間を移動するたびに、それぞれのビューが自動的に更新されます。",
              "ReactJsonのフォーマットは構造を見やすく強調し、フィールドをインラインで調整でき、報告前に為替レート、割引、パイプライン入力を確認できます。",
              "経営層がBIチャートを見たいとき、貼り付けたダッシュボードのリンクがiframe内で即座に表示されます。会議の途中でタブを切り替える必要はもうありません。"
            ]
          },
          {
            "title": "もたらされた効果",
            "paragraphs": [
              "レビュー中、チームはフォントサイズを大きくし、セル参照を埋め込んだスクリーンショットを取得することで、CFOに直接的なトレーサビリティを提供します。",
              "シナリオのプレイブックは今やMarkdownテンプレートとして存在しています。チームは前提条件を横並びで比較し、2つ目のペインにアクションアイテムを記録し、その日のうちに議事録を共有します。"
            ]
          }
        ],
        "workflow": [
          {
            "title": "構造化された入力を準備する",
            "description": "Apps ScriptでERPと決済データをJSON文字列として非表示の列に書き戻します。"
          },
          {
            "title": "分割ビューでシナリオを比較する",
            "description": "左側にMarkdownの要約を固定し、右側で各シナリオのJSON出力を切り替えます。"
          },
          {
            "title": "前提条件とフォローアップを記録する",
            "description": "Markdownのチェックリストでリスクと引き継ぎを管理し、すべてのメモがアクティブなセルに紐づくようにします。"
          },
          {
            "title": "まとめを即座に共有する",
            "description": "SheetMateビューのスクリーンショットや短い録画を取得し、会議後に経営層へ投稿します。"
          }
        ],
        "highlights": [
          "JSONフォーマットによりERPのペイロードを簡単に検証できます。",
          "分割ビューでナラティブと指標を同時に表示し続けられます。",
          "自動的なセル座標により、レビュアーが元データに素早く戻れます。"
        ],
        "metrics": [
          {
            "value": "4×",
            "label": "レビューの高速化",
            "context": "予算報告が2時間ではなく30分で完了するようになりました。"
          },
          {
            "value": "12",
            "label": "再利用可能なプレイブック",
            "context": "標準的なMarkdownシナリオが会計年度全体をカバーするようになりました。"
          },
          {
            "value": "0",
            "label": "コンテキストの切り替え",
            "context": "アナリストはGoogle Sheets内にとどまります。データをドキュメントにコピーする必要はもうありません。"
          }
        ],
        "quote": {
          "text": "SheetMateの分割ビューを共有することで、CFOが全体像を把握できます。スクリーンショットのスレッドやコピペの資料はもう必要ありません。",
          "author": "Lina Zhang",
          "role": "財務計画ディレクター"
        }
      },
      id: {
        "industry": "Keuangan / FP&A",
        "title": "Tim FP&A: Tinjau Anggaran Multi-Skenario dalam Hitungan Menit",
        "subtitle": "Simpan model, asumsi, dan dasbor dalam satu ruang kerja di dalam Google Sheets",
        "excerpt": "Tim keuangan SaaS yang berkembang pesat menggunakan SheetMate untuk menyusun ringkasan Markdown, keluaran API, dan dasbor secara berdampingan agar tinjauan oleh pimpinan berjalan lebih cepat.",
        "keywords": [
          "SheetMate",
          "ekstensi Google Sheets",
          "Perencanaan keuangan",
          "Tinjauan anggaran",
          "Analisis skenario"
        ],
        "readingTime": "Baca 8 menit",
        "heroEyebrow": "Panduan Keuangan",
        "metaTitle": "Studi Kasus Keuangan SheetMate｜Perencanaan Skenario Modern di Google Sheets",
        "metaDescription": "Temukan bagaimana tim FP&A membandingkan skenario, memvalidasi umpan JSON, dan memberi laporan kepada pimpinan tanpa keluar dari Google Sheets menggunakan SheetMate.",
        "imagePlaceholder": {
          "caption": "Mockup: tampilan terbagi yang membandingkan asumsi pendapatan antar skenario",
          "alt": "Layar terbagi SheetMate untuk tinjauan anggaran"
        },
        "sections": [
          {
            "title": "Titik awal tim",
            "paragraphs": [
              "Menjelang setiap penutupan buku, organisasi FP&A perlu menyelaraskan model dasar, optimis, dan pesimis yang penuh dengan kohort retensi dan rencana perekrutan.",
              "Analis mengekspor JSON dari ERP, Stripe, dan CRM ke dalam dek terpisah untuk menjelaskan ceritanya, yang menyebabkan ketidaksesuaian versi dan tidak ada jejak yang jelas kembali ke sel sumber."
            ]
          },
          {
            "title": "Bagaimana SheetMate menjaga konteks tetap menyatu",
            "paragraphs": [
              "Dengan SheetMate yang tertambat di panel samping, analis menyematkan ringkasan Markdown di satu sisi dan JSON langsung di sisi lain. Setiap tampilan menyegarkan secara otomatis saat mereka berpindah antar sel.",
              "Pemformatan ReactJson menyoroti struktur, memungkinkan tim menyesuaikan kolom secara langsung, dan mengonfirmasi nilai tukar, diskon, atau masukan pipeline sebelum pembacaan.",
              "Saat pimpinan perlu melihat grafik BI, tautan dasbor yang ditempel langsung dirender dalam iframe—tidak perlu lagi berpindah tab di tengah rapat."
            ]
          },
          {
            "title": "Dampaknya",
            "paragraphs": [
              "Selama tinjauan, tim memperbesar ukuran font, mengambil tangkapan layar dengan referensi sel yang sudah tertanam, dan memberi CFO keterlacakan langsung.",
              "Playbook skenario kini hidup sebagai templat Markdown. Tim membandingkan asumsi secara berdampingan, mencatat item tindak lanjut di panel kedua, dan mengirim catatan rapat di hari yang sama."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Siapkan masukan terstruktur",
            "description": "Kirim kembali data ERP dan pembayaran ke kolom tersembunyi sebagai string JSON melalui Apps Script."
          },
          {
            "title": "Bandingkan skenario dalam tampilan terbagi",
            "description": "Kunci ringkasan Markdown di sebelah kiri dan putar keluaran JSON untuk setiap skenario di sebelah kanan."
          },
          {
            "title": "Catat asumsi dan tindak lanjut",
            "description": "Gunakan daftar periksa Markdown untuk melacak risiko dan serah terima agar setiap catatan tetap terkait dengan sel aktif."
          },
          {
            "title": "Bagikan rangkuman secara instan",
            "description": "Ambil tangkapan layar atau rekaman singkat tampilan SheetMate dan kirimkan ke pimpinan setelah rapat."
          }
        ],
        "highlights": [
          "Pemformatan JSON membuat payload ERP mudah divalidasi.",
          "Tampilan terbagi menjaga narasi dan metrik tetap terlihat secara bersamaan.",
          "Koordinat sel otomatis membantu peninjau melompat kembali ke data sumber."
        ],
        "metrics": [
          {
            "value": "4×",
            "label": "Tinjauan lebih cepat",
            "context": "Pembacaan anggaran kini selesai dalam 30 menit, bukan dua jam."
          },
          {
            "value": "12",
            "label": "Playbook yang dapat digunakan kembali",
            "context": "Skenario Markdown standar kini mencakup seluruh tahun fiskal."
          },
          {
            "value": "0",
            "label": "Pergantian konteks",
            "context": "Analis tetap berada di dalam Google Sheets—tidak perlu lagi menyalin data ke dokumen."
          }
        ],
        "quote": {
          "text": "Membagikan tampilan terbagi SheetMate memberi CFO kami gambaran lengkap—tidak ada lagi utas tangkapan layar atau dek hasil salin-tempel.",
          "author": "Lina Zhang",
          "role": "Direktur Perencanaan Keuangan"
        }
      },
      es: {
        "industry": "Finanzas / FP&A",
        "title": "Equipos de FP&A: revisa presupuestos de múltiples escenarios en minutos",
        "subtitle": "Mantén modelos, supuestos y tableros en un solo espacio de trabajo dentro de Google Sheets",
        "excerpt": "Un equipo de finanzas de una SaaS de alto crecimiento usa SheetMate para apilar resúmenes en Markdown, salidas de API y tableros lado a lado, de modo que las revisiones de la dirección sean más rápidas.",
        "keywords": [
          "SheetMate",
          "extensión de Google Sheets",
          "Planeación financiera",
          "Revisión de presupuestos",
          "Análisis de escenarios"
        ],
        "readingTime": "8 min de lectura",
        "heroEyebrow": "Manual de finanzas",
        "metaTitle": "Caso de uso financiero de SheetMate｜Planeación de escenarios moderna en Google Sheets",
        "metaDescription": "Descubre cómo los equipos de FP&A comparan escenarios, validan feeds de JSON e informan a los ejecutivos sin salir de Google Sheets usando SheetMate.",
        "imagePlaceholder": {
          "caption": "Maqueta: vista dividida que contrasta los supuestos de ingresos entre escenarios",
          "alt": "Pantalla dividida de SheetMate para la revisión de presupuestos"
        },
        "sections": [
          {
            "title": "Dónde empezó el equipo",
            "paragraphs": [
              "Antes de cada cierre, el área de FP&A necesitaba alinear los modelos base, optimista y pesimista, repletos de cohortes de retención y planes de contratación.",
              "Los analistas exportaban el JSON de ERP, Stripe y CRM a presentaciones separadas para contar la historia, lo que provocaba desfases de versiones y ningún rastro claro de regreso a las celdas de origen."
            ]
          },
          {
            "title": "Cómo SheetMate mantiene el contexto unido",
            "paragraphs": [
              "Con SheetMate anclado en el panel lateral, los analistas fijan un resumen en Markdown de un lado y el JSON en vivo del otro. Cada vista se actualiza automáticamente a medida que se mueven entre celdas.",
              "El formato de ReactJson resalta la estructura, permite al equipo ajustar campos en línea y confirma tipos de cambio, descuentos o entradas del pipeline antes de los informes.",
              "Cuando la dirección necesita ver una gráfica de BI, un enlace de tablero pegado se renderiza al instante en un iframe; se acabó cambiar de pestaña a mitad de la reunión."
            ]
          },
          {
            "title": "El impacto",
            "paragraphs": [
              "Durante las revisiones, el equipo aumenta el tamaño de la fuente, captura pantallazos con la referencia de la celda incorporada y le da al CFO una trazabilidad directa.",
              "Los manuales de escenarios ahora viven como plantillas de Markdown. Los equipos comparan supuestos lado a lado, registran tareas pendientes en el segundo panel y envían las notas de la reunión el mismo día."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Prepara las entradas estructuradas",
            "description": "Envía los datos de ERP y de pagos de vuelta a columnas ocultas como cadenas JSON mediante Apps Script."
          },
          {
            "title": "Compara escenarios en vista dividida",
            "description": "Fija el resumen en Markdown a la izquierda y rota las salidas de JSON de cada escenario a la derecha."
          },
          {
            "title": "Registra supuestos y seguimientos",
            "description": "Usa listas de verificación en Markdown para dar seguimiento a riesgos y traspasos, de modo que cada nota quede vinculada a la celda activa."
          },
          {
            "title": "Comparte el resumen al instante",
            "description": "Toma un pantallazo o una grabación rápida de la vista de SheetMate y compártela con los ejecutivos después de la reunión."
          }
        ],
        "highlights": [
          "El formato JSON facilita validar las cargas útiles del ERP.",
          "La vista dividida mantiene visibles las narrativas y las métricas al mismo tiempo.",
          "Las coordenadas automáticas de celda ayudan a los revisores a volver a los datos de origen."
        ],
        "metrics": [
          {
            "value": "4×",
            "label": "Revisiones más rápidas",
            "context": "Los informes de presupuesto ahora se completan en 30 minutos en lugar de dos horas."
          },
          {
            "value": "12",
            "label": "Manuales reutilizables",
            "context": "Los escenarios estándar en Markdown ahora cubren todo el año fiscal."
          },
          {
            "value": "0",
            "label": "Cambios de contexto",
            "context": "Los analistas permanecen dentro de Google Sheets; se acabó copiar datos a documentos."
          }
        ],
        "quote": {
          "text": "Compartir la vista dividida de SheetMate le da a nuestro CFO el panorama completo: se acabaron los hilos de pantallazos y las presentaciones de copiar y pegar.",
          "author": "Lina Zhang",
          "role": "Directora de Planeación Financiera"
        }
      },
      ms: {
        "industry": "Kewangan / FP&A",
        "title": "Pasukan FP&A: Semak Belanjawan Berbilang Senario dalam Beberapa Minit",
        "subtitle": "Simpan model, andaian dan papan pemuka dalam satu ruang kerja di dalam Google Sheets",
        "excerpt": "Sebuah pasukan kewangan SaaS yang berkembang pesat menggunakan SheetMate untuk menyusun ringkasan Markdown, output API dan papan pemuka secara bersebelahan supaya semakan oleh pihak pengurusan berjalan lebih pantas.",
        "keywords": [
          "SheetMate",
          "sambungan Google Sheets",
          "Perancangan kewangan",
          "Semakan belanjawan",
          "Analisis senario"
        ],
        "readingTime": "Bacaan 8 minit",
        "heroEyebrow": "Panduan Kewangan",
        "metaTitle": "Kajian Kes Kewangan SheetMate｜Perancangan Senario Moden dalam Google Sheets",
        "metaDescription": "Ketahui cara pasukan FP&A membandingkan senario, mengesahkan suapan JSON dan memberi taklimat kepada eksekutif tanpa meninggalkan Google Sheets menggunakan SheetMate.",
        "imagePlaceholder": {
          "caption": "Mockup: paparan terbahagi yang membandingkan andaian hasil antara senario",
          "alt": "Skrin terbahagi SheetMate untuk semakan belanjawan"
        },
        "sections": [
          {
            "title": "Di mana pasukan bermula",
            "paragraphs": [
              "Sebelum setiap penutupan, organisasi FP&A perlu menyelaraskan model asas, sasaran tinggi dan kemungkinan menurun yang sarat dengan kohort pengekalan dan pelan pengambilan pekerja.",
              "Penganalisis mengeksport JSON daripada ERP, Stripe dan CRM ke dalam dek berasingan untuk menerangkan ceritanya, yang menyebabkan ketidakselarasan versi dan tiada jejak yang jelas kembali ke sel sumber."
            ]
          },
          {
            "title": "Bagaimana SheetMate mengekalkan konteks bersama",
            "paragraphs": [
              "Dengan SheetMate disangkutkan pada panel sisi, penganalisis menyematkan ringkasan Markdown di satu sisi dan JSON langsung di sisi yang lain. Setiap paparan dimuat semula secara automatik apabila mereka bergerak antara sel.",
              "Pemformatan ReactJson menyerlahkan struktur, membolehkan pasukan melaraskan medan secara sebaris, dan mengesahkan kadar pertukaran, diskaun atau input saluran paip sebelum pembentangan.",
              "Apabila pihak pengurusan perlu melihat carta BI, pautan papan pemuka yang ditampal dipaparkan serta-merta dalam iframe—tidak perlu lagi bertukar tab di tengah-tengah mesyuarat."
            ]
          },
          {
            "title": "Kesannya",
            "paragraphs": [
              "Semasa semakan, pasukan membesarkan saiz fon, menangkap tangkapan skrin dengan rujukan sel yang sudah disertakan, dan memberi CFO kebolehkesanan secara langsung.",
              "Playbook senario kini wujud sebagai templat Markdown. Pasukan membandingkan andaian secara bersebelahan, merekod item tindakan dalam anak tetingkap kedua, dan menghantar nota mesyuarat pada hari yang sama."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Sediakan input berstruktur",
            "description": "Hantar semula data ERP dan pembayaran ke lajur tersembunyi sebagai rentetan JSON melalui Apps Script."
          },
          {
            "title": "Bandingkan senario dalam paparan terbahagi",
            "description": "Kunci ringkasan Markdown di sebelah kiri dan putar output JSON bagi setiap senario di sebelah kanan."
          },
          {
            "title": "Rekod andaian dan susulan",
            "description": "Gunakan senarai semak Markdown untuk menjejaki risiko dan penyerahan supaya setiap nota kekal terikat pada sel aktif."
          },
          {
            "title": "Kongsi ringkasan serta-merta",
            "description": "Ambil tangkapan skrin atau rakaman ringkas paparan SheetMate dan hantarkannya kepada eksekutif selepas mesyuarat."
          }
        ],
        "highlights": [
          "Pemformatan JSON memudahkan pengesahan muatan ERP.",
          "Paparan terbahagi mengekalkan naratif dan metrik kelihatan pada masa yang sama.",
          "Koordinat sel automatik membantu penyemak melompat kembali ke data sumber."
        ],
        "metrics": [
          {
            "value": "4×",
            "label": "Semakan lebih pantas",
            "context": "Pembentangan belanjawan kini selesai dalam 30 minit dan bukannya dua jam."
          },
          {
            "value": "12",
            "label": "Playbook boleh guna semula",
            "context": "Senario Markdown standard kini merangkumi sepanjang tahun fiskal."
          },
          {
            "value": "0",
            "label": "Pertukaran konteks",
            "context": "Penganalisis kekal di dalam Google Sheets—tidak perlu lagi menyalin data ke dalam dokumen."
          }
        ],
        "quote": {
          "text": "Berkongsi paparan terbahagi SheetMate memberi CFO kami gambaran penuh—tiada lagi rangkaian tangkapan skrin atau dek salin-tampal.",
          "author": "Lina Zhang",
          "role": "Pengarah Perancangan Kewangan"
        }
      }
    }
  },
  {
    slug: 'quant-research-api-audit',
    locales: {
      zh: {
        industry: '金融科技 / 量化研究',
        title: '量化研究：校验行情 API 与风控 JSON 数据',
        subtitle: '在 Google Sheets 内完成多数据源对账，减少脚本调试时间',
        excerpt: '量化基金通过 SheetMate 在同一屏对比 API 响应、Markdown 注释与监控页面，缩短调试与风控核对时间。',
        keywords: ['SheetMate', '量化', 'JSON 校验', 'API 调试', 'Google Sheets 插件'],
        readingTime: '7 分钟',
        heroEyebrow: 'Quant Practice',
        metaTitle: 'SheetMate 量化研究 Use Case｜API 与风控数据校验',
        metaDescription: '了解量化研究员如何在 SheetMate 中并行查看 JSON、Markdown 与 iframe，提升行情与风控数据的验证效率。',
        imagePlaceholder: {
          caption: '示意图：水平分屏对照 JSON 响应与交易所状态页',
          alt: 'SheetMate 量化数据校验视图占位图'
        },
        sections: [
          {
            title: '量化团队的数据堆栈',
            paragraphs: [
              '一家数字资产量化基金需要每天验证来自交易所与风控服务的数十个行情 API。研究员把响应写入 Google Sheets 以便在策略回测脚本前做 sanity check。',
              '由于响应结构复杂，团队过去需要在浏览器控制台或 VS Code 中展开 JSON，再把关键信息粘贴回表格，效率低且容易漏掉错误字段。'
            ]
          },
          {
            title: 'SheetMate 的自动类型识别',
            paragraphs: [
              'SheetMate 在侧边栏自动识别单元格中的 JSON、Markdown 或 URL。研究员将原始响应写在隐藏列时，侧边栏会即时显示完整的 JSON 树，并允许在不离开表格的情况下修改字段做假设测试。',
              '对于返回的新闻富文本，研究员只需包裹在 Markdown 字符串中，就能在 SheetMate 中获得高亮、表格以及代码块的可读渲染。',
              '当需要检查交易所状态页或指标仪表盘时，把链接粘贴到单元格即可生成 iframe 预览，横向分屏能同时展示 API 数据与网页状态。'
            ]
          },
          {
            title: '改进后的交付节奏',
            paragraphs: [
              '通过 SheetMate 的 500ms 轮询刷新，研究员在更新查询脚本后能立刻看到新输出，减少了在本地重新运行脚本的往返。',
              '风控团队也会把待核对字段以 Markdown 清单形式放入表格，所有人共享同一视图，交接时不再需要繁琐的截图与注释。'
            ]
          }
        ],
        workflow: [
          {
            title: '写入原始响应',
            description: '将 API JSON 字符串写入隐藏列或命名范围，保持原始结构。'
          },
          {
            title: '切换至 JSON 视图',
            description: '在 SheetMate 选择 JSON 模式并展开关键节点，核对字段取值与单位。'
          },
          {
            title: '对照外部状态页',
            description: '开启水平分屏，在右侧 iframe 中查看交易所公告或健康度仪表盘。'
          },
          {
            title: '保存验证结论',
            description: '使用 Markdown 的任务列表记录异常与处理状态，确保交班清晰。'
          }
        ],
        highlights: [
          'JSON 视图支持在线编辑，适合假设测试与模拟回放。',
          '水平/垂直分屏帮助团队同步查看 API 响应与网页状态。',
          'Markdown 渲染支持代码块，便于记录查询脚本片段。'
        ],
        metrics: [
          {
            value: '65%',
            label: '脚本调试耗时',
            context: '跨团队验证流程从 40 分钟压缩到 14 分钟。'
          },
          {
            value: '0',
            label: '丢失字段',
            context: 'ReactJson 结构化视图避免了复制粘贴丢字段的问题。'
          },
          {
            value: '3',
            label: '数据源并行验证',
            context: '同一工作表同时监控交易所、风控与链上服务。'
          }
        ],
        quote: {
          text: '不用在 VS Code 与浏览器之间来回切换，SheetMate 让我们在同一张表里完成所有校验。',
          author: 'Eric Wu',
          role: '量化研究负责人'
        }
      },
      en: {
        industry: 'Fintech / Quant Research',
        title: 'Quant Research: Sanity-Check Market APIs in One View',
        subtitle: 'Validate exchange feeds, risk services, and notes without bouncing between consoles',
        excerpt: 'A digital asset fund lines up JSON payloads, analyst notes, and live status pages in SheetMate so debugging and sign-off take minutes instead of an afternoon.',
        keywords: ['SheetMate', 'Quant research', 'JSON validation', 'API debugging', 'Google Sheets extension'],
        readingTime: '7 min read',
        heroEyebrow: 'Quant Practice',
        metaTitle: 'SheetMate Quant Use Case｜Audit Market and Risk APIs Faster',
        metaDescription: 'See how quant teams inspect JSON feeds, Markdown callouts, and embedded dashboards in parallel with SheetMate to accelerate market data verification.',
        imagePlaceholder: {
          caption: 'Mockup: horizontal split reviewing an API JSON payload and an exchange status page',
          alt: 'SheetMate split view for quant API validation'
        },
        sections: [
          {
            title: 'Why the team needed a change',
            paragraphs: [
              'The fund validates dozens of exchange and risk APIs every morning before models run. Analysts had been copying responses into consoles or VS Code, then pasting key fields back into Sheets.',
              'Deeply nested payloads made that loop painfully slow and easy to miss, leaving the risk group to chase down missing data later in the day.'
            ]
          },
          {
            title: 'What SheetMate adds to the workflow',
            paragraphs: [
              'Raw JSON stored in hidden columns springs to life in SheetMate as a collapsible tree. Analysts can flip fields, compare values, and validate units without leaving the spreadsheet.',
              'Markdown callouts render with headings, tables, and code so qualitative notes are just as polished as the data.',
              'A pasted link to an exchange status page opens in an iframe, letting teams keep market health and payload data in sync with a horizontal split view.'
            ]
          },
          {
            title: 'Results the desk relies on',
            paragraphs: [
              'With SheetMate polling every half second, the latest API response appears instantly after a script rerun—no more refreshing local terminals.',
              'Risk reviewers log sign-off items as Markdown checklists inside the same sheet, eliminating screenshot threads and keeping handoffs tight.'
            ]
          }
        ],
        workflow: [
          {
            title: 'Drop in raw responses',
            description: 'Store API JSON strings in hidden columns or named ranges so SheetMate can render the structure.'
          },
          {
            title: 'Expand and inspect key nodes',
            description: 'Open SheetMate’s JSON view, unfold critical fields, and confirm values, units, and timestamps.'
          },
          {
            title: 'Cross-check live dashboards',
            description: 'Use horizontal split view to display exchange announcements or latency dashboards alongside the payload.'
          },
          {
            title: 'Log sign-off notes',
            description: 'Track anomalies, fixes, and follow-ups with Markdown task lists so every check leaves a trail.'
          }
        ],
        highlights: [
          'Interactive JSON editing supports quick hypothesis tests.',
          'Split view keeps payloads and external dashboards aligned.',
          'Markdown with code blocks preserves query snippets for future runs.'
        ],
        metrics: [
          {
            value: '65%',
            label: 'Debug time saved',
            context: 'Verification shrank from forty minutes of tab juggling to fourteen minutes in one sheet.'
          },
          {
            value: '0',
            label: 'Missing fields',
            context: 'Structured JSON trees prevent analysts from skipping nested attributes.'
          },
          {
            value: '3',
            label: 'Feeds in parallel',
            context: 'Exchange, risk, and on-chain monitors now live side by side.'
          }
        ],
        quote: {
          text: 'SheetMate keeps the entire verification loop in Google Sheets—we no longer bounce between consoles just to confirm one field.',
          author: 'Eric Wu',
          role: 'Head of Quant Research'
        }
      },
      ja: {
        "industry": "フィンテック / クオンツ・リサーチ",
        "title": "クオンツ・リサーチ：マーケットAPIを1画面でサニティチェック",
        "subtitle": "コンソールを行き来せずに取引所フィード、リスクサービス、メモを検証",
        "excerpt": "あるデジタル資産ファンドは、JSONペイロード、アナリストのメモ、ライブステータスページをSheetMateに並べて表示。デバッグと承認が午後いっぱいではなく数分で完了します。",
        "keywords": [
          "SheetMate",
          "クオンツ リサーチ",
          "JSON 検証",
          "API デバッグ",
          "Google Sheets 拡張機能"
        ],
        "readingTime": "7 分で読了",
        "heroEyebrow": "クオンツの実務",
        "metaTitle": "SheetMate クオンツ活用事例｜マーケット・リスクAPIをより速く監査",
        "metaDescription": "クオンツチームがSheetMateでJSONフィード、Markdownの注記、埋め込みダッシュボードを並行して確認し、マーケットデータ検証を加速させる方法をご紹介します。",
        "imagePlaceholder": {
          "caption": "モックアップ：APIのJSONペイロードと取引所のステータスページを左右に並べてレビュー",
          "alt": "クオンツAPI検証のためのSheetMate分割ビュー"
        },
        "sections": [
          {
            "title": "チームが変化を必要とした理由",
            "paragraphs": [
              "このファンドは毎朝モデルを実行する前に、数十の取引所およびリスクAPIを検証します。アナリストはレスポンスをコンソールやVS Codeにコピーし、主要フィールドをSheetsに貼り戻していました。",
              "深くネストされたペイロードによってこの作業ループは非常に遅く、見落としも起きやすく、リスクグループは日中になってから欠落データを追いかける羽目になっていました。"
            ]
          },
          {
            "title": "SheetMateがワークフローにもたらすもの",
            "paragraphs": [
              "非表示の列に格納された生のJSONが、SheetMate上で折りたたみ可能なツリーとして生き生きと表示されます。アナリストはスプレッドシートを離れることなく、フィールドを展開し、値を比較し、単位を検証できます。",
              "Markdownの注記は見出し、表、コードとともにレンダリングされるため、定性的なメモもデータと同じくらい洗練された見た目になります。",
              "取引所のステータスページへのリンクを貼り付けるとiframeで開き、左右の分割ビューでマーケットの健全性とペイロードデータを同期させたまま確認できます。"
            ]
          },
          {
            "title": "デスクが頼りにする成果",
            "paragraphs": [
              "SheetMateが0.5秒ごとにポーリングするため、スクリプトの再実行後に最新のAPIレスポンスが即座に表示されます。ローカルターミナルを更新する必要はもうありません。",
              "リスクレビュー担当者は同じシート内でMarkdownのチェックリストとして承認項目を記録し、スクリーンショットのやり取りをなくして引き継ぎを引き締めています。"
            ]
          }
        ],
        "workflow": [
          {
            "title": "生のレスポンスを投入",
            "description": "API のJSON文字列を非表示の列や名前付き範囲に格納すると、SheetMateが構造をレンダリングできます。"
          },
          {
            "title": "主要ノードを展開して確認",
            "description": "SheetMateのJSONビューを開き、重要なフィールドを展開して、値・単位・タイムスタンプを確認します。"
          },
          {
            "title": "ライブダッシュボードと相互確認",
            "description": "左右の分割ビューを使い、取引所の発表やレイテンシのダッシュボードをペイロードと並べて表示します。"
          },
          {
            "title": "承認メモを記録",
            "description": "Markdownのタスクリストで異常・修正・フォローアップを追跡し、すべてのチェックに記録を残します。"
          }
        ],
        "highlights": [
          "インタラクティブなJSON編集で素早い仮説検証が可能。",
          "分割ビューでペイロードと外部ダッシュボードの整合性を維持。",
          "コードブロック付きのMarkdownで、将来の実行のためにクエリのスニペットを保存。"
        ],
        "metrics": [
          {
            "value": "65%",
            "label": "デバッグ時間を削減",
            "context": "検証はタブを行き来する40分から、1枚のシート内で14分に短縮されました。"
          },
          {
            "value": "0",
            "label": "欠落フィールド",
            "context": "構造化されたJSONツリーにより、アナリストがネストされた属性を飛ばすことを防ぎます。"
          },
          {
            "value": "3",
            "label": "並行するフィード",
            "context": "取引所、リスク、オンチェーンのモニターが今では横並びで表示されます。"
          }
        ],
        "quote": {
          "text": "SheetMateは検証ループ全体をGoogle Sheetsの中に収めてくれます。たった1つのフィールドを確認するためにコンソール間を行き来することはもうありません。",
          "author": "Eric Wu",
          "role": "クオンツ・リサーチ責任者"
        }
      },
      id: {
        "industry": "Fintech / Riset Kuant",
        "title": "Riset Kuant: Periksa API Pasar dalam Satu Tampilan",
        "subtitle": "Validasi feed bursa, layanan risiko, dan catatan tanpa berpindah antar konsol",
        "excerpt": "Sebuah dana aset digital menyusun payload JSON, catatan analis, dan halaman status langsung di SheetMate sehingga proses debug dan persetujuan hanya butuh menit, bukan sepanjang sore.",
        "keywords": [
          "SheetMate",
          "Riset kuant",
          "Validasi JSON",
          "Debugging API",
          "Ekstensi Google Sheets"
        ],
        "readingTime": "Baca 7 menit",
        "heroEyebrow": "Praktik Kuant",
        "metaTitle": "Studi Kasus Kuant SheetMate｜Audit API Pasar dan Risiko Lebih Cepat",
        "metaDescription": "Lihat bagaimana tim kuant memeriksa feed JSON, callout Markdown, dan dasbor tertanam secara paralel dengan SheetMate untuk mempercepat verifikasi data pasar.",
        "imagePlaceholder": {
          "caption": "Mockup: tampilan terbagi horizontal saat meninjau payload JSON API dan halaman status bursa",
          "alt": "Tampilan terbagi SheetMate untuk validasi API kuant"
        },
        "sections": [
          {
            "title": "Mengapa tim membutuhkan perubahan",
            "paragraphs": [
              "Dana ini memvalidasi puluhan API bursa dan risiko setiap pagi sebelum model dijalankan. Para analis menyalin respons ke konsol atau VS Code, lalu menempelkan kembali bidang-bidang penting ke Sheets.",
              "Payload yang bersarang dalam membuat siklus itu sangat lambat dan mudah terlewat, sehingga grup risiko harus mengejar data yang hilang di kemudian hari."
            ]
          },
          {
            "title": "Apa yang ditambahkan SheetMate ke alur kerja",
            "paragraphs": [
              "JSON mentah yang disimpan di kolom tersembunyi menjadi hidup di SheetMate sebagai pohon yang bisa dilipat. Analis dapat membuka bidang, membandingkan nilai, dan memvalidasi satuan tanpa meninggalkan spreadsheet.",
              "Callout Markdown ditampilkan dengan judul, tabel, dan kode sehingga catatan kualitatif tampak serapi datanya.",
              "Tautan ke halaman status bursa yang ditempel akan terbuka dalam iframe, memungkinkan tim menjaga kesehatan pasar dan data payload tetap selaras lewat tampilan terbagi horizontal."
            ]
          },
          {
            "title": "Hasil yang diandalkan tim",
            "paragraphs": [
              "Dengan SheetMate yang melakukan polling setiap setengah detik, respons API terbaru muncul seketika setelah skrip dijalankan ulang—tidak perlu lagi menyegarkan terminal lokal.",
              "Peninjau risiko mencatat butir persetujuan sebagai checklist Markdown di sheet yang sama, menghilangkan utas tangkapan layar dan menjaga serah terima tetap rapat."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Masukkan respons mentah",
            "description": "Simpan string JSON API di kolom tersembunyi atau named range agar SheetMate dapat merender strukturnya."
          },
          {
            "title": "Buka dan periksa node penting",
            "description": "Buka tampilan JSON SheetMate, bentangkan bidang penting, dan konfirmasi nilai, satuan, serta timestamp."
          },
          {
            "title": "Cek silang dasbor langsung",
            "description": "Gunakan tampilan terbagi horizontal untuk menampilkan pengumuman bursa atau dasbor latensi berdampingan dengan payload."
          },
          {
            "title": "Catat catatan persetujuan",
            "description": "Lacak anomali, perbaikan, dan tindak lanjut dengan daftar tugas Markdown sehingga setiap pemeriksaan meninggalkan jejak."
          }
        ],
        "highlights": [
          "Pengeditan JSON interaktif mendukung uji hipotesis yang cepat.",
          "Tampilan terbagi menjaga payload dan dasbor eksternal tetap selaras.",
          "Markdown dengan blok kode menyimpan cuplikan kueri untuk eksekusi berikutnya."
        ],
        "metrics": [
          {
            "value": "65%",
            "label": "Waktu debug yang dihemat",
            "context": "Verifikasi menyusut dari empat puluh menit berpindah tab menjadi empat belas menit dalam satu sheet."
          },
          {
            "value": "0",
            "label": "Bidang yang hilang",
            "context": "Pohon JSON terstruktur mencegah analis melewatkan atribut bersarang."
          },
          {
            "value": "3",
            "label": "Feed paralel",
            "context": "Monitor bursa, risiko, dan on-chain kini berdampingan."
          }
        ],
        "quote": {
          "text": "SheetMate menjaga seluruh siklus verifikasi tetap di dalam Google Sheets—kami tidak lagi berpindah antar konsol hanya untuk memastikan satu bidang.",
          "author": "Eric Wu",
          "role": "Kepala Riset Kuant"
        }
      },
      es: {
        "industry": "Fintech / Investigación cuantitativa",
        "title": "Investigación cuant: revisa las APIs de mercado en una sola vista",
        "subtitle": "Valida feeds de exchanges, servicios de riesgo y notas sin saltar entre consolas",
        "excerpt": "Un fondo de activos digitales alinea cargas JSON, notas de analistas y páginas de estado en vivo dentro de SheetMate, así depurar y aprobar toma minutos en lugar de toda una tarde.",
        "keywords": [
          "SheetMate",
          "Investigación cuantitativa",
          "Validación de JSON",
          "Depuración de API",
          "Extensión de Google Sheets"
        ],
        "readingTime": "7 min de lectura",
        "heroEyebrow": "Práctica cuant",
        "metaTitle": "Caso de uso cuant de SheetMate｜Audita APIs de mercado y riesgo más rápido",
        "metaDescription": "Descubre cómo los equipos cuant inspeccionan feeds JSON, llamados en Markdown y tableros embebidos en paralelo con SheetMate para acelerar la verificación de datos de mercado.",
        "imagePlaceholder": {
          "caption": "Mockup: vista dividida horizontal revisando una carga JSON de API y una página de estado de un exchange",
          "alt": "Vista dividida de SheetMate para validación de APIs cuant"
        },
        "sections": [
          {
            "title": "Por qué el equipo necesitaba un cambio",
            "paragraphs": [
              "El fondo valida decenas de APIs de exchanges y de riesgo cada mañana antes de ejecutar los modelos. Los analistas copiaban las respuestas en consolas o en VS Code y luego pegaban los campos clave de vuelta en Sheets.",
              "Las cargas con anidamiento profundo hacían ese ciclo dolorosamente lento y propenso a omisiones, así que el grupo de riesgo terminaba persiguiendo datos faltantes más tarde en el día."
            ]
          },
          {
            "title": "Qué aporta SheetMate al flujo de trabajo",
            "paragraphs": [
              "El JSON sin procesar guardado en columnas ocultas cobra vida en SheetMate como un árbol plegable. Los analistas pueden desplegar campos, comparar valores y validar unidades sin salir de la hoja de cálculo.",
              "Los llamados en Markdown se renderizan con encabezados, tablas y código, por lo que las notas cualitativas quedan tan pulidas como los datos.",
              "Un enlace pegado a la página de estado de un exchange se abre en un iframe, lo que permite a los equipos mantener sincronizadas la salud del mercado y los datos de la carga con una vista dividida horizontal."
            ]
          },
          {
            "title": "Resultados en los que confía la mesa",
            "paragraphs": [
              "Con SheetMate consultando cada medio segundo, la respuesta más reciente de la API aparece al instante tras volver a correr un script: ya no hay que actualizar terminales locales.",
              "Los revisores de riesgo registran los puntos de aprobación como listas de verificación en Markdown dentro de la misma hoja, eliminando los hilos de capturas de pantalla y manteniendo entregas ágiles."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Coloca las respuestas en crudo",
            "description": "Guarda las cadenas JSON de la API en columnas ocultas o rangos con nombre para que SheetMate pueda renderizar la estructura."
          },
          {
            "title": "Despliega e inspecciona los nodos clave",
            "description": "Abre la vista JSON de SheetMate, despliega los campos críticos y confirma valores, unidades y marcas de tiempo."
          },
          {
            "title": "Coteja tableros en vivo",
            "description": "Usa la vista dividida horizontal para mostrar anuncios de exchanges o tableros de latencia junto a la carga."
          },
          {
            "title": "Registra notas de aprobación",
            "description": "Da seguimiento a anomalías, correcciones y pendientes con listas de tareas en Markdown para que cada revisión deje un rastro."
          }
        ],
        "highlights": [
          "La edición interactiva de JSON permite probar hipótesis rápidamente.",
          "La vista dividida mantiene alineadas las cargas y los tableros externos.",
          "El Markdown con bloques de código conserva fragmentos de consultas para ejecuciones futuras."
        ],
        "metrics": [
          {
            "value": "65%",
            "label": "Tiempo de depuración ahorrado",
            "context": "La verificación se redujo de cuarenta minutos saltando entre pestañas a catorce minutos en una sola hoja."
          },
          {
            "value": "0",
            "label": "Campos faltantes",
            "context": "Los árboles JSON estructurados evitan que los analistas se salten atributos anidados."
          },
          {
            "value": "3",
            "label": "Feeds en paralelo",
            "context": "Los monitores de exchange, riesgo y on-chain ahora conviven lado a lado."
          }
        ],
        "quote": {
          "text": "SheetMate mantiene todo el ciclo de verificación dentro de Google Sheets: ya no saltamos entre consolas solo para confirmar un campo.",
          "author": "Eric Wu",
          "role": "Director de Investigación Cuantitativa"
        }
      },
      ms: {
        "industry": "Fintech / Penyelidikan Kuant",
        "title": "Penyelidikan Kuant: Semak API Pasaran dalam Satu Paparan",
        "subtitle": "Sahkan suapan bursa, perkhidmatan risiko dan nota tanpa berulang-alik antara konsol",
        "excerpt": "Sebuah dana aset digital menyusun muatan JSON, nota penganalisis dan halaman status langsung di dalam SheetMate, jadi penyahpepijatan dan kelulusan mengambil masa beberapa minit dan bukan sepanjang petang.",
        "keywords": [
          "SheetMate",
          "Penyelidikan kuant",
          "Pengesahan JSON",
          "Penyahpepijatan API",
          "Sambungan Google Sheets"
        ],
        "readingTime": "Bacaan 7 minit",
        "heroEyebrow": "Amalan Kuant",
        "metaTitle": "Kes Penggunaan Kuant SheetMate｜Audit API Pasaran dan Risiko Lebih Pantas",
        "metaDescription": "Lihat bagaimana pasukan kuant memeriksa suapan JSON, sorotan Markdown dan papan pemuka terbenam secara selari dengan SheetMate untuk mempercepatkan pengesahan data pasaran.",
        "imagePlaceholder": {
          "caption": "Mockup: paparan terbahagi mendatar yang menyemak muatan JSON API dan halaman status bursa",
          "alt": "Paparan terbahagi SheetMate untuk pengesahan API kuant"
        },
        "sections": [
          {
            "title": "Mengapa pasukan memerlukan perubahan",
            "paragraphs": [
              "Dana ini mengesahkan puluhan API bursa dan risiko setiap pagi sebelum model dijalankan. Para penganalisis menyalin respons ke konsol atau VS Code, kemudian menampal semula medan penting ke dalam Sheets.",
              "Muatan yang bersarang dalam menjadikan kitaran itu amat perlahan dan mudah terlepas pandang, menyebabkan kumpulan risiko terpaksa memburu data yang hilang kemudian pada hari itu."
            ]
          },
          {
            "title": "Apa yang ditambah SheetMate pada aliran kerja",
            "paragraphs": [
              "JSON mentah yang disimpan dalam lajur tersembunyi hidup di SheetMate sebagai pokok boleh lipat. Penganalisis boleh membuka medan, membandingkan nilai dan mengesahkan unit tanpa meninggalkan hamparan.",
              "Sorotan Markdown dipaparkan dengan tajuk, jadual dan kod supaya nota kualitatif kelihatan sekemas datanya.",
              "Pautan yang ditampal ke halaman status bursa akan dibuka dalam iframe, membolehkan pasukan memastikan kesihatan pasaran dan data muatan kekal selaras melalui paparan terbahagi mendatar."
            ]
          },
          {
            "title": "Hasil yang diharapkan pasukan",
            "paragraphs": [
              "Dengan SheetMate membuat tinjauan setiap setengah saat, respons API terkini muncul serta-merta selepas skrip dijalankan semula—tidak perlu lagi menyegar semula terminal tempatan.",
              "Penyemak risiko merekod butiran kelulusan sebagai senarai semak Markdown dalam helaian yang sama, menghapuskan rentetan tangkapan skrin dan memastikan penyerahan tetap kemas."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Masukkan respons mentah",
            "description": "Simpan rentetan JSON API dalam lajur tersembunyi atau julat bernama supaya SheetMate boleh memaparkan strukturnya."
          },
          {
            "title": "Kembangkan dan periksa nod penting",
            "description": "Buka paparan JSON SheetMate, bentangkan medan kritikal, dan sahkan nilai, unit serta cap masa."
          },
          {
            "title": "Semak silang papan pemuka langsung",
            "description": "Gunakan paparan terbahagi mendatar untuk memaparkan pengumuman bursa atau papan pemuka kependaman bersebelahan dengan muatan."
          },
          {
            "title": "Rekod nota kelulusan",
            "description": "Jejaki anomali, pembetulan dan susulan dengan senarai tugasan Markdown supaya setiap semakan meninggalkan jejak."
          }
        ],
        "highlights": [
          "Penyuntingan JSON interaktif menyokong ujian hipotesis yang pantas.",
          "Paparan terbahagi memastikan muatan dan papan pemuka luaran kekal selari.",
          "Markdown dengan blok kod menyimpan cebisan pertanyaan untuk larian akan datang."
        ],
        "metrics": [
          {
            "value": "65%",
            "label": "Masa nyahpepijat dijimatkan",
            "context": "Pengesahan menyusut daripada empat puluh minit berulang-alik tab kepada empat belas minit dalam satu helaian."
          },
          {
            "value": "0",
            "label": "Medan yang hilang",
            "context": "Pokok JSON berstruktur menghalang penganalisis daripada terlepas atribut bersarang."
          },
          {
            "value": "3",
            "label": "Suapan selari",
            "context": "Pemantau bursa, risiko dan on-chain kini berada bersebelahan."
          }
        ],
        "quote": {
          "text": "SheetMate mengekalkan keseluruhan kitaran pengesahan dalam Google Sheets—kami tidak lagi berulang-alik antara konsol hanya untuk mengesahkan satu medan.",
          "author": "Eric Wu",
          "role": "Ketua Penyelidikan Kuant"
        }
      }
    }
  },
  {
    slug: 'creator-calendar-automation',
    locales: {
      zh: {
        industry: '自媒体 / 品牌营销',
        title: '自媒体矩阵：让脚本与素材在同一屏协同',
        subtitle: '把 Markdown 脚本、图片数组与 CTA 模板合并入一个 Google Sheet',
        excerpt: '跨平台内容团队使用 SheetMate 对齐脚本、素材与排期，审核效率提升并减少错漏。',
        keywords: ['SheetMate', '自媒体', '内容运营', 'Markdown 脚本', '素材审核'],
        readingTime: '6 分钟',
        heroEyebrow: 'Content Ops',
        metaTitle: 'SheetMate 自媒体 Use Case｜脚本与素材协同审核',
        metaDescription: '了解内容团队如何借助 SheetMate 的 Markdown 渲染与图片预览，在 Google Sheets 中完成脚本审核与素材校对。',
        imagePlaceholder: {
          caption: '示意图：脚本 Markdown 与素材图片分屏预览',
          alt: 'SheetMate 内容运营视图占位图'
        },
        sections: [
          {
            title: '多平台发布压力',
            paragraphs: [
              '一家跨境电商的内容团队每周要为 Instagram、YouTube 和小红书制作超过 40 条内容。策划案、脚本和素材链接都堆在同一个 Google Sheet。',
              '团队以往需要来回切换 Notion、Docs 与云盘确认文案与素材是否匹配，导致排期延迟且跨部门沟通成本高。'
            ]
          },
          {
            title: 'SheetMate 让创意与素材同步',
            paragraphs: [
              '编辑把脚本以 Markdown 形式写在单元格中，SheetMate 会即时渲染标题层级、加粗、引用及清单，方便审核。',
              '摄影师将素材地址放在数组中，SheetMate 自动识别图像链接并生成图片墙，创意总监可以一次性确认封面。',
              '通过水平分屏，左栏展示脚本，右栏展示图片或参考链接 iframe，确保视觉与文案同步。'
            ]
          },
          {
            title: '运营成果',
            paragraphs: [
              '营销部门根据不同渠道需求预设了 Markdown 模板，增加 CTA、Hashtag 与发布时间，复制到新行即可复用。',
              '团队也利用侧边栏的单元格位置提示，与远程自由职业者共享截图时，能清楚指出需要修改的条目。'
            ]
          }
        ],
        workflow: [
          {
            title: '整理内容管线',
            description: '按渠道在 Sheet 中建立模板列，例如 Markdown 脚本、素材数组与投放说明。'
          },
          {
            title: '渲染脚本与素材',
            description: '在 SheetMate 单栏模式下校对 Markdown 渲染效果与图片顺序。'
          },
          {
            title: '分屏比对渠道差异',
            description: '开启水平分屏，一侧显示短视频脚本，另一侧展示广告素材或参考链接。'
          },
          {
            title: '输出发布清单',
            description: '将 Markdown 中的任务列表勾选完成，截图或导出为 PDF 供渠道负责人执行。'
          }
        ],
        highlights: [
          'Markdown 渲染保持标题、引用与任务列表格式。',
          '图像数组自动生成素材预览墙。',
          '单元格坐标提示方便异步协同。'
        ],
        metrics: [
          {
            value: '30%',
            label: '脚本审核时间',
            context: '每条脚本的改稿时间由 20 分钟降至 14 分钟。'
          },
          {
            value: '5×',
            label: '模板复用率',
            context: '跨渠道统一使用同一套 Markdown 模板。'
          },
          {
            value: '100%',
            label: '素材匹配准确率',
            context: '图文对齐后减少了发布口误。'
          }
        ],
        quote: {
          text: 'SheetMate 帮我们把创意脚本、素材和发布指引放在同一张表中，审稿效率提升非常明显。',
          author: 'Mia Chen',
          role: '品牌内容总监'
        }
      },
      en: {
        industry: 'Creators / Content Marketing',
        title: 'Creator Teams: Keep Scripts and Assets in Sync',
        subtitle: 'Combine Markdown scripts, image arrays, and CTA templates inside one Google Sheet',
        excerpt: 'Multichannel content teams use SheetMate to align scripts, assets, and schedules in one place, improving review speed and reducing mistakes.',
        keywords: ['SheetMate', 'Creator workflow', 'Content operations', 'Markdown script', 'Asset review'],
        readingTime: '6 min read',
        heroEyebrow: 'Content Ops',
        metaTitle: 'SheetMate Creator Use Case｜Review Scripts and Assets Together',
        metaDescription: 'Discover how content teams rely on SheetMate’s Markdown rendering and image gallery to audit scripts and assets directly in Google Sheets.',
        imagePlaceholder: {
          caption: 'Mockup: split view showing Markdown script and image gallery',
          alt: 'SheetMate content workflow split view placeholder'
        },
        sections: [
          {
            title: 'Publishing pressure across channels',
            paragraphs: [
              'A commerce studio ships more than forty assets a week across Instagram, YouTube, TikTok, and Xiaohongshu. Every brief, script, and asset link lives in one production sheet.',
              'They bounced between Notion, Docs, and shared drives to confirm that copy matched visuals, which slowed approvals and invited mistakes.'
            ]
          },
          {
            title: 'Scripts and visuals finally stay aligned',
            paragraphs: [
              'Writers draft scripts in Markdown directly inside the sheet. SheetMate renders headings, emphasis, and checklists so editors can scan everything in seconds.',
              'Photographers drop CDN links into arrays and SheetMate turns them into an instant gallery so creative leads approve thumbnails in one pass.',
              'A horizontal split keeps the Markdown script on the left and reference imagery or landing pages on the right, making mismatch issues obvious before handoff.'
            ]
          },
          {
            title: 'Operational wins',
            paragraphs: [
              'Marketing built channel-specific Markdown templates with CTAs, hashtags, and publishing cadence baked in. Duplicating a row spins up the entire playbook.',
              'Cell location badges make remote collaboration easy—screenshots shared with freelancers point to the exact line that needs edits.'
            ]
          }
        ],
        workflow: [
          {
            title: 'Structure the content pipeline',
            description: 'Define columns for Markdown scripts, asset arrays, and placement notes per channel.'
          },
          {
            title: 'Render scripts and assets',
            description: 'Use SheetMate to preview Markdown formatting and confirm the order of assets before approval.'
          },
          {
            title: 'Compare channel variations',
            description: 'Split the view to show the short-form script next to paid-media creative or reference material.'
          },
          {
            title: 'Publish with confidence',
            description: 'Check off Markdown tasks, capture the combined view, and hand it directly to channel owners.'
          }
        ],
        highlights: [
          'Markdown rendering preserves tone, structure, and task lists.',
          'Image arrays automatically become shareable review galleries.',
          'Cell coordinates keep async feedback fast and precise.'
        ],
        metrics: [
          {
            value: '30%',
            label: 'Faster approvals',
            context: 'Script review time dropped from twenty minutes per asset to fourteen.'
          },
          {
            value: '5×',
            label: 'Template reuse',
            context: 'One Markdown layout now powers campaigns across every channel.'
          },
          {
            value: '100%',
            label: 'Asset-to-copy accuracy',
            context: 'Every post now ships with matching copy and visuals.'
          }
        ],
        quote: {
          text: 'SheetMate keeps scripts, asset notes, and publishing instructions in one view—our reviews finally move at the pace of the calendar.',
          author: 'Mia Chen',
          role: 'Director of Brand Content'
        }
      },
      ja: {
        "industry": "クリエイター／コンテンツマーケティング",
        "title": "クリエイターチーム：台本と素材を常に同期",
        "subtitle": "Markdown台本、画像配列、CTAテンプレートを1つのGoogle Sheetにまとめて管理",
        "excerpt": "マルチチャネルのコンテンツチームはSheetMateを使い、台本・素材・スケジュールを1か所に集約。レビューを高速化し、ミスを減らしています。",
        "keywords": [
          "SheetMate",
          "クリエイターワークフロー",
          "コンテンツ運用",
          "Markdown台本",
          "素材レビュー"
        ],
        "readingTime": "6分で読了",
        "heroEyebrow": "コンテンツオペレーション",
        "metaTitle": "SheetMate クリエイター活用事例｜台本と素材をまとめてレビュー",
        "metaDescription": "コンテンツチームがSheetMateのMarkdownレンダリングと画像ギャラリーを活用し、Google Sheets上で台本と素材を直接チェックする方法をご紹介します。",
        "imagePlaceholder": {
          "caption": "モックアップ：Markdown台本と画像ギャラリーを並べた分割表示",
          "alt": "SheetMateコンテンツワークフローの分割表示プレースホルダー"
        },
        "sections": [
          {
            "title": "複数チャネルにまたがる公開プレッシャー",
            "paragraphs": [
              "あるコマーススタジオは、Instagram、YouTube、TikTok、Xiaohongshuに毎週40点以上の素材を投入しています。すべてのブリーフ、台本、素材リンクは1つの制作シートに集約されています。",
              "コピーとビジュアルの整合性を確認するためにNotion、Docs、共有ドライブを行き来しており、承認が遅れ、ミスを招いていました。"
            ]
          },
          {
            "title": "台本とビジュアルがついに揃う",
            "paragraphs": [
              "ライターはシート内で直接Markdownの台本を書きます。SheetMateが見出し、強調、チェックリストをレンダリングするので、編集者は一瞬ですべてを見渡せます。",
              "カメラマンがCDNリンクを配列に入れると、SheetMateがそれを即座にギャラリー化し、クリエイティブリードがサムネイルを一度で承認できます。",
              "横方向の分割表示で、左にMarkdown台本、右に参考画像やランディングページを並べることで、引き渡し前に不一致が一目で分かります。"
            ]
          },
          {
            "title": "業務上の成果",
            "paragraphs": [
              "マーケティングは、CTA、ハッシュタグ、公開頻度を組み込んだチャネル別Markdownテンプレートを作成。行を複製するだけでプレイブック全体が立ち上がります。",
              "セル位置のバッジによってリモートコラボレーションが容易に。フリーランサーと共有するスクリーンショットが、修正が必要な行を正確に指し示します。"
            ]
          }
        ],
        "workflow": [
          {
            "title": "コンテンツパイプラインを構築",
            "description": "チャネルごとにMarkdown台本、素材配列、配置メモの列を定義します。"
          },
          {
            "title": "台本と素材をレンダリング",
            "description": "SheetMateでMarkdownの書式をプレビューし、承認前に素材の順序を確認します。"
          },
          {
            "title": "チャネルごとのバリエーションを比較",
            "description": "表示を分割し、ショート尺の台本を有料広告のクリエイティブや参考資料の隣に並べます。"
          },
          {
            "title": "自信を持って公開",
            "description": "Markdownのタスクにチェックを入れ、まとめた表示をキャプチャして、各チャネル担当者へ直接引き渡します。"
          }
        ],
        "highlights": [
          "Markdownレンダリングがトーン、構成、タスクリストをそのまま保持。",
          "画像配列が自動的に共有可能なレビューギャラリーに。",
          "セル座標により非同期フィードバックを素早く正確に。"
        ],
        "metrics": [
          {
            "value": "30%",
            "label": "承認の高速化",
            "context": "台本レビューの時間が1素材あたり20分から14分に短縮。"
          },
          {
            "value": "5×",
            "label": "テンプレートの再利用",
            "context": "1つのMarkdownレイアウトが全チャネルのキャンペーンを支えます。"
          },
          {
            "value": "100%",
            "label": "素材とコピーの一致率",
            "context": "すべての投稿が一致したコピーとビジュアルで配信されるように。"
          }
        ],
        "quote": {
          "text": "SheetMateは台本、素材メモ、公開指示を1つのビューにまとめてくれます。おかげでレビューがようやくカレンダーのペースに追いつきました。",
          "author": "Mia Chen",
          "role": "ブランドコンテンツ ディレクター"
        }
      },
      id: {
        "industry": "Kreator / Pemasaran Konten",
        "title": "Tim Kreator: Jaga Naskah dan Aset Tetap Sinkron",
        "subtitle": "Gabungkan naskah Markdown, array gambar, dan template CTA dalam satu Google Sheet",
        "excerpt": "Tim konten multichannel menggunakan SheetMate untuk menyelaraskan naskah, aset, dan jadwal di satu tempat, mempercepat peninjauan dan mengurangi kesalahan.",
        "keywords": [
          "SheetMate",
          "Workflow kreator",
          "Operasional konten",
          "Naskah Markdown",
          "Review aset"
        ],
        "readingTime": "Baca 6 menit",
        "heroEyebrow": "Operasional Konten",
        "metaTitle": "Studi Kasus Kreator SheetMate｜Tinjau Naskah dan Aset Bersamaan",
        "metaDescription": "Pelajari bagaimana tim konten mengandalkan rendering Markdown dan galeri gambar SheetMate untuk mengaudit naskah dan aset langsung di Google Sheets.",
        "imagePlaceholder": {
          "caption": "Mockup: tampilan terbagi yang menampilkan naskah Markdown dan galeri gambar",
          "alt": "Placeholder tampilan terbagi workflow konten SheetMate"
        },
        "sections": [
          {
            "title": "Tekanan publikasi di berbagai channel",
            "paragraphs": [
              "Sebuah studio commerce merilis lebih dari empat puluh aset per minggu di Instagram, YouTube, TikTok, dan Xiaohongshu. Setiap brief, naskah, dan tautan aset tersimpan dalam satu sheet produksi.",
              "Mereka bolak-balik antara Notion, Docs, dan shared drive untuk memastikan teks sesuai dengan visual, sehingga persetujuan melambat dan rawan kesalahan."
            ]
          },
          {
            "title": "Naskah dan visual akhirnya selaras",
            "paragraphs": [
              "Penulis menyusun naskah dalam Markdown langsung di dalam sheet. SheetMate merender heading, penekanan, dan checklist sehingga editor dapat memindainya dalam hitungan detik.",
              "Fotografer menempatkan tautan CDN ke dalam array dan SheetMate mengubahnya menjadi galeri instan sehingga creative lead menyetujui thumbnail dalam sekali lihat.",
              "Tampilan terbagi horizontal menjaga naskah Markdown di kiri dan gambar referensi atau landing page di kanan, sehingga ketidaksesuaian terlihat jelas sebelum serah terima."
            ]
          },
          {
            "title": "Keunggulan operasional",
            "paragraphs": [
              "Tim marketing membangun template Markdown khusus channel dengan CTA, hashtag, dan kadens publikasi yang sudah terpasang. Menduplikasi satu baris langsung menghidupkan seluruh playbook.",
              "Badge lokasi sel memudahkan kolaborasi jarak jauh—tangkapan layar yang dibagikan ke freelancer menunjukkan baris persis yang perlu diedit."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Susun pipeline konten",
            "description": "Tentukan kolom untuk naskah Markdown, array aset, dan catatan penempatan per channel."
          },
          {
            "title": "Render naskah dan aset",
            "description": "Gunakan SheetMate untuk meninjau format Markdown dan memastikan urutan aset sebelum persetujuan."
          },
          {
            "title": "Bandingkan variasi channel",
            "description": "Bagi tampilan untuk menempatkan naskah short-form di samping materi iklan berbayar atau bahan referensi."
          },
          {
            "title": "Publikasikan dengan percaya diri",
            "description": "Centang tugas Markdown, tangkap tampilan gabungannya, dan serahkan langsung ke pemilik channel."
          }
        ],
        "highlights": [
          "Rendering Markdown mempertahankan nada, struktur, dan daftar tugas.",
          "Array gambar otomatis menjadi galeri review yang bisa dibagikan.",
          "Koordinat sel menjaga umpan balik asinkron tetap cepat dan presisi."
        ],
        "metrics": [
          {
            "value": "30%",
            "label": "Persetujuan lebih cepat",
            "context": "Waktu peninjauan naskah turun dari dua puluh menit per aset menjadi empat belas."
          },
          {
            "value": "5×",
            "label": "Pemakaian ulang template",
            "context": "Satu layout Markdown kini menggerakkan kampanye di setiap channel."
          },
          {
            "value": "100%",
            "label": "Akurasi aset-ke-teks",
            "context": "Setiap postingan kini dirilis dengan teks dan visual yang selaras."
          }
        ],
        "quote": {
          "text": "SheetMate menyatukan naskah, catatan aset, dan instruksi publikasi dalam satu tampilan—peninjauan kami akhirnya bergerak secepat kalender.",
          "author": "Mia Chen",
          "role": "Direktur Konten Brand"
        }
      },
      es: {
        "industry": "Creadores / Marketing de contenidos",
        "title": "Equipos de creadores: mantén guiones y recursos sincronizados",
        "subtitle": "Combina guiones en Markdown, arreglos de imágenes y plantillas de CTA dentro de una sola Google Sheet",
        "excerpt": "Los equipos de contenido multicanal usan SheetMate para alinear guiones, recursos y calendarios en un solo lugar, agilizando las revisiones y reduciendo errores.",
        "keywords": [
          "SheetMate",
          "Flujo de trabajo para creadores",
          "Operaciones de contenido",
          "Guion en Markdown",
          "Revisión de recursos"
        ],
        "readingTime": "6 min de lectura",
        "heroEyebrow": "Operaciones de contenido",
        "metaTitle": "Caso de uso de SheetMate para creadores｜Revisa guiones y recursos juntos",
        "metaDescription": "Descubre cómo los equipos de contenido aprovechan el renderizado de Markdown y la galería de imágenes de SheetMate para auditar guiones y recursos directamente en Google Sheets.",
        "imagePlaceholder": {
          "caption": "Mockup: vista dividida que muestra un guion en Markdown y una galería de imágenes",
          "alt": "Marcador de posición de vista dividida del flujo de contenido de SheetMate"
        },
        "sections": [
          {
            "title": "Presión de publicación en todos los canales",
            "paragraphs": [
              "Un estudio de comercio publica más de cuarenta recursos por semana en Instagram, YouTube, TikTok y Xiaohongshu. Cada brief, guion y enlace de recurso vive en una sola hoja de producción.",
              "Saltaban entre Notion, Docs y unidades compartidas para confirmar que el texto coincidiera con lo visual, lo que ralentizaba las aprobaciones y abría la puerta a errores."
            ]
          },
          {
            "title": "Los guiones y lo visual por fin coinciden",
            "paragraphs": [
              "Los redactores escriben guiones en Markdown directamente dentro de la hoja. SheetMate renderiza encabezados, énfasis y listas de tareas para que los editores lo revisen todo en segundos.",
              "Los fotógrafos colocan enlaces de CDN en arreglos y SheetMate los convierte en una galería instantánea, de modo que los líderes creativos aprueban las miniaturas de una sola pasada.",
              "Una división horizontal mantiene el guion en Markdown a la izquierda y las imágenes de referencia o landing pages a la derecha, dejando en evidencia las inconsistencias antes de la entrega."
            ]
          },
          {
            "title": "Ganancias operativas",
            "paragraphs": [
              "Marketing creó plantillas de Markdown específicas por canal con CTAs, hashtags y cadencia de publicación ya integrados. Duplicar una fila pone en marcha todo el playbook.",
              "Las insignias de ubicación de celda facilitan la colaboración remota: las capturas compartidas con freelancers señalan la línea exacta que necesita ediciones."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Estructura el pipeline de contenido",
            "description": "Define columnas para guiones en Markdown, arreglos de recursos y notas de colocación por canal."
          },
          {
            "title": "Renderiza guiones y recursos",
            "description": "Usa SheetMate para previsualizar el formato Markdown y confirmar el orden de los recursos antes de la aprobación."
          },
          {
            "title": "Compara variaciones por canal",
            "description": "Divide la vista para mostrar el guion de formato corto junto al creativo de medios pagados o el material de referencia."
          },
          {
            "title": "Publica con confianza",
            "description": "Marca las tareas de Markdown, captura la vista combinada y entrégala directamente a los responsables de cada canal."
          }
        ],
        "highlights": [
          "El renderizado de Markdown conserva el tono, la estructura y las listas de tareas.",
          "Los arreglos de imágenes se convierten automáticamente en galerías de revisión para compartir.",
          "Las coordenadas de celda mantienen la retroalimentación asíncrona rápida y precisa."
        ],
        "metrics": [
          {
            "value": "30%",
            "label": "Aprobaciones más rápidas",
            "context": "El tiempo de revisión de guiones bajó de veinte minutos por recurso a catorce."
          },
          {
            "value": "5×",
            "label": "Reutilización de plantillas",
            "context": "Un solo diseño en Markdown ahora impulsa campañas en todos los canales."
          },
          {
            "value": "100%",
            "label": "Precisión entre recurso y texto",
            "context": "Cada publicación ahora sale con texto y elementos visuales que coinciden."
          }
        ],
        "quote": {
          "text": "SheetMate mantiene los guiones, las notas de recursos y las instrucciones de publicación en una sola vista; nuestras revisiones por fin avanzan al ritmo del calendario.",
          "author": "Mia Chen",
          "role": "Directora de Contenido de Marca"
        }
      },
      ms: {
        "industry": "Pencipta / Pemasaran Kandungan",
        "title": "Pasukan Pencipta: Pastikan Skrip dan Aset Sentiasa Segerak",
        "subtitle": "Gabungkan skrip Markdown, tatasusunan imej, dan templat CTA dalam satu Google Sheet",
        "excerpt": "Pasukan kandungan berbilang saluran menggunakan SheetMate untuk menyelaraskan skrip, aset, dan jadual di satu tempat, mempercepatkan semakan dan mengurangkan kesilapan.",
        "keywords": [
          "SheetMate",
          "Aliran kerja pencipta",
          "Operasi kandungan",
          "Skrip Markdown",
          "Semakan aset"
        ],
        "readingTime": "Bacaan 6 minit",
        "heroEyebrow": "Operasi Kandungan",
        "metaTitle": "Kes Penggunaan Pencipta SheetMate｜Semak Skrip dan Aset Bersama",
        "metaDescription": "Ketahui cara pasukan kandungan bergantung pada pemaparan Markdown dan galeri imej SheetMate untuk mengaudit skrip dan aset terus dalam Google Sheets.",
        "imagePlaceholder": {
          "caption": "Mockup: paparan terbahagi menunjukkan skrip Markdown dan galeri imej",
          "alt": "Ruang letak paparan terbahagi aliran kerja kandungan SheetMate"
        },
        "sections": [
          {
            "title": "Tekanan penerbitan merentas saluran",
            "paragraphs": [
              "Sebuah studio commerce menghantar lebih daripada empat puluh aset seminggu merentas Instagram, YouTube, TikTok, dan Xiaohongshu. Setiap brief, skrip, dan pautan aset disimpan dalam satu helaian produksi.",
              "Mereka berulang-alik antara Notion, Docs, dan pemacu kongsi untuk mengesahkan teks sepadan dengan visual, lalu memperlahankan kelulusan dan membuka ruang kepada kesilapan."
            ]
          },
          {
            "title": "Skrip dan visual akhirnya selaras",
            "paragraphs": [
              "Penulis menyusun skrip dalam Markdown terus di dalam helaian. SheetMate memaparkan tajuk, penegasan, dan senarai semak supaya editor dapat mengimbas segalanya dalam beberapa saat.",
              "Jurugambar meletakkan pautan CDN ke dalam tatasusunan dan SheetMate menukarnya menjadi galeri serta-merta supaya ketua kreatif meluluskan thumbnail dalam satu pusingan.",
              "Pembahagian melintang mengekalkan skrip Markdown di sebelah kiri dan imej rujukan atau landing page di sebelah kanan, menjadikan isu ketidakpadanan jelas kelihatan sebelum penyerahan."
            ]
          },
          {
            "title": "Kelebihan operasi",
            "paragraphs": [
              "Pasukan pemasaran membina templat Markdown khusus saluran dengan CTA, hashtag, dan kadens penerbitan yang telah disertakan. Menduplikasi satu baris terus menghidupkan keseluruhan playbook.",
              "Lencana lokasi sel memudahkan kerjasama jarak jauh—tangkap skrin yang dikongsi dengan freelancer menunjukkan baris tepat yang perlu disunting."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Strukturkan saluran paip kandungan",
            "description": "Tentukan lajur untuk skrip Markdown, tatasusunan aset, dan nota penempatan bagi setiap saluran."
          },
          {
            "title": "Paparkan skrip dan aset",
            "description": "Gunakan SheetMate untuk pratonton format Markdown dan mengesahkan susunan aset sebelum kelulusan."
          },
          {
            "title": "Bandingkan variasi saluran",
            "description": "Bahagikan paparan untuk menunjukkan skrip bentuk pendek di sebelah bahan kreatif media berbayar atau bahan rujukan."
          },
          {
            "title": "Terbitkan dengan yakin",
            "description": "Tandakan tugas Markdown, tangkap paparan gabungan, dan serahkannya terus kepada pemilik saluran."
          }
        ],
        "highlights": [
          "Pemaparan Markdown mengekalkan nada, struktur, dan senarai tugas.",
          "Tatasusunan imej secara automatik menjadi galeri semakan yang boleh dikongsi.",
          "Koordinat sel memastikan maklum balas tak segerak kekal pantas dan tepat."
        ],
        "metrics": [
          {
            "value": "30%",
            "label": "Kelulusan lebih pantas",
            "context": "Masa semakan skrip menurun daripada dua puluh minit setiap aset kepada empat belas."
          },
          {
            "value": "5×",
            "label": "Penggunaan semula templat",
            "context": "Satu susun atur Markdown kini menggerakkan kempen merentas setiap saluran."
          },
          {
            "value": "100%",
            "label": "Ketepatan aset dengan teks",
            "context": "Setiap siaran kini dihantar dengan teks dan visual yang sepadan."
          }
        ],
        "quote": {
          "text": "SheetMate mengekalkan skrip, nota aset, dan arahan penerbitan dalam satu paparan—semakan kami akhirnya bergerak selaras dengan rentak kalendar.",
          "author": "Mia Chen",
          "role": "Pengarah Kandungan Jenama"
        }
      }
    }
  },
  {
    slug: 'llm-evaluation-scorecards',
    locales: {
      zh: {
        industry: 'AI / LLM 评估',
        title: 'LLM 评估：同步对话上下文与评分指标',
        subtitle: '把 prompt、模型回复与 JSON 评分集合并入一个可视化面板',
        excerpt: '评估团队利用 SheetMate 的对话视图与 JSON 编辑能力，加速模型回归测试与问题复盘。',
        keywords: ['SheetMate', 'LLM 评估', '对话管理', 'JSON 评分', 'Prompt 工程'],
        readingTime: '7 分钟',
        heroEyebrow: 'AI Evaluation',
        metaTitle: 'SheetMate LLM 评估 Use Case｜对话与评分同步',
        metaDescription: '了解 LLM 团队如何通过 SheetMate 的对话气泡、JSON 视图与分屏布局，加速回归测试与风险复盘。',
        imagePlaceholder: {
          caption: '示意图：对话气泡与评分 JSON 垂直分屏',
          alt: 'SheetMate LLM 评估视图占位图'
        },
        sections: [
          {
            title: '评估团队的痛点',
            paragraphs: [
              '一家 LLM 平台的评估团队在 Google Sheets 中维护数百条测试案例，需要记录 system prompt、用户输入以及模型回复。',
              '传统做法是把对话复制到文档或 JSON 文件，再回贴评分，过程冗长且难以对比回归版本。'
            ]
          },
          {
            title: 'SheetMate 的对话视图',
            paragraphs: [
              '团队把对话保存在数组中，每个对象包含 role 与 content，SheetMate 自动识别为对话并以气泡形式显示 system、user 与 assistant。',
              '工程师在第二视图展示 JSON 评分结果，或直接编辑字段进行人工修正。',
              '通过垂直分屏，评估员可以在上半部分查看对话，在下半部分输入 Markdown 备注和指标权重。'
            ]
          },
          {
            title: '回归测试效率',
            paragraphs: [
              'SheetMate 的 500ms 轮询让评估员在更新新版本结果后立刻刷新，无需重新加载整张表。',
              '团队建立标准化的 Markdown 模板记录风险和 edge case，大幅降低了评审沟通成本。'
            ]
          }
        ],
        workflow: [
          {
            title: '写入对话数组',
            description: '将 LLM 对话存为 role 和 content 的数组，便于 SheetMate 自动识别。'
          },
          {
            title: '对照评分类 JSON',
            description: '在第二视图打开 JSON 得分，检查自动化指标与人工评分差异。'
          },
          {
            title: '记录复盘要点',
            description: '使用 Markdown 模板写下复现步骤、标签与追踪编号。'
          },
          {
            title: '导出给工程团队',
            description: '截取分屏视图发送到 issue tracker，确保上下文完整。'
          }
        ],
        highlights: [
          'DialogView 自动区分 system / user / assistant 消息。',
          'JSON 视图支持 inline 编辑，方便调整评分。',
          '多视图模式将对话、备注与指标放在同一屏。'
        ],
        metrics: [
          {
            value: '2×',
            label: '回归评审速度',
            context: '每个测试集的审核时间从 90 分钟降至 45 分钟。'
          },
          {
            value: '40%',
            label: 'Bug 重现率下降',
            context: '完整上下文减少了缺失信息导致的误判。'
          },
          {
            value: '0',
            label: '临时文档',
            context: '评估团队不再维护额外的 Google Docs。'
          }
        ],
        quote: {
          text: 'SheetMate 是我们评估工作流的可视化控制台，对话和评分上下文完全同步。',
          author: 'Jason Li',
          role: 'LLM 评估负责人'
        }
      },
      en: {
        industry: 'AI / LLM Evaluation',
        title: 'LLM Evaluation: Keep Dialogs and Scores in Lockstep',
        subtitle: 'Bring prompts, model replies, and scorecards into one shareable workspace',
        excerpt: 'Evaluation teams lean on SheetMate’s dialog bubbles and JSON panels to review regressions faster and capture better feedback for engineering.',
        keywords: ['SheetMate', 'LLM evaluation', 'Conversation review', 'JSON scoring', 'Prompt engineering'],
        readingTime: '7 min read',
        heroEyebrow: 'AI Evaluation',
        metaTitle: 'SheetMate LLM Use Case｜Synchronize Dialogs and Scorecards',
        metaDescription: 'Learn how LLM teams use SheetMate to view prompts, responses, and JSON scores together so regression reviews ship faster.',
        imagePlaceholder: {
          caption: 'Mockup: vertical split showing chat bubbles and evaluation JSON side by side',
          alt: 'SheetMate split screen for LLM evaluation'
        },
        sections: [
          {
            title: 'Why regressions dragged',
            paragraphs: [
              'The evaluation group maintained hundreds of test cases with system prompts, user input, and model replies inside Google Sheets.',
              'They copied conversations into docs, pasted scores back into the sheet, and lost context every time they compared model versions.'
            ]
          },
          {
            title: 'What SheetMate changes',
            paragraphs: [
              'Conversations stored as role/content arrays render automatically as chat bubbles, making it easy to scan system, user, and assistant turns.',
              'JSON scorecards open in the second pane, where reviewers can tweak values or add manual overrides on the fly.',
              'A vertical split keeps dialog context up top while Markdown notes capture weighting, risk tags, and bug IDs below.'
            ]
          },
          {
            title: 'Reviews at production speed',
            paragraphs: [
              'SheetMate refreshes every few hundred milliseconds, so updated runs appear instantly without reloading the workbook.',
              'Standard Markdown templates now hold risk callouts, reproduction steps, and owners, giving engineering complete context from a single screenshot.'
            ]
          }
        ],
        workflow: [
          {
            title: 'Store dialogs as arrays',
            description: 'Log each turn with role and content so SheetMate renders chat bubbles automatically.'
          },
          {
            title: 'Open scorecards alongside',
            description: 'Load JSON metrics in the second pane to compare automated scores with human judgment.'
          },
          {
            title: 'Capture findings in Markdown',
            description: 'Document reproduction steps, tags, and assignees right below the conversation.'
          },
          {
            title: 'Ship context to engineering',
            description: 'Grab the split view and attach it to tickets so nothing gets lost in translation.'
          }
        ],
        highlights: [
          'Chat bubbles separate system, user, and assistant roles automatically.',
          'Inline JSON edits simplify manual score adjustments.',
          'Split layouts keep dialogs, notes, and metrics in one frame.'
        ],
        metrics: [
          {
            value: '2×',
            label: 'Faster regression reviews',
            context: 'Each test suite now ships in forty-five minutes instead of ninety.'
          },
          {
            value: '40%',
            label: 'Fewer re-opened bugs',
            context: 'Complete context cuts misdiagnosed issues nearly in half.'
          },
          {
            value: '0',
            label: 'Extra docs maintained',
            context: 'The team no longer clones conversations into separate Google Docs.'
          }
        ],
        quote: {
          text: 'SheetMate is our evaluation control room—dialog and score context never drift apart anymore.',
          author: 'Jason Li',
          role: 'Head of LLM Evaluation'
        }
      },
      ja: {
        "industry": "AI / LLM 評価",
        "title": "LLM 評価：対話とスコアを完全に同期させる",
        "subtitle": "プロンプト、モデルの返答、スコアカードを共有可能な一つのワークスペースに集約",
        "excerpt": "評価チームは SheetMate の対話バブルと JSON パネルを活用し、回帰チェックを高速化しながらエンジニアへのフィードバック精度を高めています。",
        "keywords": [
          "SheetMate",
          "LLM 評価",
          "会話レビュー",
          "JSON スコアリング",
          "プロンプトエンジニアリング"
        ],
        "readingTime": "7分で読了",
        "heroEyebrow": "AI 評価",
        "metaTitle": "SheetMate LLM 活用事例｜対話とスコアカードを同期",
        "metaDescription": "LLM チームが SheetMate を使ってプロンプト、レスポンス、JSON スコアをまとめて確認し、回帰レビューをより速く出荷する方法をご紹介します。",
        "imagePlaceholder": {
          "caption": "モックアップ：チャットバブルと評価用 JSON を左右に並べた縦割りレイアウト",
          "alt": "LLM 評価向け SheetMate の分割画面"
        },
        "sections": [
          {
            "title": "回帰チェックが滞っていた理由",
            "paragraphs": [
              "評価チームは数百件のテストケースを、システムプロンプト、ユーザー入力、モデルの返答とともに Google Sheets で管理していました。",
              "会話をドキュメントにコピーし、スコアをシートに貼り戻すたびに、モデルバージョンを比較する文脈が失われていました。"
            ]
          },
          {
            "title": "SheetMate がもたらす変化",
            "paragraphs": [
              "role/content の配列として保存された会話は自動的にチャットバブルとして表示され、システム・ユーザー・アシスタントの発話を一目で追えます。",
              "JSON スコアカードは右側のペインに開き、レビュアーがその場で値を調整したり手動の上書きを加えたりできます。",
              "縦割りレイアウトの上部に対話の文脈を保ちつつ、下部の Markdown メモに重み付け、リスクタグ、バグ ID を記録します。"
            ]
          },
          {
            "title": "本番スピードのレビュー",
            "paragraphs": [
              "SheetMate は数百ミリ秒ごとに更新されるため、ブックを再読み込みしなくても最新の実行結果が即座に反映されます。",
              "標準の Markdown テンプレートにリスクの注意点、再現手順、担当者を記載できるようになり、スクリーンショット一枚でエンジニアに完全な文脈を伝えられます。"
            ]
          }
        ],
        "workflow": [
          {
            "title": "対話を配列として保存",
            "description": "各発話を role と content で記録すれば、SheetMate が自動でチャットバブルを描画します。"
          },
          {
            "title": "スコアカードを横に並べて開く",
            "description": "JSON のメトリクスを右側のペインに読み込み、自動スコアと人間の判断を比較します。"
          },
          {
            "title": "気づきを Markdown で記録",
            "description": "再現手順、タグ、担当者を会話のすぐ下にまとめて記述します。"
          },
          {
            "title": "文脈ごとエンジニアに共有",
            "description": "分割ビューをそのままチケットに添付し、情報の取りこぼしをなくします。"
          }
        ],
        "highlights": [
          "チャットバブルがシステム・ユーザー・アシスタントの役割を自動で区別します。",
          "インラインの JSON 編集で手動のスコア調整が簡単になります。",
          "分割レイアウトが対話、メモ、メトリクスを一画面にまとめます。"
        ],
        "metrics": [
          {
            "value": "2×",
            "label": "回帰レビューの高速化",
            "context": "各テストスイートが90分から45分で完了するようになりました。"
          },
          {
            "value": "40%",
            "label": "再オープンされるバグの削減",
            "context": "完全な文脈により誤診された問題がほぼ半減しました。"
          },
          {
            "value": "0",
            "label": "追加管理するドキュメント",
            "context": "会話を別の Google Docs に複製する必要がなくなりました。"
          }
        ],
        "quote": {
          "text": "SheetMate は私たちの評価コントロールルームです。対話とスコアの文脈がずれることはもうありません。",
          "author": "Jason Li",
          "role": "LLM 評価責任者"
        }
      },
      id: {
        "industry": "AI / Evaluasi LLM",
        "title": "Evaluasi LLM: Selaraskan Dialog dan Skor Secara Bersamaan",
        "subtitle": "Satukan prompt, balasan model, dan scorecard dalam satu ruang kerja yang bisa dibagikan",
        "excerpt": "Tim evaluasi mengandalkan gelembung dialog dan panel JSON SheetMate untuk meninjau regresi lebih cepat dan memberi masukan yang lebih baik bagi tim engineering.",
        "keywords": [
          "SheetMate",
          "Evaluasi LLM",
          "Tinjauan percakapan",
          "Penilaian JSON",
          "Prompt engineering"
        ],
        "readingTime": "Baca 7 menit",
        "heroEyebrow": "Evaluasi AI",
        "metaTitle": "Studi Kasus LLM SheetMate｜Sinkronkan Dialog dan Scorecard",
        "metaDescription": "Pelajari bagaimana tim LLM memakai SheetMate untuk melihat prompt, respons, dan skor JSON secara bersamaan agar tinjauan regresi rampung lebih cepat.",
        "imagePlaceholder": {
          "caption": "Mockup: tampilan terbagi vertikal yang menampilkan gelembung chat dan JSON evaluasi berdampingan",
          "alt": "Layar terbagi SheetMate untuk evaluasi LLM"
        },
        "sections": [
          {
            "title": "Mengapa regresi berjalan lambat",
            "paragraphs": [
              "Tim evaluasi mengelola ratusan kasus uji berisi system prompt, input pengguna, dan balasan model di dalam Google Sheets.",
              "Mereka menyalin percakapan ke dokumen, menempelkan skor kembali ke sheet, dan kehilangan konteks setiap kali membandingkan versi model."
            ]
          },
          {
            "title": "Yang berubah dengan SheetMate",
            "paragraphs": [
              "Percakapan yang disimpan sebagai array role/content otomatis tampil sebagai gelembung chat, sehingga mudah memindai giliran system, user, dan assistant.",
              "Scorecard JSON terbuka di panel kedua, tempat reviewer bisa mengubah nilai atau menambahkan penyesuaian manual secara langsung.",
              "Tampilan terbagi vertikal menjaga konteks dialog di atas, sementara catatan Markdown menampung bobot, tag risiko, dan ID bug di bawah."
            ]
          },
          {
            "title": "Tinjauan secepat produksi",
            "paragraphs": [
              "SheetMate menyegarkan setiap beberapa ratus milidetik, sehingga hasil run terbaru muncul seketika tanpa memuat ulang workbook.",
              "Template Markdown standar kini memuat catatan risiko, langkah reproduksi, dan pemilik tugas, memberi engineering konteks lengkap hanya dari satu tangkapan layar."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Simpan dialog sebagai array",
            "description": "Catat setiap giliran dengan role dan content agar SheetMate menampilkan gelembung chat secara otomatis."
          },
          {
            "title": "Buka scorecard berdampingan",
            "description": "Muat metrik JSON di panel kedua untuk membandingkan skor otomatis dengan penilaian manusia."
          },
          {
            "title": "Catat temuan dalam Markdown",
            "description": "Dokumentasikan langkah reproduksi, tag, dan penanggung jawab tepat di bawah percakapan."
          },
          {
            "title": "Kirim konteks ke engineering",
            "description": "Ambil tampilan terbagi dan lampirkan ke tiket agar tidak ada yang hilang saat dialihkan."
          }
        ],
        "highlights": [
          "Gelembung chat memisahkan peran system, user, dan assistant secara otomatis.",
          "Pengeditan JSON inline menyederhanakan penyesuaian skor manual.",
          "Tata letak terbagi menjaga dialog, catatan, dan metrik dalam satu bingkai."
        ],
        "metrics": [
          {
            "value": "2×",
            "label": "Tinjauan regresi lebih cepat",
            "context": "Setiap test suite kini rampung dalam empat puluh lima menit, bukan sembilan puluh menit."
          },
          {
            "value": "40%",
            "label": "Bug yang dibuka ulang lebih sedikit",
            "context": "Konteks yang lengkap memangkas masalah salah diagnosis hampir setengahnya."
          },
          {
            "value": "0",
            "label": "Dokumen tambahan yang dikelola",
            "context": "Tim tidak lagi menyalin percakapan ke Google Docs terpisah."
          }
        ],
        "quote": {
          "text": "SheetMate adalah ruang kendali evaluasi kami—konteks dialog dan skor tidak pernah lagi terpisah.",
          "author": "Jason Li",
          "role": "Kepala Evaluasi LLM"
        }
      },
      es: {
        "industry": "IA / Evaluación de LLM",
        "title": "Evaluación de LLM: mantén diálogos y puntajes perfectamente sincronizados",
        "subtitle": "Reúne prompts, respuestas del modelo y scorecards en un solo espacio de trabajo compartible",
        "excerpt": "Los equipos de evaluación se apoyan en las burbujas de diálogo y los paneles JSON de SheetMate para revisar regresiones más rápido y darle mejor retroalimentación a ingeniería.",
        "keywords": [
          "SheetMate",
          "Evaluación de LLM",
          "Revisión de conversaciones",
          "Puntuación con JSON",
          "Ingeniería de prompts"
        ],
        "readingTime": "7 min de lectura",
        "heroEyebrow": "Evaluación de IA",
        "metaTitle": "Caso de uso de LLM con SheetMate｜Sincroniza diálogos y scorecards",
        "metaDescription": "Descubre cómo los equipos de LLM usan SheetMate para ver prompts, respuestas y puntajes JSON juntos y así acelerar las revisiones de regresión.",
        "imagePlaceholder": {
          "caption": "Mockup: vista dividida en vertical que muestra las burbujas de chat y el JSON de evaluación lado a lado",
          "alt": "Pantalla dividida de SheetMate para evaluación de LLM"
        },
        "sections": [
          {
            "title": "Por qué se atascaban las regresiones",
            "paragraphs": [
              "El equipo de evaluación mantenía cientos de casos de prueba con system prompts, entradas del usuario y respuestas del modelo dentro de Google Sheets.",
              "Copiaban las conversaciones en documentos, pegaban los puntajes de vuelta en la hoja y perdían el contexto cada vez que comparaban versiones del modelo."
            ]
          },
          {
            "title": "Lo que cambia con SheetMate",
            "paragraphs": [
              "Las conversaciones guardadas como arreglos de role/content se renderizan automáticamente como burbujas de chat, lo que facilita revisar los turnos de system, user y assistant.",
              "Las scorecards JSON se abren en el segundo panel, donde los revisores pueden ajustar valores o agregar correcciones manuales al instante.",
              "Una vista dividida en vertical conserva el contexto del diálogo arriba, mientras que las notas en Markdown capturan ponderaciones, etiquetas de riesgo e IDs de bugs abajo."
            ]
          },
          {
            "title": "Revisiones a velocidad de producción",
            "paragraphs": [
              "SheetMate se actualiza cada pocos cientos de milisegundos, así que las ejecuciones nuevas aparecen al instante sin recargar el libro.",
              "Las plantillas estándar de Markdown ahora incluyen alertas de riesgo, pasos de reproducción y responsables, dándole a ingeniería contexto completo desde una sola captura de pantalla."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Guarda los diálogos como arreglos",
            "description": "Registra cada turno con role y content para que SheetMate dibuje las burbujas de chat automáticamente."
          },
          {
            "title": "Abre las scorecards al lado",
            "description": "Carga las métricas JSON en el segundo panel para comparar los puntajes automáticos con el criterio humano."
          },
          {
            "title": "Registra los hallazgos en Markdown",
            "description": "Documenta los pasos de reproducción, las etiquetas y los responsables justo debajo de la conversación."
          },
          {
            "title": "Lleva el contexto a ingeniería",
            "description": "Toma la vista dividida y adjúntala a los tickets para que nada se pierda en el camino."
          }
        ],
        "highlights": [
          "Las burbujas de chat separan automáticamente los roles de system, user y assistant.",
          "La edición JSON en línea simplifica los ajustes manuales de puntaje.",
          "Los diseños divididos mantienen diálogos, notas y métricas en un solo marco."
        ],
        "metrics": [
          {
            "value": "2×",
            "label": "Revisiones de regresión más rápidas",
            "context": "Cada suite de pruebas ahora se entrega en cuarenta y cinco minutos en lugar de noventa."
          },
          {
            "value": "40%",
            "label": "Menos bugs reabiertos",
            "context": "El contexto completo reduce casi a la mitad los problemas mal diagnosticados."
          },
          {
            "value": "0",
            "label": "Documentos extra por mantener",
            "context": "El equipo ya no clona las conversaciones en documentos de Google Docs aparte."
          }
        ],
        "quote": {
          "text": "SheetMate es nuestra sala de control de evaluación: el contexto del diálogo y del puntaje ya nunca se separan.",
          "author": "Jason Li",
          "role": "Líder de Evaluación de LLM"
        }
      },
      ms: {
        "industry": "AI / Penilaian LLM",
        "title": "Penilaian LLM: Selaraskan Dialog dan Skor Secara Serentak",
        "subtitle": "Satukan prompt, respons model dan scorecard dalam satu ruang kerja yang boleh dikongsi",
        "excerpt": "Pasukan penilaian bergantung pada gelembung dialog dan panel JSON SheetMate untuk menyemak regresi dengan lebih pantas serta memberikan maklum balas yang lebih baik kepada kejuruteraan.",
        "keywords": [
          "SheetMate",
          "Penilaian LLM",
          "Semakan perbualan",
          "Pemarkahan JSON",
          "Kejuruteraan prompt"
        ],
        "readingTime": "Bacaan 7 minit",
        "heroEyebrow": "Penilaian AI",
        "metaTitle": "Kes Penggunaan LLM SheetMate｜Selaraskan Dialog dan Scorecard",
        "metaDescription": "Ketahui cara pasukan LLM menggunakan SheetMate untuk melihat prompt, respons dan skor JSON secara serentak supaya semakan regresi siap dengan lebih pantas.",
        "imagePlaceholder": {
          "caption": "Mockup: paparan terbahagi menegak yang menunjukkan gelembung sembang dan JSON penilaian bersebelahan",
          "alt": "Skrin terbahagi SheetMate untuk penilaian LLM"
        },
        "sections": [
          {
            "title": "Mengapa regresi tersekat",
            "paragraphs": [
              "Kumpulan penilaian menyelenggara beratus-ratus kes ujian dengan system prompt, input pengguna dan respons model di dalam Google Sheets.",
              "Mereka menyalin perbualan ke dalam dokumen, menampal semula skor ke dalam helaian, dan kehilangan konteks setiap kali membandingkan versi model."
            ]
          },
          {
            "title": "Apa yang berubah dengan SheetMate",
            "paragraphs": [
              "Perbualan yang disimpan sebagai tatasusunan role/content dipaparkan secara automatik sebagai gelembung sembang, memudahkan imbasan giliran system, user dan assistant.",
              "Scorecard JSON dibuka di panel kedua, tempat penyemak boleh mengubah nilai atau menambah penggantian manual dengan serta-merta.",
              "Paparan terbahagi menegak mengekalkan konteks dialog di bahagian atas, manakala nota Markdown menyimpan pemberat, tag risiko dan ID pepijat di bawah."
            ]
          },
          {
            "title": "Semakan pada kelajuan produksi",
            "paragraphs": [
              "SheetMate menyegar semula setiap beberapa ratus milisaat, jadi larian terkini muncul serta-merta tanpa memuat semula buku kerja.",
              "Templat Markdown standard kini memuatkan amaran risiko, langkah penghasilan semula dan pemilik tugas, memberi kejuruteraan konteks lengkap hanya daripada satu tangkap layar."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Simpan dialog sebagai tatasusunan",
            "description": "Log setiap giliran dengan role dan content supaya SheetMate memaparkan gelembung sembang secara automatik."
          },
          {
            "title": "Buka scorecard bersebelahan",
            "description": "Muatkan metrik JSON di panel kedua untuk membandingkan skor automatik dengan pertimbangan manusia."
          },
          {
            "title": "Rakam penemuan dalam Markdown",
            "description": "Dokumenkan langkah penghasilan semula, tag dan pemilik tugas tepat di bawah perbualan."
          },
          {
            "title": "Hantar konteks kepada kejuruteraan",
            "description": "Ambil paparan terbahagi dan lampirkan pada tiket supaya tiada apa-apa yang hilang sepanjang proses."
          }
        ],
        "highlights": [
          "Gelembung sembang memisahkan peranan system, user dan assistant secara automatik.",
          "Penyuntingan JSON sebaris memudahkan pelarasan skor secara manual.",
          "Susun atur terbahagi mengekalkan dialog, nota dan metrik dalam satu bingkai."
        ],
        "metrics": [
          {
            "value": "2×",
            "label": "Semakan regresi lebih pantas",
            "context": "Setiap suite ujian kini siap dalam empat puluh lima minit berbanding sembilan puluh minit."
          },
          {
            "value": "40%",
            "label": "Lebih sedikit pepijat dibuka semula",
            "context": "Konteks yang lengkap mengurangkan isu salah diagnosis hampir separuh."
          },
          {
            "value": "0",
            "label": "Dokumen tambahan yang diselenggara",
            "context": "Pasukan tidak lagi menyalin perbualan ke dalam Google Docs yang berasingan."
          }
        ],
        "quote": {
          "text": "SheetMate ialah bilik kawalan penilaian kami—konteks dialog dan skor tidak lagi terpisah.",
          "author": "Jason Li",
          "role": "Ketua Penilaian LLM"
        }
      }
    }
  },
  {
    slug: 'customer-support-quality-review',
    locales: {
      zh: {
        industry: '客户成功 / 支持运营',
        title: '客户成功质检：让对话记录与工单上下文对齐',
        subtitle: '在 SheetMate 中同时查看聊天记录、工单 JSON 与知识库链接',
        excerpt: '客户成功团队借助 SheetMate 完成质检、复盘与培训材料同步，缩短反馈闭环时间。',
        keywords: ['SheetMate', '客户成功', '质检', '对话回顾', '知识库'],
        readingTime: '6 分钟',
        heroEyebrow: 'Customer Success',
        metaTitle: 'SheetMate 客户成功 Use Case｜对话质检与知识库分屏',
        metaDescription: '了解客户成功团队如何运用 SheetMate 的 Markdown、JSON 与 iframe 能力，在 Google Sheets 中完成质检闭环。',
        imagePlaceholder: {
          caption: '示意图：对话 Markdown 与知识库 iframe 分屏',
          alt: 'SheetMate 客户成功视图占位图'
        },
        sections: [
          {
            title: '质检团队面临的挑战',
            paragraphs: [
              '一家 SaaS 企业的客户成功团队每周要复盘 200+ 条对话记录，包含聊天机器人与人工客服。',
              '以往需要把聊天日志导出成 CSV，再复制到 Docs 中做批注，耗时且脱离数据源。'
            ]
          },
          {
            title: 'SheetMate 让对话质检回到表格',
            paragraphs: [
              '团队把聊天记录拆分为 Markdown 段落，SheetMate 渲染后保留格式与表情符号，方便阅读。',
              '当遇到复杂的工单 JSON 结构时，可直接切换到 JSON 视图查看嵌套字段，并记录需要客服二次核对的字段。',
              '利用水平分屏，左侧查看对话，右侧展示质量评分表或知识库链接。'
            ]
          },
          {
            title: '更快的质检闭环',
            paragraphs: [
              '质检专员在 Markdown 中实时勾选待改进项，截图附带单元格定位，客服能立刻回到原始工单。',
              '团队将周度培训的 PPT 链接直接粘贴在单元格，SheetMate 的 iframe 让专员无需打开新标签即可回顾最新流程。'
            ]
          }
        ],
        workflow: [
          {
            title: '同步聊天与工单',
            description: '通过 API 把聊天记录和工单 JSON 同步到 Sheet，不需要手动导入。'
          },
          {
            title: '阅读上下文',
            description: '单栏模式下浏览 Markdown 渲染后的对话，确保语气和格式完整。'
          },
          {
            title: '切换评分模版',
            description: '第二视图打开评分表或 JSON 详情，对照核查。'
          },
          {
            title: '提交反馈',
            description: '把质检结论直接写在 Markdown 清单中，发送给客服主管。'
          }
        ],
        highlights: [
          'Markdown 支持表情符与引用，让聊天语境保留。',
          'JSON 视图帮助定位工单字段的真实值。',
          'iframe 预览支持嵌入内部知识库或培训幻灯片。'
        ],
        metrics: [
          {
            value: '50%',
            label: '质检回合时间',
            context: '一次质检迭代缩短至原来的二分之一。'
          },
          {
            value: '3',
            label: '培训材料嵌入',
            context: '常用知识库、SOP 与案例集全部内嵌在分屏中。'
          },
          {
            value: '95%',
            label: '整改闭环率',
            context: '带定位的截图让客服易于回复并关闭任务。'
          }
        ],
        quote: {
          text: '我们把质检待办直接写在 SheetMate 的 Markdown 清单中，客服收到截图就能快速定位问题。',
          author: 'Sophia Wang',
          role: '客户成功运营经理'
        }
      },
      en: {
        industry: 'Customer Success / Support Ops',
        title: 'Customer Success QA: Keep Chats and Tickets Aligned',
        subtitle: 'Review transcripts, ticket JSON, and knowledge base links without leaving Google Sheets',
        excerpt: 'Support teams use SheetMate to close QA loops faster by keeping conversations, scorecards, and training content in one view.',
        keywords: ['SheetMate', 'Customer success', 'Quality assurance', 'Conversation review', 'Knowledge base'],
        readingTime: '6 min read',
        heroEyebrow: 'Customer Success',
        metaTitle: 'SheetMate Support Use Case｜Chat QA with Embedded Knowledge Base',
        metaDescription: 'Learn how support teams combine Markdown transcripts, ticket JSON, and knowledge base links inside SheetMate to streamline QA.',
        imagePlaceholder: {
          caption: 'Mockup: split view showing chat Markdown next to a knowledge base article',
          alt: 'SheetMate split view for support QA'
        },
        sections: [
          {
            title: 'Why QA was slow',
            paragraphs: [
              'A SaaS success team reviews more than two hundred bot and human conversations each week.',
              'They exported logs as CSV, annotated them in Docs, and lost the trail back to the original ticket data.'
            ]
          },
          {
            title: 'QA moves back into the sheet',
            paragraphs: [
              'Chats are stored as Markdown paragraphs so tone, emojis, and formatting stay intact. SheetMate renders the conversation for quick scanning.',
              'Ticket payloads open in JSON view, making it easy to pinpoint the nested fields support reps need to verify.',
              'A split view keeps the dialog on the left and the scorecard or knowledge article on the right, so reviewers don’t bounce between tabs.'
            ]
          },
          {
            title: 'Faster closure and better coaching',
            paragraphs: [
              'Leads mark action items in Markdown checklists. Screenshots include the cell reference so reps can jump straight to the right ticket.',
              'Training decks and SOP updates live as iframe links, letting reviewers refresh guidance without leaving the sheet.'
            ]
          }
        ],
        workflow: [
          {
            title: 'Sync chats and tickets',
            description: 'Pull transcripts and ticket JSON via API so all context sits in one sheet.'
          },
          {
            title: 'Review the conversation',
            description: 'Read the rendered Markdown to check tone, empathy, and policy adherence.'
          },
          {
            title: 'Open scorecards side by side',
            description: 'Load the QA rubric or ticket payload in the second pane for line-by-line comparisons.'
          },
          {
            title: 'Deliver actionable feedback',
            description: 'Record takeaways in Markdown and share annotated screenshots with team leads.'
          }
        ],
        highlights: [
          'Markdown keeps emoji and formatting intact for realistic chat reviews.',
          'JSON view surfaces the exact ticket fields that need attention.',
          'Iframe embeds bring SOPs and knowledge bases directly into the split view.'
        ],
        metrics: [
          {
            value: '50%',
            label: 'QA cycle time',
            context: 'Reviews now finish in half the time because context stays put.'
          },
          {
            value: '3',
            label: 'Embedded resources',
            context: 'Scorecards, SOPs, and best-practice examples sit inside SheetMate.'
          },
          {
            value: '95%',
            label: 'Action closure rate',
            context: 'Tagged screenshots help agents resolve follow-ups without back-and-forth.'
          }
        ],
        quote: {
          text: 'Logging QA to-dos inside SheetMate means reps tap the screenshot and land on the exact row they need to fix.',
          author: 'Sophia Wang',
          role: 'Customer Success Operations Manager'
        }
      },
      ja: {
        "industry": "カスタマーサクセス / サポート運用",
        "title": "カスタマーサクセスQA：チャットとチケットを常に一致させる",
        "subtitle": "会話ログ、チケットJSON、ナレッジベースのリンクをGoogle Sheetsから離れずに確認",
        "excerpt": "サポートチームはSheetMateを使い、会話・評価シート・トレーニング資料を1つのビューにまとめることで、QAのループをより速く完結させています。",
        "keywords": [
          "SheetMate",
          "カスタマーサクセス",
          "品質管理 QA",
          "会話レビュー",
          "ナレッジベース"
        ],
        "readingTime": "6分で読了",
        "heroEyebrow": "カスタマーサクセス",
        "metaTitle": "SheetMateサポート活用事例｜ナレッジベースを埋め込んだチャットQA",
        "metaDescription": "サポートチームがSheetMate上でMarkdownの会話ログ、チケットJSON、ナレッジベースのリンクを組み合わせ、QAを効率化する方法を紹介します。",
        "imagePlaceholder": {
          "caption": "モックアップ：チャットのMarkdownとナレッジベース記事を並べた分割ビュー",
          "alt": "サポートQAのためのSheetMate分割ビュー"
        },
        "sections": [
          {
            "title": "なぜQAは遅かったのか",
            "paragraphs": [
              "あるSaaS企業のサクセスチームは、毎週200件を超えるボットと有人の会話をレビューしています。",
              "ログをCSVで書き出し、Docsで注釈を付けていたため、元のチケットデータまでの手がかりを見失っていました。"
            ]
          },
          {
            "title": "QAをシートの中に取り戻す",
            "paragraphs": [
              "チャットはMarkdownの段落として保存されるため、トーンや絵文字、書式がそのまま保たれます。SheetMateが会話をレンダリングし、ひと目で確認できます。",
              "チケットのペイロードはJSONビューで開くので、サポート担当者が確認すべきネストされたフィールドをすぐに特定できます。",
              "分割ビューでは左に対話、右に評価シートやナレッジ記事を表示できるため、レビュアーはタブを行き来する必要がありません。"
            ]
          },
          {
            "title": "より速いクローズと、より良いコーチング",
            "paragraphs": [
              "リーダーはアクション項目をMarkdownのチェックリストに書き込みます。スクリーンショットにはセル参照が含まれるため、担当者は該当チケットへすぐに移動できます。",
              "トレーニング資料やSOPの更新はiframeリンクとして配置され、レビュアーはシートを離れずにガイダンスを更新できます。"
            ]
          }
        ],
        "workflow": [
          {
            "title": "チャットとチケットを同期",
            "description": "APIで会話ログとチケットJSONを取り込み、すべての文脈を1つのシートにまとめます。"
          },
          {
            "title": "会話をレビュー",
            "description": "レンダリングされたMarkdownを読み、トーン・共感・ポリシー順守を確認します。"
          },
          {
            "title": "評価シートを並べて開く",
            "description": "2つ目のペインにQAルーブリックやチケットのペイロードを読み込み、1行ずつ比較します。"
          },
          {
            "title": "実行可能なフィードバックを届ける",
            "description": "気づきをMarkdownに記録し、注釈付きのスクリーンショットをチームリーダーと共有します。"
          }
        ],
        "highlights": [
          "Markdownにより絵文字や書式が保たれ、現実に即したチャットレビューができます。",
          "JSONビューで、注目すべきチケットのフィールドが正確に浮かび上がります。",
          "iframe埋め込みにより、SOPやナレッジベースを分割ビューに直接取り込めます。"
        ],
        "metrics": [
          {
            "value": "50%",
            "label": "QAサイクル時間",
            "context": "文脈がその場に残るため、レビューは半分の時間で完了します。"
          },
          {
            "value": "3",
            "label": "埋め込みリソース",
            "context": "評価シート、SOP、ベストプラクティス事例がSheetMateの中にまとまっています。"
          },
          {
            "value": "95%",
            "label": "アクション完了率",
            "context": "タグ付けされたスクリーンショットにより、担当者はやり取りなしでフォローアップを解決できます。"
          }
        ],
        "quote": {
          "text": "QAのToDoをSheetMateに記録しておけば、担当者はスクリーンショットをタップするだけで修正すべき行に正確にたどり着けます。",
          "author": "Sophia Wang",
          "role": "カスタマーサクセス運用マネージャー"
        }
      },
      id: {
        "industry": "Customer Success / Operasi Dukungan",
        "title": "QA Customer Success: Selaraskan Chat dan Tiket Tanpa Repot",
        "subtitle": "Tinjau transkrip, JSON tiket, dan tautan knowledge base tanpa keluar dari Google Sheets",
        "excerpt": "Tim dukungan memakai SheetMate untuk menutup siklus QA lebih cepat dengan menyatukan percakapan, scorecard, dan materi pelatihan dalam satu tampilan.",
        "keywords": [
          "SheetMate",
          "Customer success",
          "Quality assurance",
          "Tinjauan percakapan",
          "Knowledge base"
        ],
        "readingTime": "Baca 6 menit",
        "heroEyebrow": "Customer Success",
        "metaTitle": "Studi Kasus Dukungan SheetMate｜QA Chat dengan Knowledge Base Tertanam",
        "metaDescription": "Pelajari bagaimana tim dukungan menggabungkan transkrip Markdown, JSON tiket, dan tautan knowledge base di dalam SheetMate untuk mempermudah QA.",
        "imagePlaceholder": {
          "caption": "Mockup: tampilan terbagi yang menampilkan Markdown chat di samping artikel knowledge base",
          "alt": "Tampilan terbagi SheetMate untuk QA dukungan"
        },
        "sections": [
          {
            "title": "Mengapa QA berjalan lambat",
            "paragraphs": [
              "Sebuah tim success SaaS meninjau lebih dari dua ratus percakapan bot dan manusia setiap minggu.",
              "Mereka mengekspor log sebagai CSV, memberi anotasi di Docs, lalu kehilangan jejak kembali ke data tiket aslinya."
            ]
          },
          {
            "title": "QA kembali ke dalam sheet",
            "paragraphs": [
              "Chat disimpan sebagai paragraf Markdown sehingga nada, emoji, dan format tetap utuh. SheetMate menampilkan percakapan agar mudah dibaca sekilas.",
              "Payload tiket dibuka dalam tampilan JSON, sehingga mudah menemukan field bertingkat yang perlu diverifikasi oleh rep dukungan.",
              "Tampilan terbagi menjaga dialog di kiri dan scorecard atau artikel pengetahuan di kanan, sehingga peninjau tidak perlu berpindah-pindah tab."
            ]
          },
          {
            "title": "Penyelesaian lebih cepat dan coaching lebih baik",
            "paragraphs": [
              "Lead menandai action item dalam checklist Markdown. Tangkapan layar menyertakan referensi sel sehingga rep bisa langsung melompat ke tiket yang tepat.",
              "Materi pelatihan dan pembaruan SOP tersedia sebagai tautan iframe, memungkinkan peninjau menyegarkan panduan tanpa keluar dari sheet."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Sinkronkan chat dan tiket",
            "description": "Tarik transkrip dan JSON tiket melalui API agar semua konteks berada dalam satu sheet."
          },
          {
            "title": "Tinjau percakapan",
            "description": "Baca Markdown yang sudah dirender untuk memeriksa nada, empati, dan kepatuhan kebijakan."
          },
          {
            "title": "Buka scorecard berdampingan",
            "description": "Muat rubrik QA atau payload tiket di panel kedua untuk perbandingan baris demi baris."
          },
          {
            "title": "Berikan umpan balik yang actionable",
            "description": "Catat poin penting dalam Markdown dan bagikan tangkapan layar beranotasi kepada team lead."
          }
        ],
        "highlights": [
          "Markdown menjaga emoji dan format tetap utuh untuk tinjauan chat yang realistis.",
          "Tampilan JSON memunculkan field tiket persis yang perlu diperhatikan.",
          "Sematan iframe membawa SOP dan knowledge base langsung ke dalam tampilan terbagi."
        ],
        "metrics": [
          {
            "value": "50%",
            "label": "Waktu siklus QA",
            "context": "Tinjauan kini selesai dalam separuh waktu karena konteks tetap di tempatnya."
          },
          {
            "value": "3",
            "label": "Sumber daya tertanam",
            "context": "Scorecard, SOP, dan contoh praktik terbaik berada di dalam SheetMate."
          },
          {
            "value": "95%",
            "label": "Tingkat penyelesaian action",
            "context": "Tangkapan layar bertanda membantu agen menyelesaikan tindak lanjut tanpa bolak-balik."
          }
        ],
        "quote": {
          "text": "Mencatat to-do QA di dalam SheetMate berarti rep cukup menekan tangkapan layar dan langsung mendarat di baris persis yang perlu diperbaiki.",
          "author": "Sophia Wang",
          "role": "Manajer Operasi Customer Success"
        }
      },
      es: {
        "industry": "Customer Success / Operaciones de Soporte",
        "title": "QA de Customer Success: mantén alineados chats y tickets",
        "subtitle": "Revisa transcripciones, el JSON de tickets y enlaces a la base de conocimiento sin salir de Google Sheets",
        "excerpt": "Los equipos de soporte usan SheetMate para cerrar los ciclos de QA más rápido al tener las conversaciones, los scorecards y el material de capacitación en una sola vista.",
        "keywords": [
          "SheetMate",
          "Customer success",
          "Aseguramiento de calidad",
          "Revisión de conversaciones",
          "Base de conocimiento"
        ],
        "readingTime": "6 min de lectura",
        "heroEyebrow": "Customer Success",
        "metaTitle": "Caso de uso de soporte de SheetMate｜QA de chats con base de conocimiento integrada",
        "metaDescription": "Descubre cómo los equipos de soporte combinan transcripciones en Markdown, el JSON de tickets y enlaces a la base de conocimiento dentro de SheetMate para agilizar el QA.",
        "imagePlaceholder": {
          "caption": "Mockup: vista dividida que muestra el Markdown del chat junto a un artículo de la base de conocimiento",
          "alt": "Vista dividida de SheetMate para QA de soporte"
        },
        "sections": [
          {
            "title": "Por qué el QA era lento",
            "paragraphs": [
              "Un equipo de success de una empresa SaaS revisa más de doscientas conversaciones de bots y humanos cada semana.",
              "Exportaban los registros como CSV, los anotaban en Docs y perdían el rastro hacia los datos originales del ticket."
            ]
          },
          {
            "title": "El QA regresa a la hoja",
            "paragraphs": [
              "Los chats se guardan como párrafos en Markdown, así el tono, los emojis y el formato se mantienen intactos. SheetMate renderiza la conversación para revisarla de un vistazo.",
              "Los payloads de los tickets se abren en vista JSON, lo que facilita ubicar los campos anidados que los agentes de soporte necesitan verificar.",
              "Una vista dividida mantiene el diálogo a la izquierda y el scorecard o el artículo de conocimiento a la derecha, para que los revisores no salten entre pestañas."
            ]
          },
          {
            "title": "Cierre más rápido y mejor coaching",
            "paragraphs": [
              "Los líderes marcan las tareas pendientes en checklists de Markdown. Las capturas incluyen la referencia de celda para que los agentes lleguen directo al ticket correcto.",
              "Los materiales de capacitación y las actualizaciones de SOP viven como enlaces en iframe, lo que permite a los revisores repasar las guías sin salir de la hoja."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Sincroniza chats y tickets",
            "description": "Trae las transcripciones y el JSON de tickets vía API para que todo el contexto quede en una sola hoja."
          },
          {
            "title": "Revisa la conversación",
            "description": "Lee el Markdown renderizado para revisar el tono, la empatía y el cumplimiento de las políticas."
          },
          {
            "title": "Abre los scorecards lado a lado",
            "description": "Carga la rúbrica de QA o el payload del ticket en el segundo panel para comparaciones línea por línea."
          },
          {
            "title": "Entrega retroalimentación accionable",
            "description": "Registra los aprendizajes en Markdown y comparte capturas anotadas con los líderes del equipo."
          }
        ],
        "highlights": [
          "Markdown mantiene intactos los emojis y el formato para revisiones de chat realistas.",
          "La vista JSON destaca los campos exactos del ticket que requieren atención.",
          "Los embebidos en iframe traen los SOP y las bases de conocimiento directo a la vista dividida."
        ],
        "metrics": [
          {
            "value": "50%",
            "label": "Tiempo del ciclo de QA",
            "context": "Las revisiones ahora terminan en la mitad del tiempo porque el contexto se queda en su lugar."
          },
          {
            "value": "3",
            "label": "Recursos integrados",
            "context": "Scorecards, SOP y ejemplos de buenas prácticas están dentro de SheetMate."
          },
          {
            "value": "95%",
            "label": "Tasa de cierre de acciones",
            "context": "Las capturas etiquetadas ayudan a los agentes a resolver los seguimientos sin idas y vueltas."
          }
        ],
        "quote": {
          "text": "Registrar los pendientes de QA dentro de SheetMate significa que el agente toca la captura y aterriza justo en la fila que necesita corregir.",
          "author": "Sophia Wang",
          "role": "Gerente de Operaciones de Customer Success"
        }
      },
      ms: {
        "industry": "Customer Success / Operasi Sokongan",
        "title": "QA Customer Success: Pastikan Sembang dan Tiket Sentiasa Selari",
        "subtitle": "Semak transkrip, JSON tiket dan pautan pangkalan pengetahuan tanpa keluar dari Google Sheets",
        "excerpt": "Pasukan sokongan menggunakan SheetMate untuk menutup kitaran QA dengan lebih pantas dengan menyatukan perbualan, kad skor dan bahan latihan dalam satu paparan.",
        "keywords": [
          "SheetMate",
          "Customer success",
          "Jaminan kualiti",
          "Semakan perbualan",
          "Pangkalan pengetahuan"
        ],
        "readingTime": "Bacaan 6 minit",
        "heroEyebrow": "Customer Success",
        "metaTitle": "Kes Penggunaan Sokongan SheetMate｜QA Sembang dengan Pangkalan Pengetahuan Terbenam",
        "metaDescription": "Ketahui cara pasukan sokongan menggabungkan transkrip Markdown, JSON tiket dan pautan pangkalan pengetahuan dalam SheetMate untuk mempermudah QA.",
        "imagePlaceholder": {
          "caption": "Mockup: paparan terbahagi yang menunjukkan Markdown sembang bersebelahan artikel pangkalan pengetahuan",
          "alt": "Paparan terbahagi SheetMate untuk QA sokongan"
        },
        "sections": [
          {
            "title": "Mengapa QA berjalan perlahan",
            "paragraphs": [
              "Sebuah pasukan success SaaS menyemak lebih daripada dua ratus perbualan bot dan manusia setiap minggu.",
              "Mereka mengeksport log sebagai CSV, membuat anotasi dalam Docs, lalu kehilangan jejak kembali ke data tiket asal."
            ]
          },
          {
            "title": "QA kembali ke dalam helaian",
            "paragraphs": [
              "Sembang disimpan sebagai perenggan Markdown supaya nada, emoji dan format kekal utuh. SheetMate memaparkan perbualan untuk imbasan pantas.",
              "Muatan tiket dibuka dalam paparan JSON, memudahkan untuk mengenal pasti medan bersarang yang perlu disahkan oleh wakil sokongan.",
              "Paparan terbahagi mengekalkan dialog di sebelah kiri dan kad skor atau artikel pengetahuan di sebelah kanan, supaya penyemak tidak perlu berulang-alik antara tab."
            ]
          },
          {
            "title": "Penutupan lebih pantas dan bimbingan lebih baik",
            "paragraphs": [
              "Ketua menanda item tindakan dalam senarai semak Markdown. Tangkapan skrin menyertakan rujukan sel supaya wakil boleh terus melompat ke tiket yang betul.",
              "Bahan latihan dan kemas kini SOP wujud sebagai pautan iframe, membolehkan penyemak menyegarkan panduan tanpa keluar dari helaian."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Segerakkan sembang dan tiket",
            "description": "Tarik transkrip dan JSON tiket melalui API supaya semua konteks berada dalam satu helaian."
          },
          {
            "title": "Semak perbualan",
            "description": "Baca Markdown yang dipaparkan untuk menyemak nada, empati dan pematuhan polisi."
          },
          {
            "title": "Buka kad skor bersebelahan",
            "description": "Muatkan rubrik QA atau muatan tiket dalam anak tetingkap kedua untuk perbandingan baris demi baris."
          },
          {
            "title": "Sampaikan maklum balas yang boleh dilaksanakan",
            "description": "Catat pengajaran dalam Markdown dan kongsi tangkapan skrin beranotasi dengan ketua pasukan."
          }
        ],
        "highlights": [
          "Markdown mengekalkan emoji dan format supaya semakan sembang lebih realistik.",
          "Paparan JSON menonjolkan medan tiket tepat yang memerlukan perhatian.",
          "Benaman iframe membawa SOP dan pangkalan pengetahuan terus ke dalam paparan terbahagi."
        ],
        "metrics": [
          {
            "value": "50%",
            "label": "Masa kitaran QA",
            "context": "Semakan kini selesai dalam separuh masa kerana konteks kekal di tempatnya."
          },
          {
            "value": "3",
            "label": "Sumber terbenam",
            "context": "Kad skor, SOP dan contoh amalan terbaik berada di dalam SheetMate."
          },
          {
            "value": "95%",
            "label": "Kadar penutupan tindakan",
            "context": "Tangkapan skrin bertanda membantu ejen menyelesaikan susulan tanpa berulang-alik."
          }
        ],
        "quote": {
          "text": "Mencatat senarai tugas QA dalam SheetMate bermakna wakil hanya perlu menekan tangkapan skrin dan terus tiba di baris tepat yang perlu dibetulkan.",
          "author": "Sophia Wang",
          "role": "Pengurus Operasi Customer Success"
        }
      }
    }
  },
  {
    slug: 'product-growth-ab-testing',
    locales: {
      zh: {
        industry: '产品增长 / 数据分析',
        title: '产品增长：把实验假设与实时指标放在同一屏',
        subtitle: '利用 Markdown 模板与 iframe 预览，快速完成 A/B 实验复盘',
        excerpt: '增长团队通过 SheetMate 将实验配置、实时仪表盘和行动项整合在 Google Sheets 侧边栏。',
        keywords: ['SheetMate', 'A/B 测试', '增长分析', '实验管理', 'Google Sheets'],
        readingTime: '6 分钟',
        heroEyebrow: 'Growth Insights',
        metaTitle: 'SheetMate 增长 Use Case｜实验配置与指标一体化',
        metaDescription: '了解增长团队如何使用 SheetMate 的 Markdown、JSON 与 iframe 分屏，在会议上即时展示实验数据与行动项。',
        imagePlaceholder: {
          caption: '示意图：实验 Markdown 与仪表盘 iframe 水平分屏',
          alt: 'SheetMate 增长分析视图占位图'
        },
        sections: [
          {
            title: 'A/B 实验的数据碎片',
            paragraphs: [
              '增长团队同时运行多个 A/B 实验，需要对照实验配置、实时指标与分析备注。',
              '传统方式要在 Data Studio、Sheets 与 Slack 之间跳转，难以维持统一视图。'
            ]
          },
          {
            title: 'SheetMate 保持实验上下文整合',
            paragraphs: [
              '团队将实验配置写成 Markdown，包含目标、假设与分群，SheetMate 渲染后便于阅读。',
              '把 BI 仪表盘链接粘贴进单元格，iframe 视图让分析师在同一屏查看实时数据。',
              '分屏模式用于比较实验组与对照组的 JSON 指标列表，快速识别显著差异。'
            ]
          },
          {
            title: '更快的实验决策',
            paragraphs: [
              '分析师通过字体调节在会议上放大关键指标，截图即是会议材料。',
              '团队把待跟进事项写成 Markdown 任务列表，每次更新后 SheetMate 自动反映最新状态。'
            ]
          }
        ],
        workflow: [
          {
            title: '沉淀实验模板',
            description: '使用 Markdown 模板记录目标、假设、指标与下一步计划。'
          },
          {
            title: '粘贴数据链接',
            description: '把实时仪表盘或 SQL 可视化链接放入单元格，SheetMate 自动生成预览。'
          },
          {
            title: '比较实验数据',
            description: '通过水平分屏对照实验组与对照组的指标 JSON。'
          },
          {
            title: '同步行动项',
            description: '在 Markdown 任务列表中分配责任人，截图或共享给团队。'
          }
        ],
        highlights: [
          'Markdown 模板让实验信息结构化。',
          'iframe 预览将 BI 仪表盘与表格融合。',
          '分屏对比 JSON 指标，减少导出需求。'
        ],
        metrics: [
          {
            value: '7 天',
            label: '实验结论周期',
            context: '平均缩短 30% 的决策时间。'
          },
          {
            value: '100%',
            label: '会议材料复用率',
            context: '截图直接作为周会附件，无需额外 deck。'
          },
          {
            value: '0',
            label: '手动数据复制',
            context: '所有指标直接从数据源读取。'
          }
        ],
        quote: {
          text: 'SheetMate 帮我们在评审会议上即时展示指标与假设，再也不需要来回翻 PowerPoint。',
          author: 'Leo Gu',
          role: '增长分析主管'
        }
      },
      en: {
        industry: 'Product Growth / Analytics',
        title: 'Product Growth: Keep Experiments and Metrics in One View',
        subtitle: 'Use Markdown briefs and embedded dashboards to speed up experiment readouts',
        excerpt: 'Growth analysts rely on SheetMate to pair experiment context with live metrics so decisions happen faster and action items never slip.',
        keywords: ['SheetMate', 'A/B testing', 'Growth analytics', 'Experiment management', 'Google Sheets'],
        readingTime: '6 min read',
        heroEyebrow: 'Growth Insights',
        metaTitle: 'SheetMate Growth Use Case｜Unify Experiment Hypotheses and Metrics',
        metaDescription: 'See how growth teams combine Markdown briefs, JSON metrics, and live dashboards inside SheetMate to accelerate decisions.',
        imagePlaceholder: {
          caption: 'Mockup: horizontal split showing an experiment brief next to a dashboard iframe',
          alt: 'SheetMate split view for growth experiments'
        },
        sections: [
          {
            title: 'Too many tabs for one readout',
            paragraphs: [
              'Growth managers run multiple tests at once and need hypotheses, live metrics, and analyst notes in sync.',
              'They bounced between Data Studio, Sheets, and Slack threads, making it tough to tell a clean story in meetings.'
            ]
          },
          {
            title: 'SheetMate holds the whole narrative',
            paragraphs: [
              'Experiment briefs live as Markdown with goals, target segments, and guardrails. SheetMate renders them cleanly for quick scanning.',
              'Analysts paste BI dashboard links directly into cells so the iframe preview streams real-time results next to the brief.',
              'Split view compares JSON metric lists for control and treatment, surfacing meaningful lifts instantly.'
            ]
          },
          {
            title: 'Decisions land in the same meeting',
            paragraphs: [
              'Presentation mode bumps typography for the room, and screenshots double as recap slides minutes after the call.',
              'Action items are logged in Markdown tasks so owners and due dates travel with the data.'
            ]
          }
        ],
        workflow: [
          {
            title: 'Document the experiment brief',
            description: 'Capture goals, hypotheses, and north-star metrics in Markdown within the sheet.'
          },
          {
            title: 'Embed live dashboards',
            description: 'Paste charts or SQL visuals into cells so SheetMate renders the data inline.'
          },
          {
            title: 'Compare control vs. treatment',
            description: 'Use split view to evaluate JSON metrics for each cohort without exporting data.'
          },
          {
            title: 'Log next steps',
            description: 'Assign follow-ups via Markdown task lists and share the SheetMate view with stakeholders.'
          }
        ],
        highlights: [
          'Markdown templates keep experiment context organized.',
          'Iframe previews bring BI dashboards into the same canvas.',
          'Split comparisons remove the need for manual CSV exports.'
        ],
        metrics: [
          {
            value: '7 days',
            label: 'Decision cycle',
            context: 'Average experiment decisions ship 30% sooner.'
          },
          {
            value: '100%',
            label: 'Deck reuse',
            context: 'Screenshots from SheetMate now replace custom slide decks.'
          },
          {
            value: '0',
            label: 'Manual copying',
            context: 'All metrics flow straight from their source systems.'
          }
        ],
        quote: {
          text: 'SheetMate lets us show the hypothesis and the lift in one glance—experiment reviews finally stay in the room where they start.',
          author: 'Leo Gu',
          role: 'Lead Growth Analyst'
        }
      },
      ja: {
        "industry": "プロダクトグロース / アナリティクス",
        "title": "プロダクトグロース：実験と指標を1画面に集約",
        "subtitle": "Markdownのブリーフと埋め込みダッシュボードで実験のレポーティングを高速化",
        "excerpt": "グロースアナリストはSheetMateで実験のコンテキストとライブ指標を並べ、意思決定を加速させ、アクションアイテムの抜け漏れをなくしています。",
        "keywords": [
          "SheetMate",
          "A/Bテスト",
          "グロース分析",
          "実験管理",
          "Google Sheets"
        ],
        "readingTime": "6分で読了",
        "heroEyebrow": "グロースインサイト",
        "metaTitle": "SheetMate グロース活用事例｜実験の仮説と指標を一元化",
        "metaDescription": "グロースチームがSheetMate上でMarkdownのブリーフ、JSON指標、ライブダッシュボードを組み合わせ、意思決定を加速させる方法をご紹介します。",
        "imagePlaceholder": {
          "caption": "モックアップ：実験ブリーフとダッシュボードのiframeを左右に並べた分割表示",
          "alt": "グロース実験向けSheetMateの分割ビュー"
        },
        "sections": [
          {
            "title": "1つのレポートにタブが多すぎる",
            "paragraphs": [
              "グロースマネージャーは複数のテストを同時に走らせており、仮説・ライブ指標・アナリストのメモを常に同期させる必要があります。",
              "Data Studio、Sheets、Slackのスレッドを行き来していたため、会議でわかりやすいストーリーを語るのは至難の業でした。"
            ]
          },
          {
            "title": "SheetMateがストーリー全体を保持",
            "paragraphs": [
              "実験ブリーフは目標・ターゲットセグメント・ガードレールを含むMarkdownとして管理され、SheetMateがそれをすっきりと描画してひと目で把握できます。",
              "アナリストはBIダッシュボードのリンクをそのままセルに貼り付けるだけで、iframeプレビューがブリーフの隣でリアルタイムの結果を表示します。",
              "分割表示でコントロール群と処置群のJSON指標リストを比較し、意味のあるリフトを瞬時に浮かび上がらせます。"
            ]
          },
          {
            "title": "意思決定はその場の会議で完結",
            "paragraphs": [
              "プレゼンモードで文字を大きくして会議室向けに最適化でき、スクリーンショットは通話の数分後にはそのまま振り返り用スライドとして使えます。",
              "アクションアイテムはMarkdownのタスクとして記録され、担当者と期限がデータとともに持ち運ばれます。"
            ]
          }
        ],
        "workflow": [
          {
            "title": "実験ブリーフを記述する",
            "description": "目標・仮説・ノーススター指標をシート内のMarkdownにまとめます。"
          },
          {
            "title": "ライブダッシュボードを埋め込む",
            "description": "グラフやSQLのビジュアルをセルに貼り付け、SheetMateがデータをインラインで描画します。"
          },
          {
            "title": "コントロール群と処置群を比較する",
            "description": "分割表示を使い、データを書き出すことなく各コホートのJSON指標を評価します。"
          },
          {
            "title": "次のステップを記録する",
            "description": "Markdownのタスクリストでフォローアップを割り当て、SheetMateのビューを関係者と共有します。"
          }
        ],
        "highlights": [
          "Markdownテンプレートで実験のコンテキストを整理しておけます。",
          "iframeプレビューでBIダッシュボードを同じキャンバスに取り込めます。",
          "分割比較により手作業のCSVエクスポートが不要になります。"
        ],
        "metrics": [
          {
            "value": "7 days",
            "label": "意思決定サイクル",
            "context": "実験の意思決定が平均30%早くなりました。"
          },
          {
            "value": "100%",
            "label": "資料の再利用",
            "context": "SheetMateのスクリーンショットがカスタムスライド資料の代わりになりました。"
          },
          {
            "value": "0",
            "label": "手作業のコピー",
            "context": "すべての指標がソースシステムから直接流れ込みます。"
          }
        ],
        "quote": {
          "text": "SheetMateなら仮説とリフトをひと目で見せられます。実験レビューがようやく、始まったその場で完結するようになりました。",
          "author": "Leo Gu",
          "role": "グロース分析リード"
        }
      },
      id: {
        "industry": "Pertumbuhan Produk / Analitik",
        "title": "Pertumbuhan Produk: Satukan Eksperimen dan Metrik dalam Satu Tampilan",
        "subtitle": "Gunakan brief Markdown dan dashboard tertanam untuk mempercepat pembacaan hasil eksperimen",
        "excerpt": "Analis pertumbuhan mengandalkan SheetMate untuk menyandingkan konteks eksperimen dengan metrik langsung sehingga keputusan lebih cepat diambil dan tidak ada action item yang terlewat.",
        "keywords": [
          "SheetMate",
          "A/B testing",
          "Analitik pertumbuhan",
          "Manajemen eksperimen",
          "Google Sheets"
        ],
        "readingTime": "Baca 6 menit",
        "heroEyebrow": "Wawasan Pertumbuhan",
        "metaTitle": "Studi Kasus Pertumbuhan SheetMate｜Menyatukan Hipotesis dan Metrik Eksperimen",
        "metaDescription": "Lihat bagaimana tim pertumbuhan memadukan brief Markdown, metrik JSON, dan dashboard langsung di dalam SheetMate untuk mempercepat keputusan.",
        "imagePlaceholder": {
          "caption": "Mockup: tampilan terbagi horizontal yang menampilkan brief eksperimen di samping iframe dashboard",
          "alt": "Tampilan terbagi SheetMate untuk eksperimen pertumbuhan"
        },
        "sections": [
          {
            "title": "Terlalu banyak tab untuk satu pembacaan hasil",
            "paragraphs": [
              "Manajer pertumbuhan menjalankan banyak tes sekaligus dan butuh hipotesis, metrik langsung, serta catatan analis selalu selaras.",
              "Mereka harus berpindah-pindah antara Data Studio, Sheets, dan thread Slack, sehingga sulit menceritakan kisah yang rapi dalam rapat."
            ]
          },
          {
            "title": "SheetMate menyimpan seluruh narasi",
            "paragraphs": [
              "Brief eksperimen ditulis sebagai Markdown lengkap dengan tujuan, segmen target, dan guardrail. SheetMate merendernya dengan rapi agar mudah dipindai.",
              "Analis menempelkan tautan dashboard BI langsung ke dalam sel sehingga pratinjau iframe menampilkan hasil real-time di samping brief.",
              "Tampilan terbagi membandingkan daftar metrik JSON untuk kelompok kontrol dan perlakuan, langsung memunculkan lift yang berarti."
            ]
          },
          {
            "title": "Keputusan diambil dalam rapat yang sama",
            "paragraphs": [
              "Mode presentasi memperbesar tipografi agar terbaca di ruangan, dan tangkapan layar langsung berfungsi sebagai slide rekap beberapa menit setelah rapat.",
              "Action item dicatat dalam tugas Markdown sehingga pemilik dan tenggat waktu ikut menyertai data."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Dokumentasikan brief eksperimen",
            "description": "Catat tujuan, hipotesis, dan metrik north-star dalam Markdown di dalam sheet."
          },
          {
            "title": "Sematkan dashboard langsung",
            "description": "Tempelkan grafik atau visual SQL ke dalam sel agar SheetMate merender datanya secara inline."
          },
          {
            "title": "Bandingkan kontrol vs. perlakuan",
            "description": "Gunakan tampilan terbagi untuk mengevaluasi metrik JSON tiap kohort tanpa mengekspor data."
          },
          {
            "title": "Catat langkah selanjutnya",
            "description": "Tetapkan tindak lanjut lewat daftar tugas Markdown dan bagikan tampilan SheetMate ke para pemangku kepentingan."
          }
        ],
        "highlights": [
          "Template Markdown menjaga konteks eksperimen tetap teratur.",
          "Pratinjau iframe membawa dashboard BI ke kanvas yang sama.",
          "Perbandingan terbagi menghapus kebutuhan ekspor CSV manual."
        ],
        "metrics": [
          {
            "value": "7 days",
            "label": "Siklus keputusan",
            "context": "Keputusan eksperimen rata-rata diambil 30% lebih cepat."
          },
          {
            "value": "100%",
            "label": "Pemakaian ulang deck",
            "context": "Tangkapan layar dari SheetMate kini menggantikan slide deck khusus."
          },
          {
            "value": "0",
            "label": "Penyalinan manual",
            "context": "Semua metrik mengalir langsung dari sistem sumbernya."
          }
        ],
        "quote": {
          "text": "SheetMate memungkinkan kami menampilkan hipotesis dan lift dalam satu pandangan—review eksperimen akhirnya tuntas di ruang tempat semuanya dimulai.",
          "author": "Leo Gu",
          "role": "Lead Analis Pertumbuhan"
        }
      },
      es: {
        "industry": "Crecimiento de Producto / Analítica",
        "title": "Crecimiento de producto: mantén experimentos y métricas en una sola vista",
        "subtitle": "Usa briefs en Markdown y dashboards integrados para agilizar la lectura de resultados de experimentos",
        "excerpt": "Los analistas de crecimiento se apoyan en SheetMate para combinar el contexto del experimento con métricas en vivo, así las decisiones se toman más rápido y ningún pendiente se queda en el aire.",
        "keywords": [
          "SheetMate",
          "Pruebas A/B",
          "Analítica de crecimiento",
          "Gestión de experimentos",
          "Google Sheets"
        ],
        "readingTime": "6 min de lectura",
        "heroEyebrow": "Insights de Crecimiento",
        "metaTitle": "Caso de uso de crecimiento con SheetMate｜Unifica hipótesis y métricas de experimentos",
        "metaDescription": "Descubre cómo los equipos de crecimiento combinan briefs en Markdown, métricas JSON y dashboards en vivo dentro de SheetMate para acelerar las decisiones.",
        "imagePlaceholder": {
          "caption": "Mockup: vista dividida horizontal que muestra un brief de experimento junto a un iframe de dashboard",
          "alt": "Vista dividida de SheetMate para experimentos de crecimiento"
        },
        "sections": [
          {
            "title": "Demasiadas pestañas para una sola lectura de resultados",
            "paragraphs": [
              "Los responsables de crecimiento corren varias pruebas a la vez y necesitan mantener sincronizadas las hipótesis, las métricas en vivo y las notas de los analistas.",
              "Saltaban entre Data Studio, Sheets e hilos de Slack, lo que hacía difícil contar una historia clara en las juntas."
            ]
          },
          {
            "title": "SheetMate concentra toda la narrativa",
            "paragraphs": [
              "Los briefs de experimentos viven como Markdown con objetivos, segmentos objetivo y guardrails. SheetMate los renderiza de forma limpia para revisarlos de un vistazo.",
              "Los analistas pegan los enlaces de dashboards de BI directo en las celdas, así la vista previa del iframe transmite resultados en tiempo real junto al brief.",
              "La vista dividida compara las listas de métricas JSON del grupo de control y del de tratamiento, y resalta al instante los aumentos relevantes."
            ]
          },
          {
            "title": "Las decisiones se cierran en la misma junta",
            "paragraphs": [
              "El modo de presentación agranda la tipografía para toda la sala, y las capturas funcionan como diapositivas de resumen pocos minutos después de la llamada.",
              "Los pendientes se registran en tareas de Markdown, así los responsables y las fechas de entrega viajan junto con los datos."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Documenta el brief del experimento",
            "description": "Captura objetivos, hipótesis y métricas north-star en Markdown dentro de la hoja."
          },
          {
            "title": "Integra dashboards en vivo",
            "description": "Pega gráficas o visuales de SQL en las celdas para que SheetMate renderice los datos en línea."
          },
          {
            "title": "Compara control vs. tratamiento",
            "description": "Usa la vista dividida para evaluar las métricas JSON de cada cohorte sin exportar datos."
          },
          {
            "title": "Registra los siguientes pasos",
            "description": "Asigna seguimientos con listas de tareas en Markdown y comparte la vista de SheetMate con los stakeholders."
          }
        ],
        "highlights": [
          "Las plantillas de Markdown mantienen ordenado el contexto del experimento.",
          "Las vistas previas en iframe traen los dashboards de BI al mismo lienzo.",
          "Las comparaciones divididas eliminan la necesidad de exportar CSV a mano."
        ],
        "metrics": [
          {
            "value": "7 days",
            "label": "Ciclo de decisión",
            "context": "Las decisiones de experimentos se toman en promedio 30% antes."
          },
          {
            "value": "100%",
            "label": "Reutilización de presentaciones",
            "context": "Las capturas de SheetMate ahora reemplazan las presentaciones a la medida."
          },
          {
            "value": "0",
            "label": "Copiado manual",
            "context": "Todas las métricas fluyen directo desde sus sistemas de origen."
          }
        ],
        "quote": {
          "text": "SheetMate nos deja mostrar la hipótesis y el aumento de un solo vistazo; por fin las revisiones de experimentos se quedan en la sala donde empiezan.",
          "author": "Leo Gu",
          "role": "Analista líder de crecimiento"
        }
      },
      ms: {
        "industry": "Pertumbuhan Produk / Analitik",
        "title": "Pertumbuhan Produk: Kekalkan Eksperimen dan Metrik dalam Satu Paparan",
        "subtitle": "Gunakan taklimat Markdown dan papan pemuka terbenam untuk mempercepat pembentangan hasil eksperimen",
        "excerpt": "Penganalisis pertumbuhan bergantung pada SheetMate untuk menggandingkan konteks eksperimen dengan metrik langsung supaya keputusan dibuat lebih pantas dan tiada item tindakan tertinggal.",
        "keywords": [
          "SheetMate",
          "Ujian A/B",
          "Analitik pertumbuhan",
          "Pengurusan eksperimen",
          "Google Sheets"
        ],
        "readingTime": "Bacaan 6 minit",
        "heroEyebrow": "Wawasan Pertumbuhan",
        "metaTitle": "Kes Penggunaan Pertumbuhan SheetMate｜Satukan Hipotesis dan Metrik Eksperimen",
        "metaDescription": "Lihat cara pasukan pertumbuhan menggabungkan taklimat Markdown, metrik JSON dan papan pemuka langsung dalam SheetMate untuk mempercepat keputusan.",
        "imagePlaceholder": {
          "caption": "Mockup: paparan terbahagi mendatar yang menunjukkan taklimat eksperimen di sebelah iframe papan pemuka",
          "alt": "Paparan terbahagi SheetMate untuk eksperimen pertumbuhan"
        },
        "sections": [
          {
            "title": "Terlalu banyak tab untuk satu pembentangan hasil",
            "paragraphs": [
              "Pengurus pertumbuhan menjalankan banyak ujian serentak dan perlu memastikan hipotesis, metrik langsung dan nota penganalisis sentiasa selari.",
              "Mereka terpaksa berulang-alik antara Data Studio, Sheets dan rangkaian Slack, menjadikan ia sukar untuk menyampaikan cerita yang kemas dalam mesyuarat."
            ]
          },
          {
            "title": "SheetMate menyimpan keseluruhan naratif",
            "paragraphs": [
              "Taklimat eksperimen disimpan sebagai Markdown lengkap dengan matlamat, segmen sasaran dan guardrail. SheetMate memaparkannya dengan kemas untuk imbasan pantas.",
              "Penganalisis menampal pautan papan pemuka BI terus ke dalam sel supaya pratonton iframe menstrim hasil masa nyata di sebelah taklimat.",
              "Paparan terbahagi membandingkan senarai metrik JSON bagi kumpulan kawalan dan rawatan, menonjolkan peningkatan bermakna dengan serta-merta."
            ]
          },
          {
            "title": "Keputusan dicapai dalam mesyuarat yang sama",
            "paragraphs": [
              "Mod pembentangan membesarkan tipografi untuk dilihat seisi bilik, dan tangkapan skrin terus menjadi slaid ringkasan beberapa minit selepas panggilan.",
              "Item tindakan direkodkan dalam tugas Markdown supaya pemilik dan tarikh akhir bergerak bersama-sama data."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Dokumenkan taklimat eksperimen",
            "description": "Rakam matlamat, hipotesis dan metrik north-star dalam Markdown di dalam helaian."
          },
          {
            "title": "Benamkan papan pemuka langsung",
            "description": "Tampal carta atau visual SQL ke dalam sel supaya SheetMate memaparkan data secara inline."
          },
          {
            "title": "Bandingkan kawalan lwn. rawatan",
            "description": "Gunakan paparan terbahagi untuk menilai metrik JSON bagi setiap kohort tanpa mengeksport data."
          },
          {
            "title": "Rekod langkah seterusnya",
            "description": "Tetapkan tindakan susulan melalui senarai tugas Markdown dan kongsi paparan SheetMate dengan pihak berkepentingan."
          }
        ],
        "highlights": [
          "Templat Markdown memastikan konteks eksperimen sentiasa teratur.",
          "Pratonton iframe membawa papan pemuka BI ke kanvas yang sama.",
          "Perbandingan terbahagi menghapuskan keperluan eksport CSV secara manual."
        ],
        "metrics": [
          {
            "value": "7 days",
            "label": "Kitaran keputusan",
            "context": "Keputusan eksperimen dibuat secara purata 30% lebih awal."
          },
          {
            "value": "100%",
            "label": "Penggunaan semula deck",
            "context": "Tangkapan skrin daripada SheetMate kini menggantikan deck slaid khusus."
          },
          {
            "value": "0",
            "label": "Penyalinan manual",
            "context": "Semua metrik mengalir terus daripada sistem sumbernya."
          }
        ],
        "quote": {
          "text": "SheetMate membolehkan kami menunjukkan hipotesis dan peningkatan dalam satu pandangan—ulasan eksperimen akhirnya kekal dalam bilik tempat ia bermula.",
          "author": "Leo Gu",
          "role": "Ketua Penganalisis Pertumbuhan"
        }
      }
    }
  },
  {
    slug: 'data-science-json-libraries',
    locales: {
      zh: {
        industry: '数据科学 / 内部工具',
        title: '数据科学团队：管理模型参数与实验知识库',
        subtitle: '用 JSON 展开、Markdown 与 LaTeX 呈现实验说明',
        excerpt: '数据科学团队把参数库、实验说明与假设记录集中在 SheetMate，减少重复沟通。',
        keywords: ['SheetMate', '数据科学', 'JSON 参数', 'LaTeX', '实验记录'],
        readingTime: '7 分钟',
        heroEyebrow: 'Data Science',
        metaTitle: 'SheetMate 数据科学 Use Case｜参数库与实验说明同步',
        metaDescription: '了解数据科学团队如何使用 SheetMate 展示 JSON 参数、Markdown 说明与 LaTeX 公式，打造统一知识库。',
        imagePlaceholder: {
          caption: '示意图：JSON 参数树与 LaTeX 说明分屏',
          alt: 'SheetMate 数据科学视图占位图'
        },
        sections: [
          {
            title: '混合数据源带来的复杂度',
            paragraphs: [
              '数据科学团队在 Google Sheets 中维护模型参数库，字段来自内部 API、CSV 与临时实验结果。',
              '字段经常嵌套数组，传统表格难以展示结构，导致协作困难。'
            ]
          },
          {
            title: 'SheetMate 让结构化信息可读',
            paragraphs: [
              '团队把原始 JSON 写入单元格，SheetMate 自动以可折叠树状展示，并允许临时编辑进行假设测试。',
              '对于说明性文本，研究员使用 Markdown 编写公式、引用和代码块，借助 MarkdownWithLatex 直接展示数学公式。',
              '分屏功能让团队同时查看参数说明与实际 JSON 值，避免忘记更新。'
            ]
          },
          {
            title: '知识沉淀更完整',
            paragraphs: [
              '通过 SheetMate 的单元格定位提示，团队在评审时可以精准定位到特定模型版本与参数。',
              '历史实验记录通过 Markdown 任务列表整理，减少重复实验。'
            ]
          }
        ],
        workflow: [
          {
            title: '导入参数库',
            description: '将 API 返回的 JSON 粘贴到单元格或命名范围中。'
          },
          {
            title: '展开关键节点',
            description: '在 JSON 视图展开需要讨论的权重、阈值等字段。'
          },
          {
            title: '撰写实验说明',
            description: '使用 Markdown/LaTeX 描述实验目的与结论。'
          },
          {
            title: '分享评审视图',
            description: '通过截图或共享表格，把 SheetMate 分屏视图发给利益相关方。'
          }
        ],
        highlights: [
          'JSON 树状展开适合多层嵌套结构。',
          'MarkdownWithLatex 支持公式呈现。',
          '分屏同步查看说明与数据。'
        ],
        metrics: [
          {
            value: '3×',
            label: '参数评审效率',
            context: '一次评审的沟通往返减少三分之二。'
          },
          {
            value: '20%',
            label: '重复实验降低',
            context: '明确记录避免了重复跑模型。'
          },
          {
            value: '1',
            label: '知识库入口',
            context: '团队成员只需使用 Google Sheets 即可获取全部上下文。'
          }
        ],
        quote: {
          text: '我们把模型参数、说明与实验结论聚合在一张表里，任何人打开 SheetMate 都能理解上下文。',
          author: 'Hannah Xu',
          role: '数据科学经理'
        }
      },
      en: {
        industry: 'Data Science / Internal Tools',
        title: 'Data Science: Keep Parameter Libraries and Experiment Notes Together',
        subtitle: 'Use JSON trees, Markdown, and LaTeX to document models in a single workspace',
        excerpt: 'Data science teams rely on SheetMate to centralize parameter sets, experiment context, and formulas so collaboration stays crisp.',
        keywords: ['SheetMate', 'Data science', 'JSON parameters', 'LaTeX', 'Experiment log'],
        readingTime: '7 min read',
        heroEyebrow: 'Data Science',
        metaTitle: 'SheetMate Data Science Use Case｜Synchronize Parameters and Experiment Notes',
        metaDescription: 'Learn how data science teams use SheetMate to display JSON parameters, Markdown narratives, and LaTeX formulas side by side for clear reviews.',
        imagePlaceholder: {
          caption: 'Mockup: split view of a JSON parameter tree next to Markdown + LaTeX notes',
          alt: 'SheetMate split view for data science reviews'
        },
        sections: [
          {
            title: 'Why collaboration stalled',
            paragraphs: [
              'The team keeps a parameter library in Google Sheets fed by internal APIs, CSV drops, and ad-hoc experiments.',
              'Nested arrays inside raw cells were tough to scan, slowing handoffs between research scientists and engineers.'
            ]
          },
          {
            title: 'How SheetMate clarifies the data',
            paragraphs: [
              'Pasting raw JSON into a cell gives the team a collapsible tree with inline edit support for quick what-if tests.',
              'Researchers document context in Markdown, complete with LaTeX equations and code samples rendered in place.',
              'Split view keeps the narrative on one side and the parameter values on the other so updates never fall out of sync.'
            ]
          },
          {
            title: 'Knowledge that sticks',
            paragraphs: [
              'Cell location hints make it simple to point reviewers to specific model versions or features.',
              'Markdown task lists capture experiment history, preventing duplicate model runs.'
            ]
          }
        ],
        workflow: [
          {
            title: 'Import parameter sets',
            description: 'Drop API outputs or CSV extracts into cells as JSON for instant visualization.'
          },
          {
            title: 'Expand the key nodes',
            description: 'Use SheetMate’s JSON view to inspect weights, thresholds, and hyperparameters.'
          },
          {
            title: 'Write the experiment story',
            description: 'Capture objectives, data sources, and conclusions with Markdown and LaTeX.'
          },
          {
            title: 'Share the review view',
            description: 'Send the SheetMate split screen to stakeholders so feedback stays grounded in data.'
          }
        ],
        highlights: [
          'JSON trees make multi-level structures easy to scan.',
          'MarkdownWithLatex renders formulas without leaving Sheets.',
          'Split view keeps documentation and data perfectly aligned.'
        ],
        metrics: [
          {
            value: '3×',
            label: 'Review efficiency',
            context: 'Collaboration cycles are now two-thirds shorter.'
          },
          {
            value: '20%',
            label: 'Fewer duplicate experiments',
            context: 'Clear run histories stop teams from repeating work.'
          },
          {
            value: '1',
            label: 'Knowledge hub',
            context: 'Everyone references a single SheetMate view for context.'
          }
        ],
        quote: {
          text: 'We manage parameters, documentation, and conclusions in one SheetMate view—any researcher can pick up the thread immediately.',
          author: 'Hannah Xu',
          role: 'Data Science Manager'
        }
      },
      ja: {
        "industry": "データサイエンス / 社内ツール",
        "title": "データサイエンス：パラメータライブラリと実験ノートを一つに集約",
        "subtitle": "JSONツリー、Markdown、LaTeXを使い、モデルを単一のワークスペースでドキュメント化",
        "excerpt": "データサイエンスチームはSheetMateを活用し、パラメータセット、実験の背景、数式を一元管理することで、コラボレーションを明瞭に保っています。",
        "keywords": [
          "SheetMate",
          "データサイエンス",
          "JSONパラメータ",
          "LaTeX",
          "実験ログ"
        ],
        "readingTime": "7分で読了",
        "heroEyebrow": "データサイエンス",
        "metaTitle": "SheetMate データサイエンス活用事例｜パラメータと実験ノートを同期",
        "metaDescription": "データサイエンスチームがSheetMateでJSONパラメータ、Markdownの解説、LaTeX数式を並べて表示し、明快なレビューを実現する方法をご紹介します。",
        "imagePlaceholder": {
          "caption": "モックアップ：JSONパラメータツリーをMarkdown＋LaTeXノートと並べた分割ビュー",
          "alt": "データサイエンスのレビュー向けSheetMate分割ビュー"
        },
        "sections": [
          {
            "title": "なぜコラボレーションが停滞したのか",
            "paragraphs": [
              "チームは社内API、CSVの取り込み、その都度の実験から供給されるパラメータライブラリをGoogle Sheetsで管理しています。",
              "セル内に生のままネストされた配列は読み取りづらく、リサーチサイエンティストとエンジニア間の引き継ぎを遅らせていました。"
            ]
          },
          {
            "title": "SheetMateがデータを明確にする仕組み",
            "paragraphs": [
              "生のJSONをセルに貼り付けるだけで、折りたたみ可能なツリーが得られ、インライン編集で素早いWhat-ifテストも行えます。",
              "リサーチャーはMarkdownで背景を記述し、LaTeXの数式やコードサンプルもその場でレンダリングされます。",
              "分割ビューでは一方に解説、もう一方にパラメータ値を表示するため、更新がずれることがありません。"
            ]
          },
          {
            "title": "定着する知識",
            "paragraphs": [
              "セル位置のヒントにより、レビュアーに特定のモデルバージョンや特徴量を簡単に示せます。",
              "Markdownのタスクリストが実験履歴を記録し、モデルの重複実行を防ぎます。"
            ]
          }
        ],
        "workflow": [
          {
            "title": "パラメータセットをインポート",
            "description": "APIの出力やCSV抽出をJSONとしてセルに投入し、すぐに可視化します。"
          },
          {
            "title": "重要なノードを展開",
            "description": "SheetMateのJSONビューで重み、しきい値、ハイパーパラメータを確認します。"
          },
          {
            "title": "実験のストーリーを記述",
            "description": "目的、データソース、結論をMarkdownとLaTeXで記録します。"
          },
          {
            "title": "レビュービューを共有",
            "description": "SheetMateの分割画面を関係者に送り、フィードバックをデータに根ざしたものに保ちます。"
          }
        ],
        "highlights": [
          "JSONツリーで多階層構造が一目で把握できます。",
          "MarkdownWithLatexがSheetsを離れずに数式をレンダリングします。",
          "分割ビューでドキュメントとデータが完全に揃います。"
        ],
        "metrics": [
          {
            "value": "3×",
            "label": "レビュー効率",
            "context": "コラボレーションのサイクルが3分の1に短縮されました。"
          },
          {
            "value": "20%",
            "label": "重複実験の削減",
            "context": "明確な実行履歴がチームの作業重複を防ぎます。"
          },
          {
            "value": "1",
            "label": "ナレッジハブ",
            "context": "全員が単一のSheetMateビューを背景情報として参照します。"
          }
        ],
        "quote": {
          "text": "パラメータ、ドキュメント、結論を一つのSheetMateビューで管理しているので、どのリサーチャーもすぐに文脈を掴めます。",
          "author": "Hannah Xu",
          "role": "データサイエンスマネージャー"
        }
      },
      id: {
        "industry": "Data Science / Tools Internal",
        "title": "Data Science: Satukan Pustaka Parameter dan Catatan Eksperimen",
        "subtitle": "Gunakan pohon JSON, Markdown, dan LaTeX untuk mendokumentasikan model dalam satu ruang kerja",
        "excerpt": "Tim data science mengandalkan SheetMate untuk memusatkan kumpulan parameter, konteks eksperimen, dan formula agar kolaborasi tetap rapi.",
        "keywords": [
          "SheetMate",
          "Data science",
          "Parameter JSON",
          "LaTeX",
          "Log eksperimen"
        ],
        "readingTime": "Baca 7 menit",
        "heroEyebrow": "Data Science",
        "metaTitle": "Studi Kasus Data Science SheetMate｜Sinkronkan Parameter dan Catatan Eksperimen",
        "metaDescription": "Pelajari bagaimana tim data science menggunakan SheetMate untuk menampilkan parameter JSON, narasi Markdown, dan formula LaTeX secara berdampingan demi tinjauan yang jelas.",
        "imagePlaceholder": {
          "caption": "Mockup: tampilan terbagi antara pohon parameter JSON dan catatan Markdown + LaTeX",
          "alt": "Tampilan terbagi SheetMate untuk tinjauan data science"
        },
        "sections": [
          {
            "title": "Mengapa kolaborasi tersendat",
            "paragraphs": [
              "Tim menyimpan pustaka parameter di Google Sheets yang diisi oleh API internal, unggahan CSV, dan eksperimen dadakan.",
              "Array bersarang di dalam sel mentah sulit dibaca, sehingga memperlambat serah terima antara peneliti dan insinyur."
            ]
          },
          {
            "title": "Bagaimana SheetMate memperjelas data",
            "paragraphs": [
              "Menempelkan JSON mentah ke dalam sel memberi tim pohon yang dapat dilipat dengan dukungan edit inline untuk uji what-if cepat.",
              "Peneliti mendokumentasikan konteks dalam Markdown, lengkap dengan persamaan LaTeX dan contoh kode yang langsung dirender.",
              "Tampilan terbagi menempatkan narasi di satu sisi dan nilai parameter di sisi lain sehingga pembaruan tidak pernah keluar dari sinkron."
            ]
          },
          {
            "title": "Pengetahuan yang melekat",
            "paragraphs": [
              "Petunjuk lokasi sel memudahkan untuk mengarahkan peninjau ke versi model atau fitur tertentu.",
              "Daftar tugas Markdown mencatat riwayat eksperimen, mencegah pengulangan run model yang sama."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Impor kumpulan parameter",
            "description": "Letakkan output API atau ekstrak CSV ke dalam sel sebagai JSON untuk visualisasi instan."
          },
          {
            "title": "Buka node-node kunci",
            "description": "Gunakan tampilan JSON SheetMate untuk memeriksa bobot, ambang batas, dan hiperparameter."
          },
          {
            "title": "Tulis cerita eksperimen",
            "description": "Catat tujuan, sumber data, dan kesimpulan dengan Markdown dan LaTeX."
          },
          {
            "title": "Bagikan tampilan tinjauan",
            "description": "Kirim layar terbagi SheetMate ke pemangku kepentingan agar umpan balik tetap berlandaskan data."
          }
        ],
        "highlights": [
          "Pohon JSON memudahkan membaca struktur bertingkat.",
          "MarkdownWithLatex merender formula tanpa meninggalkan Sheets.",
          "Tampilan terbagi menjaga dokumentasi dan data tetap selaras sempurna."
        ],
        "metrics": [
          {
            "value": "3×",
            "label": "Efisiensi tinjauan",
            "context": "Siklus kolaborasi kini dua pertiga lebih singkat."
          },
          {
            "value": "20%",
            "label": "Eksperimen ganda berkurang",
            "context": "Riwayat run yang jelas mencegah tim mengulang pekerjaan."
          },
          {
            "value": "1",
            "label": "Pusat pengetahuan",
            "context": "Semua orang merujuk pada satu tampilan SheetMate untuk konteks."
          }
        ],
        "quote": {
          "text": "Kami mengelola parameter, dokumentasi, dan kesimpulan dalam satu tampilan SheetMate—peneliti mana pun bisa langsung melanjutkan benang merahnya.",
          "author": "Hannah Xu",
          "role": "Manajer Data Science"
        }
      },
      es: {
        "industry": "Ciencia de datos / Herramientas internas",
        "title": "Ciencia de datos: mantén juntas las bibliotecas de parámetros y las notas de experimentos",
        "subtitle": "Usa árboles JSON, Markdown y LaTeX para documentar modelos en un solo espacio de trabajo",
        "excerpt": "Los equipos de ciencia de datos confían en SheetMate para centralizar conjuntos de parámetros, contexto de experimentos y fórmulas, manteniendo la colaboración impecable.",
        "keywords": [
          "SheetMate",
          "Ciencia de datos",
          "Parámetros JSON",
          "LaTeX",
          "Registro de experimentos"
        ],
        "readingTime": "7 min de lectura",
        "heroEyebrow": "Ciencia de datos",
        "metaTitle": "Caso de uso de SheetMate en ciencia de datos｜Sincroniza parámetros y notas de experimentos",
        "metaDescription": "Descubre cómo los equipos de ciencia de datos usan SheetMate para mostrar parámetros JSON, narrativas en Markdown y fórmulas LaTeX lado a lado para revisiones claras.",
        "imagePlaceholder": {
          "caption": "Mockup: vista dividida de un árbol de parámetros JSON junto a notas en Markdown + LaTeX",
          "alt": "Vista dividida de SheetMate para revisiones de ciencia de datos"
        },
        "sections": [
          {
            "title": "Por qué se estancaba la colaboración",
            "paragraphs": [
              "El equipo mantiene una biblioteca de parámetros en Google Sheets alimentada por API internas, cargas de CSV y experimentos puntuales.",
              "Los arreglos anidados dentro de las celdas en bruto eran difíciles de leer, lo que ralentizaba el traspaso entre científicos de investigación e ingenieros."
            ]
          },
          {
            "title": "Cómo SheetMate aclara los datos",
            "paragraphs": [
              "Pegar JSON en bruto en una celda le da al equipo un árbol plegable con edición en línea para pruebas rápidas de hipótesis.",
              "Los investigadores documentan el contexto en Markdown, con ecuaciones LaTeX y ejemplos de código renderizados al instante.",
              "La vista dividida deja la narrativa en un lado y los valores de los parámetros en el otro, para que las actualizaciones nunca se desincronicen."
            ]
          },
          {
            "title": "Conocimiento que perdura",
            "paragraphs": [
              "Las pistas de ubicación de celda facilitan dirigir a los revisores hacia versiones de modelo o características específicas.",
              "Las listas de tareas en Markdown capturan el historial de experimentos y evitan ejecuciones de modelo duplicadas."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Importa los conjuntos de parámetros",
            "description": "Coloca las salidas de API o los extractos de CSV en las celdas como JSON para visualizarlos al instante."
          },
          {
            "title": "Expande los nodos clave",
            "description": "Usa la vista JSON de SheetMate para inspeccionar pesos, umbrales e hiperparámetros."
          },
          {
            "title": "Escribe la historia del experimento",
            "description": "Registra objetivos, fuentes de datos y conclusiones con Markdown y LaTeX."
          },
          {
            "title": "Comparte la vista de revisión",
            "description": "Envía la pantalla dividida de SheetMate a las partes interesadas para que los comentarios se basen en datos."
          }
        ],
        "highlights": [
          "Los árboles JSON facilitan la lectura de estructuras de varios niveles.",
          "MarkdownWithLatex renderiza fórmulas sin salir de Sheets.",
          "La vista dividida mantiene la documentación y los datos perfectamente alineados."
        ],
        "metrics": [
          {
            "value": "3×",
            "label": "Eficiencia en las revisiones",
            "context": "Los ciclos de colaboración ahora son dos tercios más cortos."
          },
          {
            "value": "20%",
            "label": "Menos experimentos duplicados",
            "context": "Los historiales de ejecución claros evitan que los equipos repitan el trabajo."
          },
          {
            "value": "1",
            "label": "Centro de conocimiento",
            "context": "Todos consultan una sola vista de SheetMate para tener contexto."
          }
        ],
        "quote": {
          "text": "Gestionamos parámetros, documentación y conclusiones en una sola vista de SheetMate; cualquier investigador puede retomar el hilo de inmediato.",
          "author": "Hannah Xu",
          "role": "Gerente de Ciencia de Datos"
        }
      },
      ms: {
        "industry": "Sains Data / Alat Dalaman",
        "title": "Sains Data: Kekalkan Pustaka Parameter dan Nota Eksperimen Bersama",
        "subtitle": "Gunakan pepohon JSON, Markdown dan LaTeX untuk mendokumentasikan model dalam satu ruang kerja",
        "excerpt": "Pasukan sains data bergantung pada SheetMate untuk memusatkan set parameter, konteks eksperimen dan formula supaya kerjasama kekal jelas.",
        "keywords": [
          "SheetMate",
          "Sains data",
          "Parameter JSON",
          "LaTeX",
          "Log eksperimen"
        ],
        "readingTime": "Bacaan 7 minit",
        "heroEyebrow": "Sains Data",
        "metaTitle": "Kes Penggunaan Sains Data SheetMate｜Selaraskan Parameter dan Nota Eksperimen",
        "metaDescription": "Ketahui cara pasukan sains data menggunakan SheetMate untuk memaparkan parameter JSON, naratif Markdown dan formula LaTeX bersebelahan demi semakan yang jelas.",
        "imagePlaceholder": {
          "caption": "Mockup: paparan terbahagi antara pepohon parameter JSON dengan nota Markdown + LaTeX",
          "alt": "Paparan terbahagi SheetMate untuk semakan sains data"
        },
        "sections": [
          {
            "title": "Mengapa kerjasama tersekat",
            "paragraphs": [
              "Pasukan ini menyimpan pustaka parameter dalam Google Sheets yang disuap oleh API dalaman, muat naik CSV dan eksperimen secara ad-hoc.",
              "Tatasusunan bersarang dalam sel mentah sukar dibaca, lalu memperlahankan penyerahan tugas antara saintis penyelidik dengan jurutera."
            ]
          },
          {
            "title": "Bagaimana SheetMate menjelaskan data",
            "paragraphs": [
              "Menampal JSON mentah ke dalam sel memberi pasukan satu pepohon boleh lipat dengan sokongan suntingan sebaris untuk ujian what-if pantas.",
              "Penyelidik mendokumentasikan konteks dalam Markdown, lengkap dengan persamaan LaTeX dan contoh kod yang dipaparkan di tempatnya.",
              "Paparan terbahagi mengekalkan naratif di satu sisi dan nilai parameter di sisi lain supaya kemas kini tidak pernah hilang penyelarasan."
            ]
          },
          {
            "title": "Pengetahuan yang kekal",
            "paragraphs": [
              "Petunjuk lokasi sel memudahkan untuk menunjukkan kepada penyemak versi model atau ciri tertentu.",
              "Senarai tugas Markdown merekod sejarah eksperimen, mencegah larian model yang berulang."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Import set parameter",
            "description": "Letakkan output API atau ekstrak CSV ke dalam sel sebagai JSON untuk visualisasi serta-merta."
          },
          {
            "title": "Kembangkan nod utama",
            "description": "Gunakan paparan JSON SheetMate untuk memeriksa pemberat, ambang dan hiperparameter."
          },
          {
            "title": "Tulis kisah eksperimen",
            "description": "Rakam objektif, sumber data dan kesimpulan dengan Markdown dan LaTeX."
          },
          {
            "title": "Kongsi paparan semakan",
            "description": "Hantar skrin terbahagi SheetMate kepada pihak berkepentingan supaya maklum balas kekal berasaskan data."
          }
        ],
        "highlights": [
          "Pepohon JSON memudahkan pembacaan struktur berbilang aras.",
          "MarkdownWithLatex memaparkan formula tanpa meninggalkan Sheets.",
          "Paparan terbahagi mengekalkan dokumentasi dan data sejajar dengan sempurna."
        ],
        "metrics": [
          {
            "value": "3×",
            "label": "Kecekapan semakan",
            "context": "Kitaran kerjasama kini dua pertiga lebih singkat."
          },
          {
            "value": "20%",
            "label": "Eksperimen berganda berkurang",
            "context": "Sejarah larian yang jelas menghalang pasukan daripada mengulang kerja."
          },
          {
            "value": "1",
            "label": "Hab pengetahuan",
            "context": "Semua orang merujuk satu paparan SheetMate untuk konteks."
          }
        ],
        "quote": {
          "text": "Kami menguruskan parameter, dokumentasi dan kesimpulan dalam satu paparan SheetMate—mana-mana penyelidik boleh menyambung semula perbincangan dengan segera.",
          "author": "Hannah Xu",
          "role": "Pengurus Sains Data"
        }
      }
    }
  },
  {
    slug: 'ecommerce-visual-merchandising',
    locales: {
      zh: {
        industry: '电商 / 视觉陈列',
        title: '电商新品陈列：同步文案、图片与渠道指引',
        subtitle: '一次性在 SheetMate 中核对 SKU 信息与渠道素材',
        excerpt: '电商团队用 SheetMate 的图片画廊、Markdown 模板与 iframe 预览，确保新品上线节奏顺畅。',
        keywords: ['SheetMate', '电商', '视觉陈列', '图片预览', '渠道管理'],
        readingTime: '5 分钟',
        heroEyebrow: 'E-commerce Ops',
        metaTitle: 'SheetMate 电商 Use Case｜新品陈列与素材审核',
        metaDescription: '了解电商品牌如何使用 SheetMate 对齐图片素材、渠道文案与上线指引，减少返工。',
        imagePlaceholder: {
          caption: '示意图：图片画廊与渠道文案垂直分屏',
          alt: 'SheetMate 电商陈列视图占位图'
        },
        sections: [
          {
            title: 'SKU 扩张带来的协作难题',
            paragraphs: [
              '一家 DTC 品牌在上线新系列时，需要在 Google Sheets 汇总 SKU、素材与各渠道文案。',
              '设计与运营团队难以在同一地方确认图片与文案是否匹配，易出现错误。'
            ]
          },
          {
            title: 'SheetMate 显示素材与文案',
            paragraphs: [
              '设计师把 CDN 图片链接数组放入单元格，SheetMate 的 ImageView 自动生成画廊预览。',
              '运营将不同渠道的文案写成 Markdown，渲染后保留标题、列表与 CTA。',
              '通过 vertical 分屏，一侧展示桌面端素材，另一侧显示移动端素材与上线说明。'
            ]
          },
          {
            title: '上线节奏更顺畅',
            paragraphs: [
              '团队利用单元格位置提示在沟通时准确定位 SKU。',
              '把渠道后台的预览链接粘贴为 iframe，随时检查页面最终呈现。'
            ]
          }
        ],
        workflow: [
          {
            title: '整理素材链接',
            description: '将图片 CDN 地址以数组形式写入单元格，SheetMate 自动拉取并展示。'
          },
          {
            title: '撰写渠道文案',
            description: '使用 Markdown 模板覆盖不同渠道尺寸与 CTA。'
          },
          {
            title: '分屏审核桌面与移动版本',
            description: '垂直分屏分别展示两套素材，确保一致性。'
          },
          {
            title: '确认上线',
            description: '在 Markdown 清单中标记状态，与团队同步。'
          }
        ],
        highlights: [
          'ImageView 支持批量预览图像链接。',
          'Markdown 渲染保持品牌语气与格式。',
          'iframe 预览渠道页面，减少切换。'
        ],
        metrics: [
          {
            value: '2 天',
            label: '新品上线提前量',
            context: '上线准备时间平均提前两天完成。'
          },
          {
            value: '0',
            label: '重复返工',
            context: '图文同步减少了返工请求。'
          },
          {
            value: '6',
            label: '渠道同步',
            context: '在同一视图内确认 6 个渠道的素材。'
          }
        ],
        quote: {
          text: 'SheetMate 让设计和运营首次在同一屏讨论素材与文案，launch 节奏完全变了。',
          author: 'Olivia Yang',
          role: '电商运营负责人'
        }
      },
      en: {
        industry: 'E-commerce / Visual Merchandising',
        title: 'E-commerce Launches: Sync Copy, Imagery, and Channel Guides',
        subtitle: 'Review SKU data, assets, and channel requirements side by side',
        excerpt: 'Merchandising teams use SheetMate to keep product copy, creative, and launch checklists aligned so releases go live without fire drills.',
        keywords: ['SheetMate', 'E-commerce', 'Visual merchandising', 'Image preview', 'Channel operations'],
        readingTime: '5 min read',
        heroEyebrow: 'E-commerce Ops',
        metaTitle: 'SheetMate E-commerce Use Case｜Align Launch Assets and Copy',
        metaDescription: 'Learn how brands use SheetMate to combine image assets, channel copy, and launch instructions in one workspace.',
        imagePlaceholder: {
          caption: 'Mockup: vertical split showing a merchandising gallery next to Markdown channel copy',
          alt: 'SheetMate split view for e-commerce merchandising'
        },
        sections: [
          {
            title: 'Scaling catalog coordination',
            paragraphs: [
              'A DTC brand rolled new collections into Google Sheets to track SKUs, imagery, and channel copy.',
              'Design and operations couldn’t confirm everything in one place, leading to mismatched creative on launch day.'
            ]
          },
          {
            title: 'SheetMate puts assets and copy in lockstep',
            paragraphs: [
              'Designers paste CDN links into arrays and SheetMate instantly renders a gallery for desktop and mobile assets.',
              'Channel managers draft copy in Markdown so tone, CTAs, and bullet lists stay on brand.',
              'Vertical split shows desktop creative on one side and mobile variations—or launch instructions—on the other for easy comparison.'
            ]
          },
          {
            title: 'Launches stay on schedule',
            paragraphs: [
              'Cell location hints keep feedback tied to specific SKUs and variants.',
              'Embedded iframes preview channel backends, letting teams verify merchandising pages without leaving the sheet.'
            ]
          }
        ],
        workflow: [
          {
            title: 'Organize asset links',
            description: 'Store CDN URLs as arrays so SheetMate can surface a review gallery automatically.'
          },
          {
            title: 'Draft channel-ready copy',
            description: 'Use Markdown templates for each channel’s CTA, formatting, and localization needs.'
          },
          {
            title: 'Review desktop and mobile together',
            description: 'Compare creative variations in split view to keep messaging aligned.'
          },
          {
            title: 'Sign off the launch',
            description: 'Check off Markdown tasks and share the SheetMate view with the launch squad.'
          }
        ],
        highlights: [
          'ImageView assembles galleries for quick creative approval.',
          'Markdown keeps voice and structure consistent across channels.',
          'Iframe embeds surface channel previews without extra tabs.'
        ],
        metrics: [
          {
            value: '2 days',
            label: 'Lead time gained',
            context: 'Launch prep now finishes roughly two days earlier than before.'
          },
          {
            value: '0',
            label: 'Rework loops',
            context: 'Aligned creative and copy mean no last-minute corrections.'
          },
          {
            value: '6',
            label: 'Channels coordinated',
            context: 'All six launch channels are reviewed from the same SheetMate view.'
          }
        ],
        quote: {
          text: 'SheetMate finally put design and ops in the same frame—our launch cadence is smoother than ever.',
          author: 'Olivia Yang',
          role: 'Head of E-commerce Operations'
        }
      },
      ja: {
        "industry": "Eコマース / ビジュアルマーチャンダイジング",
        "title": "ECの新商品ローンチ：コピー・ビジュアル・チャネルガイドを一括同期",
        "subtitle": "SKUデータ、素材、チャネル要件を横並びでレビュー",
        "excerpt": "マーチャンダイジングチームはSheetMateで商品コピー、クリエイティブ、ローンチ用チェックリストを揃え、慌てることなくリリースを公開しています。",
        "keywords": [
          "SheetMate",
          "ECサイト運営",
          "ビジュアルマーチャンダイジング",
          "画像プレビュー",
          "チャネル運用"
        ],
        "readingTime": "5分で読了",
        "heroEyebrow": "EC運用",
        "metaTitle": "SheetMate ECユースケース｜ローンチ素材とコピーを揃える",
        "metaDescription": "画像素材、チャネル別コピー、ローンチ指示を一つのワークスペースにまとめるブランドのSheetMate活用法をご紹介します。",
        "imagePlaceholder": {
          "caption": "モックアップ：マーチャンダイジングギャラリーとMarkdownのチャネルコピーを縦に分割表示",
          "alt": "ECマーチャンダイジング向けSheetMateの分割ビュー"
        },
        "sections": [
          {
            "title": "カタログ連携をスケールさせる",
            "paragraphs": [
              "あるDTCブランドは、SKU・ビジュアル・チャネルコピーを管理するため、新コレクションをGoogle Sheetsに集約していました。",
              "デザインと運用が一か所ですべてを確認できず、ローンチ当日にクリエイティブの食い違いが発生していました。"
            ]
          },
          {
            "title": "SheetMateが素材とコピーを完全に同期",
            "paragraphs": [
              "デザイナーが配列にCDNリンクを貼り付けると、SheetMateがデスクトップ・モバイル用素材のギャラリーを即座にレンダリングします。",
              "チャネル担当者はMarkdownでコピーを作成するため、トーン、CTA、箇条書きがブランドの世界観を保ちます。",
              "縦分割で一方にデスクトップのクリエイティブ、もう一方にモバイルのバリエーションやローンチ指示を表示し、簡単に比較できます。"
            ]
          },
          {
            "title": "ローンチがスケジュール通りに進む",
            "paragraphs": [
              "セル位置のヒントによって、フィードバックが特定のSKUやバリアントと結びつきます。",
              "埋め込んだiframeでチャネルのバックエンドをプレビューでき、シートを離れずにマーチャンダイジングページを確認できます。"
            ]
          }
        ],
        "workflow": [
          {
            "title": "素材リンクを整理する",
            "description": "CDNのURLを配列として保存すれば、SheetMateがレビュー用ギャラリーを自動的に表示します。"
          },
          {
            "title": "チャネル向けコピーを作成する",
            "description": "各チャネルのCTA、書式、ローカライズに合わせたMarkdownテンプレートを使います。"
          },
          {
            "title": "デスクトップとモバイルを同時にレビュー",
            "description": "分割ビューでクリエイティブのバリエーションを比較し、メッセージを揃えます。"
          },
          {
            "title": "ローンチを承認する",
            "description": "Markdownのタスクをチェックし、SheetMateのビューをローンチチームと共有します。"
          }
        ],
        "highlights": [
          "ImageViewがギャラリーを組み立て、クリエイティブの承認を素早く行えます。",
          "Markdownでチャネル間のトーンと構成を一貫させます。",
          "iframe埋め込みで余計なタブを開かずにチャネルプレビューを表示します。"
        ],
        "metrics": [
          {
            "value": "2 days",
            "label": "短縮されたリードタイム",
            "context": "ローンチ準備が以前より約2日早く完了するようになりました。"
          },
          {
            "value": "0",
            "label": "手戻りの回数",
            "context": "クリエイティブとコピーが揃っているため、直前の修正が発生しません。"
          },
          {
            "value": "6",
            "label": "連携するチャネル数",
            "context": "6つすべてのローンチチャネルを同じSheetMateのビューでレビューします。"
          }
        ],
        "quote": {
          "text": "SheetMateのおかげでデザインと運用がようやく同じ画面に収まりました。ローンチのリズムがこれまでになくスムーズです。",
          "author": "Olivia Yang",
          "role": "Eコマース運用責任者"
        }
      },
      id: {
        "industry": "E-commerce / Visual Merchandising",
        "title": "Peluncuran E-commerce: Sinkronkan Copy, Visual, dan Panduan Channel",
        "subtitle": "Tinjau data SKU, aset, dan kebutuhan channel secara berdampingan",
        "excerpt": "Tim merchandising memakai SheetMate agar copy produk, materi kreatif, dan checklist peluncuran tetap selaras sehingga rilis berjalan tanpa kepanikan.",
        "keywords": [
          "SheetMate",
          "E-commerce",
          "Visual merchandising",
          "Pratinjau gambar",
          "Operasional channel"
        ],
        "readingTime": "Baca 5 menit",
        "heroEyebrow": "Operasional E-commerce",
        "metaTitle": "Studi Kasus E-commerce SheetMate｜Selaraskan Aset dan Copy Peluncuran",
        "metaDescription": "Pelajari cara brand memakai SheetMate untuk menggabungkan aset gambar, copy channel, dan instruksi peluncuran dalam satu ruang kerja.",
        "imagePlaceholder": {
          "caption": "Mockup: pembagian vertikal yang menampilkan galeri merchandising di samping copy channel Markdown",
          "alt": "Tampilan terbagi SheetMate untuk merchandising e-commerce"
        },
        "sections": [
          {
            "title": "Menskalakan koordinasi katalog",
            "paragraphs": [
              "Sebuah brand DTC memasukkan koleksi baru ke Google Sheets untuk melacak SKU, visual, dan copy channel.",
              "Tim desain dan operasional tidak bisa memverifikasi semuanya di satu tempat, sehingga materi kreatif tidak cocok saat hari peluncuran."
            ]
          },
          {
            "title": "SheetMate menyelaraskan aset dan copy secara seirama",
            "paragraphs": [
              "Desainer menempelkan tautan CDN ke dalam array dan SheetMate langsung merender galeri untuk aset desktop maupun mobile.",
              "Manajer channel menyusun copy dalam Markdown sehingga tone, CTA, dan daftar poin tetap sesuai brand.",
              "Pembagian vertikal menampilkan materi kreatif desktop di satu sisi dan variasi mobile—atau instruksi peluncuran—di sisi lain agar mudah dibandingkan."
            ]
          },
          {
            "title": "Peluncuran tetap sesuai jadwal",
            "paragraphs": [
              "Petunjuk lokasi sel menjaga masukan tetap terhubung dengan SKU dan varian tertentu.",
              "Iframe tertanam menampilkan pratinjau backend channel, sehingga tim bisa memverifikasi halaman merchandising tanpa keluar dari sheet."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Rapikan tautan aset",
            "description": "Simpan URL CDN sebagai array agar SheetMate dapat memunculkan galeri tinjauan secara otomatis."
          },
          {
            "title": "Susun copy siap channel",
            "description": "Gunakan template Markdown untuk kebutuhan CTA, format, dan lokalisasi setiap channel."
          },
          {
            "title": "Tinjau desktop dan mobile bersamaan",
            "description": "Bandingkan variasi kreatif dalam tampilan terbagi agar pesan tetap selaras."
          },
          {
            "title": "Setujui peluncuran",
            "description": "Centang tugas Markdown dan bagikan tampilan SheetMate kepada tim peluncuran."
          }
        ],
        "highlights": [
          "ImageView menyusun galeri untuk persetujuan kreatif yang cepat.",
          "Markdown menjaga gaya bahasa dan struktur tetap konsisten di semua channel.",
          "Sematan iframe menampilkan pratinjau channel tanpa tab tambahan."
        ],
        "metrics": [
          {
            "value": "2 days",
            "label": "Waktu persiapan yang dihemat",
            "context": "Persiapan peluncuran kini selesai sekitar dua hari lebih awal dari sebelumnya."
          },
          {
            "value": "0",
            "label": "Putaran revisi",
            "context": "Materi kreatif dan copy yang selaras berarti tidak ada koreksi di menit-menit terakhir."
          },
          {
            "value": "6",
            "label": "Channel yang dikoordinasikan",
            "context": "Keenam channel peluncuran ditinjau dari tampilan SheetMate yang sama."
          }
        ],
        "quote": {
          "text": "SheetMate akhirnya menyatukan tim desain dan operasional dalam satu bingkai—ritme peluncuran kami lebih lancar dari sebelumnya.",
          "author": "Olivia Yang",
          "role": "Kepala Operasional E-commerce"
        }
      },
      es: {
        "industry": "E-commerce / Visual merchandising",
        "title": "Lanzamientos de e-commerce: sincroniza textos, imágenes y guías por canal",
        "subtitle": "Revisa datos de SKU, recursos y requisitos de cada canal lado a lado",
        "excerpt": "Los equipos de merchandising usan SheetMate para mantener alineados los textos de producto, lo creativo y las listas de verificación, así los lanzamientos salen sin contratiempos.",
        "keywords": [
          "SheetMate",
          "E-commerce",
          "Visual merchandising",
          "Vista previa de imágenes",
          "Operación de canales"
        ],
        "readingTime": "5 min de lectura",
        "heroEyebrow": "Operaciones de e-commerce",
        "metaTitle": "Caso de uso de e-commerce de SheetMate｜Alinea recursos y textos de lanzamiento",
        "metaDescription": "Descubre cómo las marcas usan SheetMate para combinar recursos de imagen, textos por canal e instrucciones de lanzamiento en un solo espacio de trabajo.",
        "imagePlaceholder": {
          "caption": "Mockup: división vertical que muestra una galería de merchandising junto a textos de canal en Markdown",
          "alt": "Vista dividida de SheetMate para merchandising de e-commerce"
        },
        "sections": [
          {
            "title": "Escalar la coordinación del catálogo",
            "paragraphs": [
              "Una marca DTC volcó sus nuevas colecciones en Google Sheets para llevar el control de SKU, imágenes y textos por canal.",
              "Diseño y operaciones no podían confirmar todo en un mismo lugar, lo que provocaba creatividades que no coincidían el día del lanzamiento."
            ]
          },
          {
            "title": "SheetMate pone recursos y textos en perfecta sintonía",
            "paragraphs": [
              "Los diseñadores pegan enlaces de CDN en arreglos y SheetMate genera al instante una galería con los recursos de escritorio y móvil.",
              "Los responsables de canal redactan los textos en Markdown para que el tono, los CTA y las listas se mantengan fieles a la marca.",
              "La división vertical muestra la creatividad de escritorio de un lado y las variaciones móviles —o las instrucciones de lanzamiento— del otro, para compararlas fácilmente."
            ]
          },
          {
            "title": "Los lanzamientos se mantienen en agenda",
            "paragraphs": [
              "Las pistas de ubicación de celda mantienen los comentarios ligados a SKU y variantes específicas.",
              "Los iframe incrustados muestran una vista previa del backend de cada canal, lo que permite verificar las páginas de merchandising sin salir de la hoja."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Organiza los enlaces de recursos",
            "description": "Guarda las URL de CDN como arreglos para que SheetMate muestre una galería de revisión de forma automática."
          },
          {
            "title": "Redacta textos listos por canal",
            "description": "Usa plantillas de Markdown para el CTA, el formato y las necesidades de localización de cada canal."
          },
          {
            "title": "Revisa escritorio y móvil a la vez",
            "description": "Compara las variaciones creativas en vista dividida para mantener el mensaje alineado."
          },
          {
            "title": "Aprueba el lanzamiento",
            "description": "Marca las tareas en Markdown y comparte la vista de SheetMate con el equipo de lanzamiento."
          }
        ],
        "highlights": [
          "ImageView arma galerías para aprobar lo creativo con rapidez.",
          "Markdown mantiene la voz y la estructura consistentes en todos los canales.",
          "Los iframe incrustados muestran vistas previas de canal sin pestañas adicionales."
        ],
        "metrics": [
          {
            "value": "2 days",
            "label": "Tiempo de preparación ganado",
            "context": "La preparación del lanzamiento ahora termina unos dos días antes que antes."
          },
          {
            "value": "0",
            "label": "Ciclos de retrabajo",
            "context": "Tener lo creativo y los textos alineados significa que no hay correcciones de último minuto."
          },
          {
            "value": "6",
            "label": "Canales coordinados",
            "context": "Los seis canales de lanzamiento se revisan desde la misma vista de SheetMate."
          }
        ],
        "quote": {
          "text": "SheetMate por fin puso a diseño y operaciones en el mismo cuadro: nuestro ritmo de lanzamientos es más fluido que nunca.",
          "author": "Olivia Yang",
          "role": "Directora de Operaciones de E-commerce"
        }
      },
      ms: {
        "industry": "E-dagang / Visual Merchandising",
        "title": "Pelancaran E-dagang: Selaraskan Salinan, Imej dan Panduan Saluran",
        "subtitle": "Semak data SKU, aset dan keperluan saluran secara bersebelahan",
        "excerpt": "Pasukan merchandising menggunakan SheetMate untuk memastikan salinan produk, bahan kreatif dan senarai semak pelancaran sentiasa selaras supaya keluaran berjalan tanpa kelam-kabut.",
        "keywords": [
          "SheetMate",
          "E-dagang",
          "Visual merchandising",
          "Pratonton imej",
          "Operasi saluran"
        ],
        "readingTime": "Bacaan 5 minit",
        "heroEyebrow": "Operasi E-dagang",
        "metaTitle": "Kes Penggunaan E-dagang SheetMate｜Selaraskan Aset dan Salinan Pelancaran",
        "metaDescription": "Ketahui cara jenama menggunakan SheetMate untuk menggabungkan aset imej, salinan saluran dan arahan pelancaran dalam satu ruang kerja.",
        "imagePlaceholder": {
          "caption": "Mockup: pembahagian menegak yang memaparkan galeri merchandising di sebelah salinan saluran Markdown",
          "alt": "Paparan terbahagi SheetMate untuk merchandising e-dagang"
        },
        "sections": [
          {
            "title": "Menskalakan penyelarasan katalog",
            "paragraphs": [
              "Sebuah jenama DTC memasukkan koleksi baharu ke dalam Google Sheets untuk menjejaki SKU, imej dan salinan saluran.",
              "Pasukan reka bentuk dan operasi tidak dapat mengesahkan segalanya di satu tempat, lalu mengakibatkan bahan kreatif yang tidak sepadan pada hari pelancaran."
            ]
          },
          {
            "title": "SheetMate menyelaraskan aset dan salinan secara seiring",
            "paragraphs": [
              "Pereka menampal pautan CDN ke dalam tatasusunan dan SheetMate serta-merta memaparkan galeri untuk aset desktop dan mudah alih.",
              "Pengurus saluran merangka salinan dalam Markdown supaya nada, CTA dan senarai berbutir kekal selaras dengan jenama.",
              "Pembahagian menegak memaparkan bahan kreatif desktop di satu sisi dan variasi mudah alih—atau arahan pelancaran—di sisi lain untuk perbandingan yang mudah."
            ]
          },
          {
            "title": "Pelancaran kekal mengikut jadual",
            "paragraphs": [
              "Petunjuk lokasi sel mengekalkan maklum balas terikat dengan SKU dan varian tertentu.",
              "Iframe terbenam memaparkan pratonton backend saluran, membolehkan pasukan mengesahkan halaman merchandising tanpa meninggalkan helaian."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Susun pautan aset",
            "description": "Simpan URL CDN sebagai tatasusunan supaya SheetMate boleh memaparkan galeri semakan secara automatik."
          },
          {
            "title": "Rangka salinan sedia saluran",
            "description": "Gunakan templat Markdown untuk keperluan CTA, format dan penyetempatan setiap saluran."
          },
          {
            "title": "Semak desktop dan mudah alih serentak",
            "description": "Bandingkan variasi kreatif dalam paparan terbahagi untuk memastikan mesej kekal selaras."
          },
          {
            "title": "Luluskan pelancaran",
            "description": "Tandai tugasan Markdown dan kongsi paparan SheetMate dengan pasukan pelancaran."
          }
        ],
        "highlights": [
          "ImageView menyusun galeri untuk kelulusan kreatif yang pantas.",
          "Markdown memastikan nada dan struktur konsisten merentas semua saluran.",
          "Sematan iframe memaparkan pratonton saluran tanpa tab tambahan."
        ],
        "metrics": [
          {
            "value": "2 days",
            "label": "Masa persediaan yang dijimatkan",
            "context": "Persediaan pelancaran kini selesai kira-kira dua hari lebih awal daripada sebelumnya."
          },
          {
            "value": "0",
            "label": "Pusingan kerja semula",
            "context": "Bahan kreatif dan salinan yang selaras bermakna tiada pembetulan pada saat akhir."
          },
          {
            "value": "6",
            "label": "Saluran diselaraskan",
            "context": "Kesemua enam saluran pelancaran disemak dari paparan SheetMate yang sama."
          }
        ],
        "quote": {
          "text": "SheetMate akhirnya menyatukan reka bentuk dan operasi dalam bingkai yang sama—rentak pelancaran kami lebih lancar berbanding sebelum ini.",
          "author": "Olivia Yang",
          "role": "Ketua Operasi E-dagang"
        }
      }
    }
  },
  {
    slug: 'academic-research-latex',
    locales: {
      zh: {
        industry: '教育 / 学术研究',
        title: '高校教学：在 SheetMate 中展示公式与评分数据',
        subtitle: '用 Markdown + LaTeX 呈现课堂讲义，同时核对 JSON 成绩',
        excerpt: '高校讲师通过 SheetMate 呈现课程说明、公式与学生成绩，实现教学内容与数据的一体化。',
        keywords: ['SheetMate', '教育', 'LaTeX', '教学管理', 'JSON 成绩'],
        readingTime: '6 分钟',
        heroEyebrow: 'Academic Toolkit',
        metaTitle: 'SheetMate 教育 Use Case｜课堂讲义与评分并行',
        metaDescription: '了解高校如何借助 SheetMate 将 Markdown 与 LaTeX 讲义、JSON 成绩与课堂资源结合。',
        imagePlaceholder: {
          caption: '示意图：课程讲义 Markdown 与评分 JSON 分屏',
          alt: 'SheetMate 教学视图占位图'
        },
        sections: [
          {
            title: '教学与科研的文档碎片',
            paragraphs: [
              '高校讲师在 Google Sheets 中管理作业评分、实验参数以及课堂讲义，希望快速查看公式与说明。',
              '传统方式需要在 Sheets 与 LaTeX 编辑器之间复制，格式经常丢失。'
            ]
          },
          {
            title: 'SheetMate 呈现 Markdown 与 LaTeX',
            paragraphs: [
              '讲师把课程说明写成 Markdown，并嵌入 \\(\\LaTeX\\) 公式，SheetMate 即时渲染，便于在课堂上展示。',
              '评分结果保存在 JSON 中，SheetMate 的 JSON 视图帮助助教核对学生成绩。',
              '通过水平分屏，一侧显示作业 Rubric Markdown，另一侧展示学生得分 JSON，方便评分会议。'
            ]
          },
          {
            title: '知识共享更高效',
            paragraphs: [
              '团队将教学日历链接粘贴到单元格，iframe 视图可以直接查看学校 LMS 页面。',
              '单元格定位提醒让助教在批改时准确引用哪份作业。'
            ]
          }
        ],
        workflow: [
          {
            title: '撰写课程说明',
            description: '在单元格中编写 Markdown 与 LaTeX，用于课堂讲义。'
          },
          {
            title: '核对评分 JSON',
            description: '展开 JSON 结构，确认评分标准与备注。'
          },
          {
            title: '分屏回顾 Rubric 与得分',
            description: '让 Rubric 与学生成绩同时呈现，便于讨论。'
          },
          {
            title: '分享教学资源',
            description: '截图或共享 SheetMate 视图给学生，确保指引一致。'
          }
        ],
        highlights: [
          'MarkdownWithLatex 支持数学公式实时渲染。',
          'JSON 视图帮助核查评分数据。',
          '分屏模式方便课堂演示与助教协作。'
        ],
        metrics: [
          {
            value: '90%',
            label: '课堂准备效率',
            context: '讲师准备讲义所需时间显著减少。'
          },
          {
            value: '0',
            label: '公式格式错误',
            context: 'LaTeX 直接渲染避免复制错误。'
          },
          {
            value: '15',
            label: '课程周数',
            context: '一份模板覆盖整个学期。'
          }
        ],
        quote: {
          text: '我可以在 SheetMate 里同时展示公式推导与学生成绩，课堂反馈更即时。',
          author: 'Professor Chen',
          role: '应用数学讲师'
        }
      },
      en: {
        industry: 'Education / Academic Research',
        title: 'University Teaching: Present Formulas and Grades in One Place',
        subtitle: 'Use Markdown, LaTeX, and JSON views to run class and grading from a single sheet',
        excerpt: 'Lecturers rely on SheetMate to display lecture content, formulas, and grading data together so prep and reviews stay smooth.',
        keywords: ['SheetMate', 'Education', 'LaTeX', 'Teaching management', 'JSON grades'],
        readingTime: '6 min read',
        heroEyebrow: 'Academic Toolkit',
        metaTitle: 'SheetMate Education Use Case｜Lecture Notes and Grading in Sync',
        metaDescription: 'See how universities pair Markdown + LaTeX lecture notes with JSON grading data using SheetMate for a single source of truth.',
        imagePlaceholder: {
          caption: 'Mockup: split view showing lecture Markdown with LaTeX next to grading JSON',
          alt: 'SheetMate split view for academic teaching'
        },
        sections: [
          {
            title: 'Why course materials felt scattered',
            paragraphs: [
              'Instructors juggle assignments, lab parameters, and lecture notes in Google Sheets and still need quick access to formulas and explanations.',
              'Switching between Sheets, LaTeX editors, and LMS tools led to formatting issues and wasted prep time.'
            ]
          },
          {
            title: 'SheetMate renders everything in place',
            paragraphs: [
              'Lecture outlines written in Markdown with embedded \\(\\LaTeX\\) render instantly for classroom projection.',
              'Grades stored as JSON open in SheetMate’s tree view so teaching assistants can verify points and feedback quickly.',
              'A split layout keeps rubrics on one side and student scores on the other, making grading meetings more efficient.'
            ]
          },
          {
            title: 'Course resources stay aligned',
            paragraphs: [
              'Instructors paste LMS calendar links into cells so the iframe preview opens the schedule without new tabs.',
              'Cell location hints show TAs the exact assignment row that needs attention.'
            ]
          }
        ],
        workflow: [
          {
            title: 'Draft lecture notes',
            description: 'Write Markdown and LaTeX directly in the sheet so slides and references are always available.'
          },
          {
            title: 'Verify grading JSON',
            description: 'Expand JSON structures to double-check scoring criteria and comments.'
          },
          {
            title: 'Review rubrics with scores',
            description: 'Compare grading rubrics and student results in split view for collaborative grading.'
          },
          {
            title: 'Share consistent guidance',
            description: 'Capture SheetMate views for students and TAs so instructions never get lost.'
          }
        ],
        highlights: [
          'MarkdownWithLatex renders mathematical notation on the fly.',
          'JSON view keeps grading data transparent and accurate.',
          'Split view supports classroom projection and TA collaboration.'
        ],
        metrics: [
          {
            value: '90%',
            label: 'Prep efficiency',
            context: 'Lecture prep time dropped substantially once notes and data lived together.'
          },
          {
            value: '0',
            label: 'Formula formatting errors',
            context: 'Direct LaTeX rendering eliminated copy-and-paste mistakes.'
          },
          {
            value: '15',
            label: 'Weeks covered',
            context: 'One template now supports the entire semester.'
          }
        ],
        quote: {
          text: 'I can project the derivation and student scores at the same time—class discussions move a lot faster now.',
          author: 'Professor Chen',
          role: 'Applied Mathematics Lecturer'
        }
      },
      ja: {
        "industry": "教育 / 学術研究",
        "title": "大学の授業：数式と成績を一つの画面でまとめて提示",
        "subtitle": "Markdown・LaTeX・JSON ビューを活用し、授業運営と採点を一枚のシートで完結",
        "excerpt": "講師は SheetMate を使って講義内容、数式、採点データをまとめて表示し、授業準備とレビューをスムーズに進めています。",
        "keywords": [
          "SheetMate",
          "教育 LaTeX",
          "授業管理",
          "成績管理 JSON",
          "講義ノート"
        ],
        "readingTime": "6分で読了",
        "heroEyebrow": "学術向けツールキット",
        "metaTitle": "SheetMate 教育活用事例｜講義ノートと採点をシームレスに同期",
        "metaDescription": "Markdown + LaTeX の講義ノートと JSON の採点データを SheetMate で一元管理し、唯一の信頼できる情報源を実現する方法をご紹介します。",
        "imagePlaceholder": {
          "caption": "モックアップ：LaTeX 付きの講義 Markdown と採点 JSON を並べた分割ビュー",
          "alt": "学術授業向け SheetMate の分割ビュー"
        },
        "sections": [
          {
            "title": "授業教材がバラバラになりがちだった理由",
            "paragraphs": [
              "教員は課題、実験パラメータ、講義ノートを Google Sheets で管理しながら、数式や解説にもすぐにアクセスする必要があります。",
              "Sheets、LaTeX エディタ、LMS ツールを行き来するたびに書式の崩れが起き、準備時間も無駄になっていました。"
            ]
          },
          {
            "title": "SheetMate ならその場ですべてが描画される",
            "paragraphs": [
              "\\(\\LaTeX\\) を埋め込んだ Markdown の講義アウトラインが即座に描画され、そのまま教室で投影できます。",
              "JSON 形式で保存した成績は SheetMate のツリービューで開けるため、TA が点数やフィードバックを素早く確認できます。",
              "分割レイアウトで採点基準を片側、学生のスコアをもう片側に表示でき、採点会議がより効率的になります。"
            ]
          },
          {
            "title": "授業リソースの整合性を維持",
            "paragraphs": [
              "教員が LMS のカレンダーリンクをセルに貼り付けると、iframe プレビューが新しいタブを開かずにスケジュールを表示します。",
              "セルの位置ヒントによって、TA は対応すべき課題の行を正確に把握できます。"
            ]
          }
        ],
        "workflow": [
          {
            "title": "講義ノートを作成する",
            "description": "Markdown と LaTeX をシート内に直接書き込み、スライドや参考資料をいつでも参照できるようにします。"
          },
          {
            "title": "採点 JSON を確認する",
            "description": "JSON 構造を展開し、採点基準やコメントを二重チェックします。"
          },
          {
            "title": "採点基準とスコアを照合する",
            "description": "採点基準と学生の結果を分割ビューで比較し、共同採点を進めます。"
          },
          {
            "title": "一貫した指針を共有する",
            "description": "SheetMate のビューを保存して学生や TA に共有し、指示が抜け落ちないようにします。"
          }
        ],
        "highlights": [
          "MarkdownWithLatex が数式表記をリアルタイムで描画します。",
          "JSON ビューで採点データの透明性と正確性を保ちます。",
          "分割ビューが教室投影と TA の共同作業をサポートします。"
        ],
        "metrics": [
          {
            "value": "90%",
            "label": "準備効率",
            "context": "ノートとデータを一つにまとめたことで、講義準備の時間が大幅に短縮されました。"
          },
          {
            "value": "0",
            "label": "数式の書式エラー",
            "context": "LaTeX を直接描画することで、コピー＆ペーストのミスがなくなりました。"
          },
          {
            "value": "15",
            "label": "カバーする週数",
            "context": "一つのテンプレートで学期全体を支えられるようになりました。"
          }
        ],
        "quote": {
          "text": "数式の導出と学生のスコアを同時に投影できるので、授業中の議論がずっとスムーズに進むようになりました。",
          "author": "Professor Chen",
          "role": "応用数学の講師"
        }
      },
      id: {
        "industry": "Pendidikan / Riset Akademik",
        "title": "Mengajar di Kampus: Tampilkan Rumus dan Nilai dalam Satu Tempat",
        "subtitle": "Manfaatkan tampilan Markdown, LaTeX, dan JSON untuk menjalankan kelas serta penilaian dari satu sheet",
        "excerpt": "Para dosen mengandalkan SheetMate untuk menampilkan materi kuliah, rumus, dan data penilaian secara bersamaan agar persiapan dan evaluasi tetap lancar.",
        "keywords": [
          "SheetMate",
          "Pendidikan LaTeX",
          "Manajemen pengajaran",
          "Nilai JSON",
          "Catatan kuliah"
        ],
        "readingTime": "Baca 6 menit",
        "heroEyebrow": "Perangkat Akademik",
        "metaTitle": "Studi Kasus Pendidikan SheetMate｜Catatan Kuliah dan Penilaian Selalu Sinkron",
        "metaDescription": "Simak bagaimana kampus memadukan catatan kuliah Markdown + LaTeX dengan data penilaian JSON menggunakan SheetMate sebagai satu sumber kebenaran.",
        "imagePlaceholder": {
          "caption": "Mockup: tampilan terbagi yang menampilkan Markdown kuliah dengan LaTeX di sebelah JSON penilaian",
          "alt": "Tampilan terbagi SheetMate untuk pengajaran akademik"
        },
        "sections": [
          {
            "title": "Mengapa materi kuliah terasa tercerai-berai",
            "paragraphs": [
              "Pengajar mengelola tugas, parameter lab, dan catatan kuliah di Google Sheets, namun tetap perlu akses cepat ke rumus dan penjelasannya.",
              "Berpindah-pindah antara Sheets, editor LaTeX, dan perangkat LMS memicu masalah format dan membuang waktu persiapan."
            ]
          },
          {
            "title": "SheetMate menampilkan semuanya langsung di tempat",
            "paragraphs": [
              "Kerangka kuliah yang ditulis dalam Markdown dengan \\(\\LaTeX\\) tersemat langsung ditampilkan untuk proyeksi di kelas.",
              "Nilai yang disimpan sebagai JSON terbuka dalam tampilan pohon SheetMate sehingga asisten dosen bisa memverifikasi poin dan masukan dengan cepat.",
              "Tata letak terbagi menempatkan rubrik di satu sisi dan nilai mahasiswa di sisi lain, membuat rapat penilaian lebih efisien."
            ]
          },
          {
            "title": "Sumber daya mata kuliah tetap selaras",
            "paragraphs": [
              "Pengajar menempelkan tautan kalender LMS ke dalam sel sehingga pratinjau iframe membuka jadwal tanpa tab baru.",
              "Petunjuk lokasi sel menunjukkan kepada asisten dosen baris tugas tepat yang perlu diperhatikan."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Susun catatan kuliah",
            "description": "Tulis Markdown dan LaTeX langsung di sheet agar slide dan referensi selalu tersedia."
          },
          {
            "title": "Verifikasi JSON penilaian",
            "description": "Bentangkan struktur JSON untuk memeriksa ulang kriteria penilaian dan komentar."
          },
          {
            "title": "Tinjau rubrik beserta nilai",
            "description": "Bandingkan rubrik penilaian dan hasil mahasiswa dalam tampilan terbagi untuk penilaian bersama."
          },
          {
            "title": "Bagikan panduan yang konsisten",
            "description": "Tangkap tampilan SheetMate untuk mahasiswa dan asisten dosen agar instruksi tidak pernah hilang."
          }
        ],
        "highlights": [
          "MarkdownWithLatex menampilkan notasi matematika secara real-time.",
          "Tampilan JSON menjaga data penilaian tetap transparan dan akurat.",
          "Tampilan terbagi mendukung proyeksi kelas dan kolaborasi asisten dosen."
        ],
        "metrics": [
          {
            "value": "90%",
            "label": "Efisiensi persiapan",
            "context": "Waktu persiapan kuliah turun drastis setelah catatan dan data berada di satu tempat."
          },
          {
            "value": "0",
            "label": "Kesalahan format rumus",
            "context": "Penampilan LaTeX secara langsung menghilangkan kesalahan salin-tempel."
          },
          {
            "value": "15",
            "label": "Minggu yang tercakup",
            "context": "Satu templat kini mendukung seluruh satu semester."
          }
        ],
        "quote": {
          "text": "Saya bisa memproyeksikan penurunan rumus dan nilai mahasiswa secara bersamaan—diskusi di kelas jadi jauh lebih cepat sekarang.",
          "author": "Professor Chen",
          "role": "Dosen Matematika Terapan"
        }
      },
      es: {
        "industry": "Educación / Investigación académica",
        "title": "Docencia universitaria: presenta fórmulas y calificaciones en un solo lugar",
        "subtitle": "Usa las vistas de Markdown, LaTeX y JSON para gestionar la clase y la evaluación desde una sola hoja",
        "excerpt": "Los profesores confían en SheetMate para mostrar juntos el contenido de la clase, las fórmulas y los datos de evaluación, de modo que la preparación y las revisiones fluyan sin tropiezos.",
        "keywords": [
          "SheetMate",
          "Educación LaTeX",
          "Gestión docente",
          "Calificaciones JSON",
          "Apuntes de clase"
        ],
        "readingTime": "6 min de lectura",
        "heroEyebrow": "Kit académico",
        "metaTitle": "Caso de uso educativo de SheetMate｜Apuntes y calificaciones siempre sincronizados",
        "metaDescription": "Descubre cómo las universidades combinan apuntes en Markdown + LaTeX con datos de calificación en JSON usando SheetMate como única fuente de verdad.",
        "imagePlaceholder": {
          "caption": "Mockup: vista dividida que muestra el Markdown de la clase con LaTeX junto al JSON de calificaciones",
          "alt": "Vista dividida de SheetMate para la docencia académica"
        },
        "sections": [
          {
            "title": "Por qué los materiales del curso se sentían dispersos",
            "paragraphs": [
              "Los docentes manejan tareas, parámetros de laboratorio y apuntes de clase en Google Sheets, y aun así necesitan acceso rápido a las fórmulas y sus explicaciones.",
              "Cambiar entre Sheets, editores de LaTeX y herramientas del LMS generaba problemas de formato y desperdiciaba tiempo de preparación."
            ]
          },
          {
            "title": "SheetMate lo renderiza todo en el momento",
            "paragraphs": [
              "Los esquemas de clase escritos en Markdown con \\(\\LaTeX\\) incrustado se renderizan al instante para proyectarlos en el aula.",
              "Las calificaciones guardadas como JSON se abren en la vista de árbol de SheetMate, así los auxiliares pueden verificar puntos y comentarios rápidamente.",
              "Un diseño dividido mantiene las rúbricas de un lado y las calificaciones de los alumnos del otro, lo que hace más eficientes las reuniones de evaluación."
            ]
          },
          {
            "title": "Los recursos del curso se mantienen alineados",
            "paragraphs": [
              "Los docentes pegan los enlaces del calendario del LMS en las celdas, así la vista previa en iframe abre el calendario sin nuevas pestañas.",
              "Las pistas de ubicación de celda muestran a los auxiliares la fila exacta de la tarea que necesita atención."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Redacta los apuntes de clase",
            "description": "Escribe Markdown y LaTeX directamente en la hoja para tener siempre a mano las diapositivas y las referencias."
          },
          {
            "title": "Verifica el JSON de calificaciones",
            "description": "Despliega las estructuras JSON para revisar dos veces los criterios de puntuación y los comentarios."
          },
          {
            "title": "Revisa las rúbricas junto con las calificaciones",
            "description": "Compara las rúbricas de evaluación y los resultados de los alumnos en vista dividida para calificar en equipo."
          },
          {
            "title": "Comparte una guía consistente",
            "description": "Captura las vistas de SheetMate para alumnos y auxiliares, así las instrucciones nunca se pierden."
          }
        ],
        "highlights": [
          "MarkdownWithLatex renderiza la notación matemática al instante.",
          "La vista JSON mantiene los datos de calificación transparentes y precisos.",
          "La vista dividida apoya la proyección en el aula y la colaboración de los auxiliares."
        ],
        "metrics": [
          {
            "value": "90%",
            "label": "Eficiencia en la preparación",
            "context": "El tiempo de preparación de clase bajó notablemente al tener los apuntes y los datos juntos."
          },
          {
            "value": "0",
            "label": "Errores de formato en fórmulas",
            "context": "Renderizar LaTeX directamente eliminó los errores de copiar y pegar."
          },
          {
            "value": "15",
            "label": "Semanas cubiertas",
            "context": "Una sola plantilla ahora cubre todo el semestre."
          }
        ],
        "quote": {
          "text": "Puedo proyectar la deducción y las calificaciones de los alumnos al mismo tiempo: las discusiones en clase ahora avanzan mucho más rápido.",
          "author": "Professor Chen",
          "role": "Profesor de Matemáticas Aplicadas"
        }
      },
      ms: {
        "industry": "Pendidikan / Penyelidikan Akademik",
        "title": "Pengajaran Universiti: Paparkan Formula dan Markah di Satu Tempat",
        "subtitle": "Gunakan paparan Markdown, LaTeX dan JSON untuk mengendalikan kelas serta penilaian daripada satu helaian",
        "excerpt": "Para pensyarah bergantung pada SheetMate untuk memaparkan kandungan kuliah, formula dan data penilaian secara serentak supaya persediaan dan semakan kekal lancar.",
        "keywords": [
          "SheetMate",
          "Pendidikan LaTeX",
          "Pengurusan pengajaran",
          "Markah JSON",
          "Nota kuliah"
        ],
        "readingTime": "Bacaan 6 minit",
        "heroEyebrow": "Kit Akademik",
        "metaTitle": "Kes Penggunaan Pendidikan SheetMate｜Nota Kuliah dan Penilaian Sentiasa Segerak",
        "metaDescription": "Lihat cara universiti menggabungkan nota kuliah Markdown + LaTeX dengan data penilaian JSON menggunakan SheetMate sebagai satu sumber kebenaran.",
        "imagePlaceholder": {
          "caption": "Mockup: paparan terbahagi yang menunjukkan Markdown kuliah dengan LaTeX di sebelah JSON penilaian",
          "alt": "Paparan terbahagi SheetMate untuk pengajaran akademik"
        },
        "sections": [
          {
            "title": "Mengapa bahan kursus terasa berselerak",
            "paragraphs": [
              "Pengajar menguruskan tugasan, parameter makmal dan nota kuliah dalam Google Sheets, namun masih memerlukan akses pantas kepada formula dan penerangannya.",
              "Bertukar antara Sheets, penyunting LaTeX dan alat LMS menyebabkan masalah pemformatan dan membazirkan masa persediaan."
            ]
          },
          {
            "title": "SheetMate memaparkan semuanya di tempatnya",
            "paragraphs": [
              "Rangka kuliah yang ditulis dalam Markdown dengan \\(\\LaTeX\\) terbenam dipaparkan serta-merta untuk unjuran dalam kelas.",
              "Markah yang disimpan sebagai JSON dibuka dalam paparan pokok SheetMate supaya pembantu pengajar dapat mengesahkan mata dan maklum balas dengan pantas.",
              "Susun atur terbahagi mengekalkan rubrik di satu sisi dan markah pelajar di sisi lain, menjadikan mesyuarat penilaian lebih cekap."
            ]
          },
          {
            "title": "Sumber kursus kekal selari",
            "paragraphs": [
              "Pengajar menampal pautan kalendar LMS ke dalam sel supaya pratonton iframe membuka jadual tanpa tab baharu.",
              "Petunjuk lokasi sel menunjukkan kepada pembantu pengajar baris tugasan tepat yang memerlukan perhatian."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Rangka nota kuliah",
            "description": "Tulis Markdown dan LaTeX terus dalam helaian supaya slaid dan rujukan sentiasa tersedia."
          },
          {
            "title": "Sahkan JSON penilaian",
            "description": "Kembangkan struktur JSON untuk menyemak semula kriteria pemarkahan dan komen."
          },
          {
            "title": "Semak rubrik bersama markah",
            "description": "Bandingkan rubrik penilaian dan keputusan pelajar dalam paparan terbahagi untuk pemarkahan bersama."
          },
          {
            "title": "Kongsi panduan yang konsisten",
            "description": "Tangkap paparan SheetMate untuk pelajar dan pembantu pengajar supaya arahan tidak pernah hilang."
          }
        ],
        "highlights": [
          "MarkdownWithLatex memaparkan notasi matematik secara langsung.",
          "Paparan JSON memastikan data penilaian kekal telus dan tepat.",
          "Paparan terbahagi menyokong unjuran kelas dan kerjasama pembantu pengajar."
        ],
        "metrics": [
          {
            "value": "90%",
            "label": "Kecekapan persediaan",
            "context": "Masa persediaan kuliah menurun dengan ketara apabila nota dan data berada bersama."
          },
          {
            "value": "0",
            "label": "Ralat pemformatan formula",
            "context": "Pemaparan LaTeX secara langsung menghapuskan kesilapan salin dan tampal."
          },
          {
            "value": "15",
            "label": "Minggu yang diliputi",
            "context": "Satu templat kini menyokong keseluruhan semester."
          }
        ],
        "quote": {
          "text": "Saya boleh mengunjurkan penerbitan formula dan markah pelajar pada masa yang sama—perbincangan kelas kini bergerak jauh lebih pantas.",
          "author": "Professor Chen",
          "role": "Pensyarah Matematik Gunaan"
        }
      }
    }
  },
  {
    slug: 'supply-chain-sop-control',
    locales: {
      zh: {
        industry: '供应链 / 运营计划',
        title: '供应链 S&OP：统一预测、排产与行动项',
        subtitle: '在 SheetMate 中整合 JSON 指标、iframe 看板与 Markdown 行动列表',
        excerpt: '供应链团队使用 SheetMate 将需求预测、排产看板与会议纪要统一起来，加速决策闭环。',
        keywords: ['SheetMate', '供应链', 'S&OP', '排产', '行动项管理'],
        readingTime: '7 分钟',
        heroEyebrow: 'Supply Chain',
        metaTitle: 'SheetMate 供应链 Use Case｜S&OP 决策与执行同步',
        metaDescription: '了解供应链团队如何利用 SheetMate 在 Google Sheets 中同步预测 JSON、排产 iframe 与 Markdown 行动列表。',
        imagePlaceholder: {
          caption: '示意图：需求预测 JSON 与排产 iframe 垂直分屏',
          alt: 'SheetMate 供应链视图占位图'
        },
        sections: [
          {
            title: 'S&OP 会议的信息割裂',
            paragraphs: [
              '一家制造企业的供应链团队每周要在 Google Sheets 中整合需求预测、生产计划与库存健康度。',
              '不同来源的数据格式不一，导致会议讨论时需要频繁切换到 ERP 仪表盘或邮件。'
            ]
          },
          {
            title: 'SheetMate 连接多源信息',
            paragraphs: [
              '团队把预测模型输出的 JSON 粘贴到单元格，SheetMate 自动格式化，供应链经理可以展开查看各区域需求。',
              '将工厂排产看板 URL 粘贴后，iframe 预览让团队在同一屏查看滚动排期。',
              'Markdown 记录行动项、风险与缓解计划，通过垂直分屏同时展示指标与决策记录。'
            ]
          },
          {
            title: '决策链路更清晰',
            paragraphs: [
              'SheetMate 的单元格定位帮助团队在复盘时迅速回到对应 SKU 或工厂的原始行。',
              '会议结束后通过 Markdown 任务列表导出，发送给采购、生产与销售，确保执行。'
            ]
          }
        ],
        workflow: [
          {
            title: '汇集预测数据',
            description: '将 ERP 与需求模型的输出写入表格，并保持 JSON 格式。'
          },
          {
            title: '嵌入排产看板',
            description: '把工厂或物流系统的网页链接粘贴到单元格，生成实时 iframe。'
          },
          {
            title: '分屏记录行动项',
            description: '左侧查看指标，右侧实时更新 Markdown 行动列表。'
          },
          {
            title: '同步多方执行',
            description: '导出或截图 SheetMate 视图分享到项目频道，实现跨团队协同。'
          }
        ],
        highlights: [
          'JSON 展示区域与 SKU 细分数据。',
          'iframe 预览排产与库存仪表盘。',
          'Markdown 行动列表形成闭环。'
        ],
        metrics: [
          {
            value: '25%',
            label: '会议时间缩短',
            context: 'S&OP 例会从 80 分钟缩至 60 分钟。'
          },
          {
            value: '100%',
            label: '行动项追踪率',
            context: 'Markdown 任务列表实现全员闭环。'
          },
          {
            value: '5',
            label: '关键系统联动',
            context: 'SheetMate 中同时查看 ERP、WMS、BI、采购平台与邮件摘要。'
          }
        ],
        quote: {
          text: '我们把供应链的关键指标、行动项和系统链接整合在 SheetMate 中，周会流程终于顺畅了。',
          author: 'Michael Du',
          role: '供应链运营总监'
        }
      },
      en: {
        industry: 'Supply Chain / S&OP',
        title: 'Supply Chain S&OP: Unite Forecasts, Schedules, and Actions',
        subtitle: 'Review JSON metrics, embedded dashboards, and action lists side by side',
        excerpt: 'Supply chain teams use SheetMate to bring demand forecasts, production plans, and meeting notes together so decisions turn into execution faster.',
        keywords: ['SheetMate', 'Supply chain', 'S&OP', 'Production planning', 'Action tracking'],
        readingTime: '7 min read',
        heroEyebrow: 'Supply Chain',
        metaTitle: 'SheetMate Supply Chain Use Case｜Sync S&OP Decisions and Execution',
        metaDescription: 'Discover how supply chain teams align forecasts, dashboards, and action plans inside SheetMate to keep S&OP meetings focused.',
        imagePlaceholder: {
          caption: 'Mockup: vertical split with forecast JSON on the left and a production dashboard on the right',
          alt: 'SheetMate split view for S&OP reviews'
        },
        sections: [
          {
            title: 'Too many systems in one meeting',
            paragraphs: [
              'The S&OP team blended demand forecasts, factory schedules, and inventory health inside Google Sheets each week.',
              'Every review meant flipping between ERP dashboards, email threads, and spreadsheets, which slowed decisions.'
            ]
          },
          {
            title: 'SheetMate pulls the pieces together',
            paragraphs: [
              'Forecast outputs pasted as JSON are formatted automatically, letting planners drill into regional demand with a click.',
              'Factory scheduling boards embed as iframes so the latest plan stays visible without leaving the sheet.',
              'Markdown action lists capture risks, owners, and due dates in the same view as the metrics driving the discussion.'
            ]
          },
          {
            title: 'Execution stays aligned',
            paragraphs: [
              'Cell location markers make it easy to revisit specific SKUs or plants during follow-up conversations.',
              'After the call, teams export Markdown task lists to procurement, manufacturing, and sales so everyone knows the next move.'
            ]
          }
        ],
        workflow: [
          {
            title: 'Gather forecast data',
            description: 'Write ERP and demand model outputs into the sheet as JSON for instant inspection.'
          },
          {
            title: 'Embed scheduling boards',
            description: 'Paste production or logistics dashboards into cells to generate live iframes.'
          },
          {
            title: 'Track actions in split view',
            description: 'Compare metrics on one side and update Markdown action lists on the other.'
          },
          {
            title: 'Coordinate execution',
            description: 'Share the SheetMate view or screenshots with every stakeholder channel.'
          }
        ],
        highlights: [
          'JSON view exposes regional and SKU-level details instantly.',
          'Iframe previews keep production and inventory dashboards within reach.',
          'Markdown action lists maintain accountability loops after the meeting.'
        ],
        metrics: [
          {
            value: '25%',
            label: 'Meeting time saved',
            context: 'Weekly S&OP reviews now wrap in about an hour instead of eighty minutes.'
          },
          {
            value: '100%',
            label: 'Action tracking',
            context: 'Every follow-up is captured in Markdown and handed to the owning team.'
          },
          {
            value: '5',
            label: 'Systems consolidated',
            context: 'ERP, WMS, BI, purchasing, and email summaries now live in one SheetMate view.'
          }
        ],
        quote: {
          text: 'We brought metrics, dashboards, and follow-ups into one SheetMate view—our weekly S&OP finally runs like clockwork.',
          author: 'Michael Du',
          role: 'Director of Supply Chain Operations'
        }
      },
      ja: {
        "industry": "サプライチェーン / S&OP",
        "title": "サプライチェーンS&OP：予測・計画・アクションを一つに統合",
        "subtitle": "JSON指標、埋め込みダッシュボード、アクションリストを並べて確認",
        "excerpt": "サプライチェーンチームはSheetMateで需要予測、生産計画、会議メモを一つにまとめ、意思決定を素早く実行へとつなげています。",
        "keywords": [
          "SheetMate",
          "サプライチェーン",
          "S&OP",
          "生産計画",
          "アクション管理"
        ],
        "readingTime": "7分で読了",
        "heroEyebrow": "サプライチェーン",
        "metaTitle": "SheetMate サプライチェーン活用事例｜S&OPの意思決定と実行を同期",
        "metaDescription": "サプライチェーンチームがSheetMate内で予測、ダッシュボード、アクションプランを揃え、S&OP会議の焦点を保つ方法をご紹介します。",
        "imagePlaceholder": {
          "caption": "モックアップ：左に予測JSON、右に生産ダッシュボードを配置した縦分割ビュー",
          "alt": "S&OPレビュー向けSheetMate分割ビュー"
        },
        "sections": [
          {
            "title": "一つの会議に多すぎるシステム",
            "paragraphs": [
              "S&OPチームは毎週、需要予測、工場スケジュール、在庫状況をGoogle Sheets上でまとめていました。",
              "レビューのたびにERPダッシュボード、メールスレッド、スプレッドシートを行き来する必要があり、意思決定が滞っていました。"
            ]
          },
          {
            "title": "SheetMateが各要素を一つにまとめる",
            "paragraphs": [
              "JSONとして貼り付けた予測結果は自動で整形され、プランナーはクリック一つで地域別の需要を掘り下げられます。",
              "工場スケジュールのボードはiframeとして埋め込まれ、シートを離れることなく最新の計画を常に確認できます。",
              "Markdownのアクションリストが、議論を支える指標と同じビュー内でリスク、担当者、期限を記録します。"
            ]
          },
          {
            "title": "実行のズレを防ぐ",
            "paragraphs": [
              "セル位置のマーカーにより、フォローアップの会話で特定のSKUや工場をすぐに見返せます。",
              "通話後、チームはMarkdownのタスクリストを調達、製造、営業へエクスポートし、全員が次の動きを把握できます。"
            ]
          }
        ],
        "workflow": [
          {
            "title": "予測データを集める",
            "description": "ERPと需要モデルの出力をJSONとしてシートに書き込み、その場で確認できるようにします。"
          },
          {
            "title": "スケジュールボードを埋め込む",
            "description": "生産や物流のダッシュボードをセルに貼り付け、ライブなiframeを生成します。"
          },
          {
            "title": "分割ビューでアクションを管理",
            "description": "片側で指標を比較し、もう片側でMarkdownのアクションリストを更新します。"
          },
          {
            "title": "実行を調整する",
            "description": "SheetMateのビューやスクリーンショットを各関係者チャネルに共有します。"
          }
        ],
        "highlights": [
          "JSONビューで地域別・SKU別の詳細を瞬時に表示。",
          "iframeプレビューで生産・在庫ダッシュボードをすぐ手元に。",
          "Markdownのアクションリストが会議後も責任のループを維持。"
        ],
        "metrics": [
          {
            "value": "25%",
            "label": "会議時間の短縮",
            "context": "毎週のS&OPレビューは80分から約1時間で完了するようになりました。"
          },
          {
            "value": "100%",
            "label": "アクション追跡",
            "context": "すべてのフォローアップがMarkdownに記録され、担当チームへ引き渡されます。"
          },
          {
            "value": "5",
            "label": "統合したシステム数",
            "context": "ERP、WMS、BI、購買、メール要約が一つのSheetMateビューに集約されました。"
          }
        ],
        "quote": {
          "text": "指標、ダッシュボード、フォローアップを一つのSheetMateビューにまとめたことで、毎週のS&OPがようやく時計のように正確に回るようになりました。",
          "author": "Michael Du",
          "role": "サプライチェーンオペレーション ディレクター"
        }
      },
      id: {
        "industry": "Rantai Pasok / S&OP",
        "title": "S&OP Rantai Pasok: Satukan Prakiraan, Jadwal, dan Tindakan",
        "subtitle": "Tinjau metrik JSON, dasbor tertanam, dan daftar tindakan secara berdampingan",
        "excerpt": "Tim rantai pasok memakai SheetMate untuk menyatukan prakiraan permintaan, rencana produksi, dan catatan rapat agar keputusan lebih cepat menjadi eksekusi.",
        "keywords": [
          "SheetMate",
          "Rantai pasok",
          "S&OP",
          "Perencanaan produksi",
          "Pelacakan tindakan"
        ],
        "readingTime": "Baca 7 menit",
        "heroEyebrow": "Rantai Pasok",
        "metaTitle": "Studi Kasus Rantai Pasok SheetMate｜Selaraskan Keputusan dan Eksekusi S&OP",
        "metaDescription": "Lihat bagaimana tim rantai pasok menyelaraskan prakiraan, dasbor, dan rencana tindakan di dalam SheetMate agar rapat S&OP tetap fokus.",
        "imagePlaceholder": {
          "caption": "Mockup: pembagian vertikal dengan JSON prakiraan di kiri dan dasbor produksi di kanan",
          "alt": "Tampilan terbagi SheetMate untuk tinjauan S&OP"
        },
        "sections": [
          {
            "title": "Terlalu banyak sistem dalam satu rapat",
            "paragraphs": [
              "Setiap minggu tim S&OP memadukan prakiraan permintaan, jadwal pabrik, dan kondisi inventaris di dalam Google Sheets.",
              "Setiap tinjauan menuntut perpindahan antara dasbor ERP, utas email, dan spreadsheet, sehingga keputusan menjadi lambat."
            ]
          },
          {
            "title": "SheetMate menyatukan semua bagian",
            "paragraphs": [
              "Hasil prakiraan yang ditempel sebagai JSON langsung diformat otomatis, sehingga perencana bisa menelusuri permintaan per wilayah hanya dengan sekali klik.",
              "Papan penjadwalan pabrik tertanam sebagai iframe sehingga rencana terbaru tetap terlihat tanpa harus meninggalkan sheet.",
              "Daftar tindakan Markdown mencatat risiko, penanggung jawab, dan tenggat di tampilan yang sama dengan metrik yang menjadi dasar diskusi."
            ]
          },
          {
            "title": "Eksekusi tetap selaras",
            "paragraphs": [
              "Penanda lokasi sel memudahkan untuk meninjau kembali SKU atau pabrik tertentu saat percakapan lanjutan.",
              "Setelah panggilan, tim mengekspor daftar tugas Markdown ke bagian pengadaan, manufaktur, dan penjualan agar semua tahu langkah berikutnya."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Kumpulkan data prakiraan",
            "description": "Tulis keluaran ERP dan model permintaan ke dalam sheet sebagai JSON untuk pemeriksaan instan."
          },
          {
            "title": "Tanamkan papan penjadwalan",
            "description": "Tempel dasbor produksi atau logistik ke dalam sel untuk menghasilkan iframe langsung."
          },
          {
            "title": "Lacak tindakan dalam tampilan terbagi",
            "description": "Bandingkan metrik di satu sisi dan perbarui daftar tindakan Markdown di sisi lain."
          },
          {
            "title": "Koordinasikan eksekusi",
            "description": "Bagikan tampilan SheetMate atau tangkapan layar ke setiap saluran pemangku kepentingan."
          }
        ],
        "highlights": [
          "Tampilan JSON menyajikan detail per wilayah dan per SKU secara instan.",
          "Pratinjau iframe menjaga dasbor produksi dan inventaris tetap mudah dijangkau.",
          "Daftar tindakan Markdown mempertahankan lingkaran akuntabilitas setelah rapat."
        ],
        "metrics": [
          {
            "value": "25%",
            "label": "Waktu rapat dihemat",
            "context": "Tinjauan S&OP mingguan kini selesai sekitar satu jam, bukan delapan puluh menit."
          },
          {
            "value": "100%",
            "label": "Pelacakan tindakan",
            "context": "Setiap tindak lanjut tercatat dalam Markdown dan diserahkan kepada tim penanggung jawab."
          },
          {
            "value": "5",
            "label": "Sistem dikonsolidasikan",
            "context": "ERP, WMS, BI, pembelian, dan ringkasan email kini berada dalam satu tampilan SheetMate."
          }
        ],
        "quote": {
          "text": "Kami menyatukan metrik, dasbor, dan tindak lanjut dalam satu tampilan SheetMate—rapat S&OP mingguan kami akhirnya berjalan sangat teratur.",
          "author": "Michael Du",
          "role": "Direktur Operasi Rantai Pasok"
        }
      },
      es: {
        "industry": "Cadena de Suministro / S&OP",
        "title": "S&OP de cadena de suministro: une pronósticos, programas y acciones",
        "subtitle": "Revisa métricas JSON, tableros incrustados y listas de acciones lado a lado",
        "excerpt": "Los equipos de cadena de suministro usan SheetMate para reunir pronósticos de demanda, planes de producción y notas de reunión, de modo que las decisiones se conviertan en ejecución más rápido.",
        "keywords": [
          "SheetMate",
          "Cadena de suministro",
          "S&OP",
          "Planeación de producción",
          "Seguimiento de acciones"
        ],
        "readingTime": "7 min de lectura",
        "heroEyebrow": "Cadena de Suministro",
        "metaTitle": "Caso de uso de cadena de suministro de SheetMate｜Sincroniza decisiones y ejecución del S&OP",
        "metaDescription": "Descubre cómo los equipos de cadena de suministro alinean pronósticos, tableros y planes de acción dentro de SheetMate para mantener enfocadas las juntas de S&OP.",
        "imagePlaceholder": {
          "caption": "Mockup: división vertical con el JSON de pronóstico a la izquierda y un tablero de producción a la derecha",
          "alt": "Vista dividida de SheetMate para revisiones de S&OP"
        },
        "sections": [
          {
            "title": "Demasiados sistemas en una sola junta",
            "paragraphs": [
              "Cada semana, el equipo de S&OP combinaba pronósticos de demanda, programas de fábrica y el estado del inventario dentro de Google Sheets.",
              "Cada revisión implicaba saltar entre tableros del ERP, cadenas de correo y hojas de cálculo, lo que retrasaba las decisiones."
            ]
          },
          {
            "title": "SheetMate junta todas las piezas",
            "paragraphs": [
              "Los resultados de pronóstico pegados como JSON se formatean de forma automática, lo que permite a los planeadores explorar la demanda regional con un clic.",
              "Los tableros de programación de fábrica se incrustan como iframes para que el plan más reciente siga visible sin salir de la hoja.",
              "Las listas de acciones en Markdown registran riesgos, responsables y fechas límite en la misma vista que las métricas que guían la discusión."
            ]
          },
          {
            "title": "La ejecución se mantiene alineada",
            "paragraphs": [
              "Los marcadores de ubicación de celdas facilitan volver a SKU o plantas específicas durante las conversaciones de seguimiento.",
              "Tras la junta, los equipos exportan listas de tareas en Markdown a compras, manufactura y ventas para que todos sepan el siguiente paso."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Reúne los datos de pronóstico",
            "description": "Escribe los resultados del ERP y del modelo de demanda en la hoja como JSON para revisarlos al instante."
          },
          {
            "title": "Incrusta tableros de programación",
            "description": "Pega tableros de producción o logística en las celdas para generar iframes en vivo."
          },
          {
            "title": "Da seguimiento a las acciones en vista dividida",
            "description": "Compara las métricas de un lado y actualiza las listas de acciones en Markdown del otro."
          },
          {
            "title": "Coordina la ejecución",
            "description": "Comparte la vista de SheetMate o capturas de pantalla con cada canal de interesados."
          }
        ],
        "highlights": [
          "La vista JSON muestra al instante los detalles por región y por SKU.",
          "Las vistas previas en iframe mantienen a la mano los tableros de producción e inventario.",
          "Las listas de acciones en Markdown sostienen los ciclos de rendición de cuentas después de la junta."
        ],
        "metrics": [
          {
            "value": "25%",
            "label": "Tiempo de junta ahorrado",
            "context": "Las revisiones semanales de S&OP ahora terminan en cerca de una hora en lugar de ochenta minutos."
          },
          {
            "value": "100%",
            "label": "Seguimiento de acciones",
            "context": "Cada seguimiento queda registrado en Markdown y se entrega al equipo responsable."
          },
          {
            "value": "5",
            "label": "Sistemas consolidados",
            "context": "ERP, WMS, BI, compras y resúmenes de correo ahora viven en una sola vista de SheetMate."
          }
        ],
        "quote": {
          "text": "Reunimos métricas, tableros y seguimientos en una sola vista de SheetMate; por fin nuestro S&OP semanal funciona como reloj.",
          "author": "Michael Du",
          "role": "Director de Operaciones de Cadena de Suministro"
        }
      },
      ms: {
        "industry": "Rantaian Bekalan / S&OP",
        "title": "S&OP Rantaian Bekalan: Satukan Ramalan, Jadual dan Tindakan",
        "subtitle": "Semak metrik JSON, papan pemuka terbenam dan senarai tindakan secara bersebelahan",
        "excerpt": "Pasukan rantaian bekalan menggunakan SheetMate untuk menyatukan ramalan permintaan, pelan pengeluaran dan nota mesyuarat supaya keputusan bertukar menjadi pelaksanaan dengan lebih pantas.",
        "keywords": [
          "SheetMate",
          "Rantaian bekalan",
          "S&OP",
          "Perancangan pengeluaran",
          "Penjejakan tindakan"
        ],
        "readingTime": "Bacaan 7 minit",
        "heroEyebrow": "Rantaian Bekalan",
        "metaTitle": "Kes Penggunaan Rantaian Bekalan SheetMate｜Selaraskan Keputusan dan Pelaksanaan S&OP",
        "metaDescription": "Ketahui cara pasukan rantaian bekalan menyelaraskan ramalan, papan pemuka dan pelan tindakan dalam SheetMate untuk memastikan mesyuarat S&OP kekal fokus.",
        "imagePlaceholder": {
          "caption": "Mockup: pembahagian menegak dengan JSON ramalan di sebelah kiri dan papan pemuka pengeluaran di sebelah kanan",
          "alt": "Paparan terbahagi SheetMate untuk semakan S&OP"
        },
        "sections": [
          {
            "title": "Terlalu banyak sistem dalam satu mesyuarat",
            "paragraphs": [
              "Setiap minggu pasukan S&OP menggabungkan ramalan permintaan, jadual kilang dan kesihatan inventori di dalam Google Sheets.",
              "Setiap semakan memerlukan pertukaran antara papan pemuka ERP, rangkaian e-mel dan hamparan, lalu melambatkan keputusan."
            ]
          },
          {
            "title": "SheetMate menyatukan semua bahagian",
            "paragraphs": [
              "Hasil ramalan yang ditampal sebagai JSON diformat secara automatik, membolehkan perancang meneliti permintaan mengikut wilayah dengan satu klik.",
              "Papan penjadualan kilang terbenam sebagai iframe supaya pelan terkini sentiasa kelihatan tanpa perlu meninggalkan sheet.",
              "Senarai tindakan Markdown merekodkan risiko, pemilik dan tarikh akhir dalam paparan yang sama dengan metrik yang memacu perbincangan."
            ]
          },
          {
            "title": "Pelaksanaan kekal selaras",
            "paragraphs": [
              "Penanda lokasi sel memudahkan untuk meninjau semula SKU atau kilang tertentu semasa perbualan susulan.",
              "Selepas panggilan, pasukan mengeksport senarai tugas Markdown ke bahagian perolehan, pembuatan dan jualan supaya semua orang tahu langkah seterusnya."
            ]
          }
        ],
        "workflow": [
          {
            "title": "Kumpulkan data ramalan",
            "description": "Tulis output ERP dan model permintaan ke dalam sheet sebagai JSON untuk pemeriksaan segera."
          },
          {
            "title": "Benamkan papan penjadualan",
            "description": "Tampal papan pemuka pengeluaran atau logistik ke dalam sel untuk menjana iframe secara langsung."
          },
          {
            "title": "Jejak tindakan dalam paparan terbahagi",
            "description": "Bandingkan metrik di satu sisi dan kemas kini senarai tindakan Markdown di sisi lain."
          },
          {
            "title": "Selaraskan pelaksanaan",
            "description": "Kongsi paparan SheetMate atau tangkapan skrin dengan setiap saluran pihak berkepentingan."
          }
        ],
        "highlights": [
          "Paparan JSON memaparkan butiran mengikut wilayah dan SKU dengan serta-merta.",
          "Pratonton iframe memastikan papan pemuka pengeluaran dan inventori sentiasa mudah dicapai.",
          "Senarai tindakan Markdown mengekalkan kitaran akauntabiliti selepas mesyuarat."
        ],
        "metrics": [
          {
            "value": "25%",
            "label": "Masa mesyuarat dijimatkan",
            "context": "Semakan S&OP mingguan kini selesai dalam kira-kira sejam berbanding lapan puluh minit."
          },
          {
            "value": "100%",
            "label": "Penjejakan tindakan",
            "context": "Setiap tindakan susulan direkodkan dalam Markdown dan diserahkan kepada pasukan yang bertanggungjawab."
          },
          {
            "value": "5",
            "label": "Sistem disatukan",
            "context": "ERP, WMS, BI, pembelian dan ringkasan e-mel kini berada dalam satu paparan SheetMate."
          }
        ],
        "quote": {
          "text": "Kami membawa metrik, papan pemuka dan tindakan susulan ke dalam satu paparan SheetMate—mesyuarat S&OP mingguan kami akhirnya berjalan dengan lancar dan teratur.",
          "author": "Michael Du",
          "role": "Pengarah Operasi Rantaian Bekalan"
        }
      }
    }
  }
]

export const getUseCaseBySlug = (slug: string) =>
  useCases.find((useCase) => useCase.slug === slug)

export const getLocalizedUseCase = (entry: UseCaseEntry, language: Language): UseCaseLocalizedContent =>
  entry.locales[language] || entry.locales.en

export const getUseCasesForLanguage = (language: Language): UseCaseLocalized[] =>
  useCases.map((entry) => ({
    slug: entry.slug,
    ...getLocalizedUseCase(entry, language)
  }))
